/**
 * EffectCalculator Service
 *
 * PURPOSE: Central service for calculating all character stat modifications
 * WHY: Single source of truth for stat calculations, eliminating duplication and bugs
 *
 * D&D 5e RULES IMPLEMENTED:
 * 1. Ability scores: Base + Racial + ASI + Feats + Items (SET overrides if higher) + Temp effects
 * 2. HP: Base from class/level/CON + Feats (Tough) + Magic items + Temp HP (doesn't stack)
 * 3. AC: Base (10 + DEX or armor) + Shield + Magic items + Feats + Spells
 * 4. Speed: Base (race) + Feats + Items + Conditions (half if prone/grappled)
 * 5. Carry capacity: STR × 15 (or STR × 30 if powerful build)
 *
 * INTEGRATION: Works with existing App.jsx state structure
 * - playerStats: Character base stats
 * - inventory: Array of items with .equipped and .equipSlot properties
 * - feats: Array of feat objects with .id property
 * - activeQuickBonuses: Temporary effects from potions/spells
 *
 * Created: December 10, 2024
 * Part of: Master Implementation Plan - Week 1, Day 3-4
 */

import { SRD_FEATS } from '../data/srdFeats';
import { SRD_CLASSES, SRD_RACES } from '../data/srdCharacterData';
import effectRegistry from './effectRegistry'; // [PHASE 0.1 - NOV 14, 2025]: Unified effect system
// [ESR-005 FIX — MAY 2026]: subscribe to systemBus events so cache invalidation
//   doesn't depend on every state-changing call site remembering to call
//   effectCalculator.clearCache(). Mirrors EffectRegistry._setupEventListeners
//   (effectRegistry.js:1424).
import { systemBus, EVENTS } from './systemBus';
// [v1.0]: Content resolver for unified SRD + custom content lookup
import { getCasterType, getClassData, getRaceData } from '../utils/contentResolver';
import { WEAPON_APPLICABILITY } from '../constants/enums';
// [PHASE B / #013 — STRING-SIN SWEEP / RULE 11]: Single source of truth for
//   polearm detection. Pre-extraction the same POLEARM_NAMES list was
//   duplicated literally here and in srdFeats.js (Polearm Master).
import { isPolearm } from '../utils/weaponClassification';
// [Phase B / #324 — Spec 1b]: Armor-proficiency penalty subsystem.
// hasArmorProficiency + hasShieldProficiency are the SSOT for the proficiency
// check so effectCalculator, EquipmentPanel, and spellCastingService all share.
import { hasArmorProficiency, hasShieldProficiency } from '../utils/armorProficiency';
// [Sub-plan 1Q-F-5b-ii — 2026-05-28]: quickBonusKinds registry — retires raw
//   b.type / b.id string checks across 3 sites in this file (Rule 11 / Rule 17).
import { QUICK_BONUS_KINDS, findQuickBonusOfKind } from '../data/quickBonusKinds';

/**
 * [FIX APR 2026 sub-project G]: Single source of truth for weapon-applicability
 * checks. Previously the same if/else string-compare chain was duplicated four
 * times in effectCalculator (lines 1165, 1209, 1379, 1423). One typo in a single
 * copy would silently break only that call site. This helper uses the
 * WEAPON_APPLICABILITY enum and is the ONLY place that interprets those strings.
 *
 * @param {string|null|undefined} appliesTo — enum value or falsy (= always applies)
 * @param {Object} weaponData — weapon item data with optional `properties` array
 * @returns {boolean}
 */
export function weaponAppliesTo(appliesTo, weaponData) {
  if (!appliesTo) return true;
  const properties = weaponData?.properties || [];
  const hasProp = (needle) => properties.some(p => String(p).includes(needle));

  switch (appliesTo) {
    case WEAPON_APPLICABILITY.ALL: return true;
    case WEAPON_APPLICABILITY.HEAVY_MELEE: return hasProp('Two-Handed') || hasProp('Heavy');
    case WEAPON_APPLICABILITY.RANGED: return hasProp('Ammunition') || hasProp('Thrown');
    case WEAPON_APPLICABILITY.MELEE: return !hasProp('Ammunition');
    case WEAPON_APPLICABILITY.FINESSE: return properties.includes('Finesse');
    case WEAPON_APPLICABILITY.LIGHT: return properties.includes('Light');
    case WEAPON_APPLICABILITY.VERSATILE: return hasProp('Versatile');
    default: return false;
  }
}

/**
 * [Spec A — Wizard capstones]: Evaluate a slot-recovery budget formula
 * against a class level. Supports the minimum DSL needed by SRD + plausible
 * CW custom classes. Kept small + intentionally restricted (no eval) so
 * authoring is data-driven and safe.
 *
 *   'classLevel'           → classLevel
 *   'ceil(classLevel/N)'   → Math.ceil(classLevel / N)
 *   'floor(classLevel/N)'  → Math.floor(classLevel / N)
 *   '<number>'             → that number
 *   anything else          → 1 (safe fallback; never throws on bad data)
 *
 * @param {string} formula
 * @param {number} classLevel
 * @returns {number}
 */
function _resolveSlotRecoveryBudget(formula, classLevel) {
  if (typeof formula !== 'string') return 1;
  const trimmed = formula.trim();
  if (trimmed === 'classLevel') return classLevel;

  const ceilMatch = trimmed.match(/^ceil\(classLevel\/(\d+)\)$/);
  if (ceilMatch) {
    const divisor = parseInt(ceilMatch[1], 10);
    if (divisor > 0) return Math.ceil(classLevel / divisor);
  }

  const floorMatch = trimmed.match(/^floor\(classLevel\/(\d+)\)$/);
  if (floorMatch) {
    const divisor = parseInt(floorMatch[1], 10);
    if (divisor > 0) return Math.floor(classLevel / divisor);
  }

  const numericMatch = trimmed.match(/^(\d+)$/);
  if (numericMatch) return parseInt(numericMatch[1], 10);

  return 1;
}

/**
 * [Spec B Task 4 — 2026-05-16]: Sparse-table lookup for level-keyed
 * tables in longRestPicker (knownTable, activeTable). Mirrors
 * src/utils/sparseTableLookup.js / Rule 21 / Q56 — find the highest
 * declared level ≤ target. Returns null if table is missing/empty so
 * callers can distinguish "no table" from "level too low".
 *
 * @param {Object|null|undefined} table  Level-keyed map { 2: 4, 6: 6, ... }
 * @param {number} classLevel
 * @returns {number|null}
 */
function _resolveLevelTableLookup(table, classLevel) {
  if (!table || typeof table !== 'object') return null;
  const thresholds = Object.keys(table)
    .map(Number)
    .filter(n => Number.isFinite(n))
    .sort((a, b) => b - a);
  if (thresholds.length === 0) return null;
  for (const threshold of thresholds) {
    if (classLevel >= threshold) return table[threshold];
  }
  return null;
}

/**
 * [Spec C Task 2 — 2026-05-16/17]: Read a trigger-specific filter key from the
 * firer's event context. Maps the condition key (e.g., 'ammoTypeMatch') to the
 * actual nested context field (e.g., context.ammo.ammoType). Centralized so the
 * mapping lives in ONE place and stays consistent across all firers.
 *
 * Rule 11 compliance: keys are enum names (from TRIGGER_VOCABULARY's
 * allowedFilterKeys[]); values matched are data-driven (ammoType from
 * BASE_ITEMS, damageType from DAMAGE_TYPES enum).
 *
 * @param {Object} eventContext  Firer-provided context object
 * @param {string} key           Condition key (e.g., 'ammoTypeMatch')
 * @returns {*}                  The contextual value to compare, or null if absent
 */
function _readTriggerContextField(eventContext, key) {
  if (!eventContext || typeof eventContext !== 'object') return null;
  // Filter-key → context-field mapping. Each branch picks the most specific
  // nested field first, then falls back to the flat field. Add new keys here
  // when a new trigger gains a filter (also update TRIGGER_VOCABULARY's
  // allowedFilterKeys for the corresponding trigger).
  if (key === 'ammoTypeMatch') return eventContext.ammo?.ammoType ?? eventContext.ammoType ?? null;
  if (key === 'targetTypeMatch') return eventContext.target?.type ?? eventContext.targetType ?? null;
  if (key === 'damageTypeMatch') return eventContext.damageType ?? null;
  if (key === 'ability') return eventContext.ability ?? null;
  if (key === 'restType') return eventContext.restType ?? null;
  // Unknown key — fall through to direct lookup (forward-compat for new filter keys).
  return eventContext[key] ?? null;
}

/**
 * [Spec C Task 2 — 2026-05-16/17]: Test whether a triggeredEffect's `condition`
 * matches the current firing event.
 *
 * Condition shape: { [key]: expectedValue }
 *   - `when: <flagName>` — matches against buildContext flags (raging,
 *     unarmored, etc. from existing CONDITION_FLAGS vocab).
 *   - other keys (ammoTypeMatch, targetTypeMatch, damageTypeMatch, ability,
 *     restType) — match against eventContext via _readTriggerContextField.
 *
 * Missing/empty filter values (`null`, `undefined`, `''`) are treated as
 * NO CONSTRAINT (skipped). An empty condition object matches everything.
 *
 * String comparisons are case-insensitive (e.g., `ammoType === 'Arrows'`
 * matches `'arrows'` and `'ARROWS'`).
 *
 * @param {Object} condition     Grant's value.condition (may be empty/missing)
 * @param {Object} eventContext  Firer-provided context
 * @param {Object} buildContext  Output of _buildContext (CONDITION_FLAGS lookups)
 * @returns {boolean}            true if all condition keys match
 */
function _triggeredEffectMatches(condition, eventContext, buildContext) {
  if (!condition || typeof condition !== 'object') return true;
  for (const [key, expected] of Object.entries(condition)) {
    // Empty filter value = no constraint on this key
    if (expected == null || expected === '') continue;

    if (key === 'when') {
      // Universal flag-key — match against buildContext truthy flag
      if (!buildContext || !buildContext[expected]) return false;
      continue;
    }

    const actual = _readTriggerContextField(eventContext, key);
    if (actual == null) return false;
    if (typeof actual === 'string' && typeof expected === 'string') {
      if (actual.toLowerCase() !== expected.toLowerCase()) return false;
    } else if (actual !== expected) {
      return false;
    }
  }
  return true;
}

export class EffectCalculator {
  constructor() {
    // Cache for expensive calculations
    this.cache = new Map();
    // [PHASE 4.5]: Increased from 100ms to 1000ms for better performance
    // clearCache() is called on state changes, so 1s timeout is safe
    this.cacheExpiry = 1000; // Cache valid for 1000ms (1 second)

    // [NEW 2025-10-21]: Effect tracking system for proper reversal
    // Track all active effects and their sources for debugging and reversal
    this._effectHistory = []; // Complete history of all effects ever applied
    this._activeEffects = new Map(); // Map of source -> effects currently active
    this._effectBreakdown = new Map(); // Map of stat -> sources affecting it

    // [PHASE 0.1 - NOV 14, 2025]: Reference to unified EffectRegistry
    // PURPOSE: Gradually migrate to centralized effect management
    // WHY: Unifies 4 separate effect systems (feats, items, spells, conditions)
    this.effectRegistry = effectRegistry;
    // [PHASE 1.5 - NOV 15, 2025]: EffectRegistry activation
    // PURPOSE: Enable EffectRegistry integration after migrating all effects (Phases 1.2-1.4)
    // WHY: All feats (12), items (29), bonuses (6), and conditions (6) now have unified effects arrays
    this.useEffectRegistry = true; // Feature flag for gradual migration

    // [ESR-005 FIX — MAY 2026]: Defense-in-depth cache invalidation. There
    //   are 30+ call sites across App.jsx / hooks / components that manually
    //   call effectCalculator.clearCache() after state changes. Future
    //   contributors who add a new state-change path may forget; the
    //   symptom (1s of stale stats) is transient + hard to spot. Now we
    //   mirror EffectRegistry's auto-subscribe pattern.
    this._setupEventListeners();
  }

  _setupEventListeners() {
    if (!systemBus || typeof systemBus.on !== 'function') return;
    const clearOnEvent = () => this.clearCache();
    systemBus.on(EVENTS.CHARACTER_LOADED, clearOnEvent);
    systemBus.on(EVENTS.CHARACTER_RESET, clearOnEvent);
    systemBus.on(EVENTS.STATS_CHANGED, clearOnEvent);
    systemBus.on(EVENTS.EQUIPMENT_CHANGED, clearOnEvent);
    systemBus.on(EVENTS.FEAT_ADDED, clearOnEvent);
    systemBus.on(EVENTS.FEAT_REMOVED, clearOnEvent);
    systemBus.on(EVENTS.INVENTORY_ITEM_ADDED, clearOnEvent);
    systemBus.on(EVENTS.INVENTORY_ITEM_REMOVED, clearOnEvent);
  }

  /**
   * Clear calculation cache
   * Call this when character state changes
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cached calculation or compute new one
   */
  _cached(key, calculator) {
    const now = Date.now();
    const cached = this.cache.get(key);

    if (cached && (now - cached.timestamp) < this.cacheExpiry) {
      return cached.value;
    }

    const value = calculator();
    this.cache.set(key, { value, timestamp: now });
    return value;
  }

  // ========================================
  // ABILITY SCORE CALCULATIONS
  // ========================================

  /**
   * Calculate effective ability score with ALL modifiers
   * [PHASE 2.1 - NOV 15, 2025]: INTEGRATED WITH EFFECTREGISTRY
   * [BEFORE]: Manual parsing of feats, items, and quick bonuses
   * [AFTER]: Query EffectRegistry for all modifying effects
   *
   * D&D 5e Priority:
   * 1. Base score (rolled/point buy/standard array)
   * 2. Racial bonuses (permanent) - NOT in EffectRegistry
   * 3. ASI increases from leveling (permanent) - NOT in EffectRegistry
   * 4. Feat bonuses → FROM EffectRegistry (registered by FeatsPanel)
   * 5. Magic items → FROM EffectRegistry (registered by InventoryContext)
   * 6. Temporary effects → FROM EffectRegistry (registered by QuickBonusesPanel)
   * 7. Conditions → FROM EffectRegistry (registered by ConditionsTracker)
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @param {string} ability - Ability name: 'strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'
   * @returns {number} Final ability score (1-30 per D&D bounds)
   */
  getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, ability) {
    const cacheKey = `ability_${ability}_${playerStats.level}_${inventory?.length}`;

    return this._cached(cacheKey, () => {
      // ===== PHASE 2.1: NEW EFFECTREGISTRY-INTEGRATED FLOW =====

      // Step 1: Calculate permanent base (not in EffectRegistry)
      // Base score + racial bonuses + ASI increases
      let permanentBase = playerStats[ability] || 10;
      permanentBase += this._getRacialBonus(playerStats, ability);
      permanentBase += (playerStats.asiIncreases?.[ability] || 0);

      // Step 2: Query EffectRegistry for all modifying effects
      // (feats, items, quick bonuses, conditions already registered by components)
      if (this.useEffectRegistry) {
        const abilityCode = this._getStatKey(ability);
        const context = {
          character: playerStats,
          inventory,
          activeQuickBonuses
        };

        // [PHASE 2.2 - NOV 15, 2025]: Pass playerStats for conditional effect checks
        const finalScore = this.effectRegistry.getEffectiveValue(abilityCode, permanentBase, playerStats, context);

        // Step 3: Apply D&D 5e bounds (1-30)
        return Math.max(1, Math.min(30, finalScore));
      }

      // ===== LEGACY FALLBACK (if EffectRegistry disabled) =====
      let score = permanentBase;

      // 4. Feat bonuses (manual parsing - LEGACY)
      (playerStats.feats || []).forEach(feat => {
        const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);
        if (!featData?.passiveEffects) return;
        // [v1.0 ARC-008]: Skip legacy path if feat has modern effects array (handled by effectRegistry)
        if (featData.effects && Array.isArray(featData.effects)) return;

        // Direct ability score bonuses (like Actor feat: +1 CHA)
        const abilityBonus = featData.passiveEffects[ability];
        if (typeof abilityBonus === 'number') {
          score += abilityBonus;
        }

        // Ability score increases from custom choices (like Resilient feat)
        if (feat.choices?.ability === ability || feat.choices?.ability === ability.slice(0, 3)) {
          score += 1;
        }
      });

      // 5. Magic items (manual parsing - LEGACY)
      let setTo = null;
      let addBonus = 0;

      (inventory || []).forEach(item => {
        if (!item.equipped) return;
        if (item.requiresAttunement && !item.attuned) return;

        const statMods = item.statModifications || item.passiveEffects?.statModifications;
        if (!statMods) return;

        const mod = statMods[ability];
        if (!mod) return;

        if (mod.set && typeof mod.set === 'number') {
          setTo = Math.max(setTo || 0, mod.set);
        }

        if (mod.add && typeof mod.add === 'number') {
          addBonus += mod.add;
        }
      });

      score += addBonus;

      if (setTo !== null && setTo > score) {
        score = setTo;
      }

      // 6. Temporary effects (manual parsing - LEGACY)
      (activeQuickBonuses || []).forEach(bonus => {
        if (bonus.type === `set${ability.charAt(0).toUpperCase() + ability.slice(1)}`) {
          if (bonus.value > score) {
            score = bonus.value;
          }
        }
        else if (bonus.type === `add${ability.charAt(0).toUpperCase() + ability.slice(1)}`) {
          score += bonus.value || 0;
        }
        else if (bonus.type === `subtract${ability.charAt(0).toUpperCase() + ability.slice(1)}`) {
          score += bonus.value || 0;
        }
      });

      // D&D 5e bounds: Ability scores must be 1-30
      return Math.max(1, Math.min(30, score));
    });
  }

  /**
   * Calculate racial ability score bonuses (PERMANENT character properties)
   * [PHASE 2.1 - NOV 15, 2025]: Extracted from getEffectiveAbilityScore
   * [PURPOSE]: Separate permanent character traits from temporary effects
   * [WHY]: Racial bonuses are not managed by EffectRegistry - they're permanent character properties
   * [INTEGRATION]: Called before EffectRegistry queries to establish permanent base value
   *
   * @param {Object} playerStats - Character stats object
   * @param {string} ability - Ability name: 'strength', 'dexterity', etc.
   * @returns {number} Racial bonus to apply
   */
  _getRacialBonus(playerStats, ability) {
    // [DUAL STORAGE FIX - NOV 19, 2025]: Use canonical nested structure
    const race = playerStats.basics?.race;
    const subrace = playerStats.basics?.subrace;

    // [v1.0]: Use contentResolver for unified SRD + custom race lookup
    if (!race) return 0;
    const fullRaceData = getRaceData(race); // Without subrace — need raw data for override logic
    if (!fullRaceData) return 0;

    const raceData = fullRaceData;
    let bonus = 0;
    let applyBaseRaceBonuses = true;

    // Check if subrace overrides base race bonuses
    if (subrace && raceData.subraces?.[subrace]) {
      const subraceData = raceData.subraces[subrace];

      // Check for override flag (used by Variant Human)
      if (subraceData.abilityScoreIncrease?.override) {
        applyBaseRaceBonuses = false;

        // [FIX APR 2026 — AUDIT M-13]: Property-based variant check for custom content
        if (subraceData.variantBonuses) {
          // Variant bonuses: +1 to two chosen abilities instead of base race bonuses
          if (playerStats.variantHumanChoices?.abilities) {
            if (playerStats.variantHumanChoices.abilities.includes(ability)) {
              bonus += 1;
            }
          }
        }
      } else if (subraceData.abilityScoreIncrease?.[ability]) {
        // Normal subrace bonuses stack with base race
        bonus += subraceData.abilityScoreIncrease[ability];
      }
    }

    // Apply base race bonuses unless overridden
    if (applyBaseRaceBonuses && raceData.abilityScoreIncrease?.[ability]) {
      bonus += raceData.abilityScoreIncrease[ability];
    }

    return bonus;
  }

  /**
   * Map full stat names to EffectRegistry keys
   * [PHASE 2.1 - NOV 15, 2025]: Created for EffectRegistry integration
   * [PURPOSE]: Convert calculation method parameter names to EffectRegistry stat keys
   * [WHY]: effectRegistry uses 3-letter codes for abilities, calculations use full names
   * [EXAMPLE]: 'strength' → 'str', 'dexterity' → 'dex', 'ac' → 'ac'
   *
   * @param {string} stat - Full stat name
   * @returns {string} EffectRegistry stat key
   */
  _getStatKey(stat) {
    const abilityMap = {
      'strength': 'str',
      'dexterity': 'dex',
      'constitution': 'con',
      'intelligence': 'int',
      'wisdom': 'wis',
      'charisma': 'cha'
    };

    return abilityMap[stat] || stat;
  }

  /**
   * Calculate ability modifier from score
   * D&D 5e formula: (score - 10) / 2, rounded down
   * [PHASE 2.1 - NOV 11, 2025]: Added NaN and type validation for defensive programming
   */
  getAbilityModifier(score) {
    // Validate input to prevent NaN propagation
    if (typeof score !== 'number' || isNaN(score)) {
      console.warn('[effectCalculator] getAbilityModifier received invalid score:', score);
      return 0; // Safe fallback - no modifier
    }
    return Math.floor((score - 10) / 2);
  }

  /**
   * Get effective ability modifier with all modifications
   */
  getEffectiveModifier(playerStats, inventory, activeQuickBonuses, ability) {
    const score = this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, ability);
    return this.getAbilityModifier(score);
  }

  // ========================================
  // HIT POINTS CALCULATIONS
  // ========================================

  /**
   * Calculate maximum hit points including ALL modifiers
   *
   * D&D 5e Rules:
   * 1. Base HP from class hit dice + CON modifier per level
   *    - Level 1: Max hit die + CON mod
   *    - Additional levels: Average of hit die (rounded up) + CON mod OR roll + CON mod
   * 2. Feat bonuses (Tough = +2 HP per character level)
   * 3. Magic item bonuses (Amulet of Health sets CON to 19, which increases HP)
   * 4. Class features (Hill Dwarf = +1 HP per level)
   *
   * NOTE: This calculates MAX HP only. Current HP and temp HP are separate.
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @param {Object} [settings] - Optional user settings (Batch 5 / P0 C-4).
   *   When provided, settings.exhaustionRules takes precedence over
   *   playerStats.exhaustionRules. Without it, falls back to playerStats
   *   then defaults to '5e'.
   * @returns {number} Maximum hit points
   */
  calculateMaxHP(playerStats, inventory, activeQuickBonuses, settings) {
    // [BATCH 5 / P0 C-4 — APR 2026]: Cache key includes exhaustion inputs
    //   so settings toggle invalidates the entry. Without this the player
    //   sees stale HP after switching editions.
    const cacheKey = `maxhp_${playerStats.level}_${playerStats.constitution}_${playerStats.exhaustionLevel || 0}_${settings?.exhaustionRules || playerStats.exhaustionRules || '5e'}_${playerStats.hpRollDelta || 0}`;

    return this._cached(cacheKey, () => {
      let hp = 0;

      // Get effective CON modifier (includes items like Amulet of Health)
      const conMod = this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, 'constitution');

      // 1. Base HP from classes
      // [v1.0]: Use contentResolver for unified SRD + custom class lookup
      // [FIX APR 2026 — AUDIT H-1]: Only first class (idx 0) gets max hit die at level 1.
      // [WHY]: PHB p.163 — multiclass additions use average for ALL levels including their first.
      (playerStats.classes || []).forEach((cls, idx) => {
        const classData = getClassData(cls.className);
        if (!classData) {
          console.error(`[calculateMaxHP] Unknown class: "${cls.className}" at level ${cls.level}. HP calculation may be incorrect.`);
          return;
        }

        // [FIX NOV 25, 2025]: Parse hitDice string to extract die number
        let hitDie = 8; // Default
        const hitDiceValue = classData.hitDice || cls.hitDice;
        if (typeof hitDiceValue === 'string') {
          const match = hitDiceValue.match(/d(\d+)/i);
          if (match) {
            hitDie = parseInt(match[1], 10);
          }
        } else if (typeof hitDiceValue === 'number') {
          hitDie = hitDiceValue;
        }

        const averageRoll = Math.floor(hitDie / 2) + 1;
        const isFirstClass = (idx === 0);

        if (isFirstClass) {
          // First class: Level 1 gets max hit die + CON mod
          hp += hitDie + conMod;
          if (cls.level > 1) {
            hp += (cls.level - 1) * (averageRoll + conMod);
          }
        } else {
          // Multiclass additions: ALL levels use average (PHB p.163)
          hp += cls.level * (averageRoll + conMod);
        }
      });

      // 2. Feat bonuses
      (playerStats.feats || []).forEach(feat => {
        const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);
        if (!featData?.passiveEffects) return;
        // [v1.0 ARC-008]: Skip legacy path if feat has modern effects array (handled by effectRegistry)
        if (featData.effects && Array.isArray(featData.effects)) return;

        // Tough feat: +2 HP per character level
        const hpPerLevel = featData.passiveEffects.hpPerLevel || 0;
        if (hpPerLevel > 0) {
          hp += hpPerLevel * playerStats.level;
        }

        // Direct bonus HP
        const bonusHP = featData.passiveEffects.bonusHP || 0;
        if (bonusHP > 0) {
          hp += bonusHP;
        }
      });

      // 3. Magic item bonuses
      // [SUB-PLAN 1J — 2026-05-27]: Gated to !useEffectRegistry. The canonical reader is
      // effectRegistry.getEffectsForStat('hp', ...) downstream. This dead-code path prevents
      // double-count when migration of S4 items adds effects[] alongside existing S4 bonusHP.
      // Other 4 path-2a sites already use this pattern at L357 / L949 / L1312 / L2294.
      if (!this.useEffectRegistry) {
        (inventory || []).forEach(item => {
          if (!item.equipped) return;

          const statMods = item.statModifications || item.passiveEffects?.statModifications;
          if (!statMods?.bonusHP) return;

          hp += statMods.bonusHP || 0;
        });
      }

      // 4. Racial HP bonuses
      // [RACIAL HP BONUS - OCT 25, 2025]: Apply HP bonuses from race/subrace features
      // PURPOSE: Hill Dwarf gets +1 HP per level (Dwarven Toughness)
      // WHY: D&D 5e racial feature that increases max HP
      // [v1.0]: Use contentResolver for unified SRD + custom race lookup
      const race = playerStats.basics?.race;
      const subrace = playerStats.basics?.subrace;

      if (race) {
        // Get base race data (without subrace merge) to check both separately
        const baseRaceData = getRaceData(race);
        if (baseRaceData) {
          // Check base race for HP bonus
          if (baseRaceData.hitPointBonus) {
            hp += baseRaceData.hitPointBonus * playerStats.level;
          }
          // Check subrace for HP bonus (stacks with base race)
          if (subrace && baseRaceData.subraces?.[subrace]) {
            const subraceData = baseRaceData.subraces[subrace];
            if (subraceData.hitPointBonus) {
              hp += subraceData.hitPointBonus * playerStats.level;
            }
          }
        }
      }

      // 4.5. effectRegistry hp effects (Phase 4b bucket 3 followup — APR 2026)
      // [WHY]: Subclass features (Sorcerer Draconic Resilience: +1 HP/Sorcerer level),
      //        custom feats with effects arrays, and any future race/item HP grants
      //        register as { type: 'hp', operation: 'add'|'set', value: <num|formula> }.
      //        Without this consumer call, the producer side fired but the bonus was
      //        silently dropped — same silent-gap pattern as carryCapacity (40cff69)
      //        and damageBonus (b4e273c). RAW order: registry HP applies BEFORE
      //        exhaustion halving (PHB p.291 — exhaustion halves "hit point maximum",
      //        which already includes all standing bonuses).
      const hpContext = this._buildContext(playerStats, inventory, activeQuickBonuses);
      hp = this.effectRegistry.getEffectiveValue('hp', hp, playerStats, hpContext);

      // 4.6. Roll-luck carryover (maxHP unification — 2026-07-25)
      // [WHY]: Levels 2+ above use the AVERAGE roll, but the level-up wizard lets the
      //        player ROLL. hpRollDelta accumulates (taken − average) per level-up
      //        (see src/utils/maxHPMirror.js), so a lucky roll survives the formula.
      //        MUST apply before exhaustion halving: PHB p.291 halves the full maximum.
      hp += playerStats.hpRollDelta || 0;

      // 5. Exhaustion effects (D&D 5e 2014 rules)
      // [EXHAUSTION SYSTEM - OCT 25, 2025]: Apply exhaustion HP max penalty
      // PURPOSE: Level 4 exhaustion halves hit point maximum (D&D 5e 2014)
      // WHY: Core D&D 5e mechanic - exhaustion severely impacts survival
      // NOTE: 5.5e (2024) rules don't affect HP max, only d20 rolls
      // [PHASE 2.6 - NOV 11, 2025]: Standardized to use exhaustionLevel consistently
      // [BATCH 5 / P0 C-4 — APR 2026]: settings.exhaustionRules takes
      //   precedence over the (possibly stale) playerStats.exhaustionRules.
      //   The latter is only updated when changeExhaustion() runs in
      //   VitalStatsCard; toggling the rule alone in Settings does not sync.
      const exhaustionLevel = playerStats.exhaustionLevel || 0;
      const exhaustionRules = settings?.exhaustionRules || playerStats.exhaustionRules || '5e';

      if (exhaustionRules === '5e' && exhaustionLevel >= 4) {
        hp = Math.floor(hp / 2); // Halve HP maximum
      }

      // [FIX NOV 15, 2025]: Add NaN protection and diagnostic logging
      // [WHY]: User reported calculatedMaxHP = NaN - ensure we never return invalid HP
      // [FIX 2026-04-28 S1.A.1]: Distinguish hydration window from genuine invalid state.
      // Hydration: classes empty + constitution undefined during context init → silent fallback.
      // Invalid: anything else returning hp ≤ 0 → log for diagnostics.
      // [FIX 2026-04-29 P0.2]: Widen guard to catch partial-hydration shapes too.
      // [WHY]: User session showed 30+ console.error spam on cold load. The narrow
      //        AND-guard from S1.A.1 only caught the simultaneous case, but real
      //        hydration ticks include shapes where classes hydrate before
      //        constitution (or vice versa) — both legs are intermediate states
      //        the user can't act on. EITHER side missing → still hydrating;
      //        BOTH sides present + hp=0 → genuine invalid state worth logging.
      const hasNoClasses = !Array.isArray(playerStats.classes) || playerStats.classes.length === 0;
      const conIsUnset = playerStats.constitution === undefined || playerStats.constitution === null;

      if (hasNoClasses || conIsUnset) {
        return 8;  // hydration window — no log
      }

      if (isNaN(hp) || hp === 0) {
        console.error('[calculateMaxHP] Invalid HP calculated:', {
          hp,
          classes: playerStats.classes,
          level: playerStats.level,
          constitution: playerStats.constitution,
          conMod
        });
        // Fallback: Level 1 character with CON 10 has 8 HP (d8 hit die average)
        return 8;
      }

      return Math.max(1, hp); // Minimum 1 HP
    });
  }

  // ========================================
  // ARMOR CLASS CALCULATIONS
  // ========================================

  /**
   * Calculate base AC from armor and DEX (before modifiers from effects)
   * [PHASE 2.1 - NOV 15, 2025]: Extracted from calculateAC for clean separation
   * [PURPOSE]: Separate fundamental D&D AC calculation from effect modifiers
   * [WHY]: Base AC (armor + DEX + shield) is core equipment, modifiers come from EffectRegistry
   *
   * Includes:
   * - Armor AC + DEX modifier (with armor type limits)
   * - Medium Armor Master feat (affects DEX cap, stays here for calculation logic)
   * - Barbarian/Monk Unarmored Defense
   * - Shield base +2 bonus
   *
   * Does NOT include (comes from EffectRegistry):
   * - Magical armor/shield enhancements (+1/+2/+3) - registered by InventoryContext
   * - Other magic items (Bracers of Defense, Ring of Protection) - registered by InventoryContext
   * - Feat AC bonuses - registered by FeatsPanel
   * - Spell/bonus AC bonuses (Shield of Faith) - registered by QuickBonusesPanel
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @returns {number} Base AC before effect modifiers
   */
  _calculateBaseAC(playerStats, inventory, activeQuickBonuses) {
    let ac = 10; // Base unarmored AC
    let dexMod = this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, 'dexterity');
    let maxDexBonus = 99; // No limit by default

    // [FIX JAN 2026]: Improved armor detection
    // Check multiple conditions: equipSlot OR (category=armor AND not a shield)
    // This handles both properly formatted items AND legacy items without equipSlot
    const armor = (inventory || []).find(item => {
      if (!item.equipped) return false;
      // Check if explicitly marked as armor slot
      if (item.equipSlot === 'armor') return true;
      // Check if it's armor category but NOT a shield (shields have baseType 'shield' or 'wooden_shield')
      if (item.category === 'armor' &&
          item.baseType !== 'shield' &&
          item.baseType !== 'wooden_shield' &&
          item.armorType !== 'shield') {
        return true;
      }
      return false;
    });

    if (armor) {
      // Armor provides base AC (e.g., 16 for chain mail)
      ac = armor.ac || 10;

      // [FIX JAN 2026]: Add magical armor bonus (+1/+2/+3) directly to base AC
      // This was never being applied - magicalBonus wasn't converted to effects
      // and wasn't added in _calculateBaseAC
      if (armor.magicalBonus && (!armor.requiresAttunement || armor.attuned)) {
        ac += armor.magicalBonus;
      }

      // [FIX JAN 2026]: Use armorType property (from BASE_ITEMS) instead of type
      // armorType is 'light', 'medium', or 'heavy'
      const armorType = (armor.armorType || armor.type || '').toLowerCase();

      // [FIX JAN 2026]: Also check maxDexMod directly from item (copied from BASE_ITEMS)
      // maxDexMod: null = unlimited (light), 2 = medium, 0 = heavy
      if (armor.maxDexMod !== undefined && armor.maxDexMod !== null) {
        maxDexBonus = armor.maxDexMod;
      } else if (armorType.includes('heavy')) {
        maxDexBonus = 0; // No DEX bonus for heavy armor
      } else if (armorType.includes('medium')) {
        maxDexBonus = 2; // Base: Max +2 DEX for medium armor
      }
      // Light armor has no limit (maxDexBonus = 99)

      // [Phase 4b-infra APR 2026]: armorDexCap registry consumer (Medium Armor Master, PHB p.168)
      // [WHY]: Effect type was orphaned — feat data declared armorDexCap effects but no consumer
      // applied them. Registry query lets any feat / class feature / item raise the cap data-drivenly.
      // [HOW]: Highest "set" override wins vs. armor's native maxDexBonus. This preserves the rule
      // that the cap can only increase (never decrease) and stacks correctly with future content.
      // [Phase 4b-infra APR 2026 — followup]: Armor flags now live in _buildContext so all
      // calculators (AC, speed, initiative, passive perception) see them uniformly. Previously
      // only this method computed them, leaving feat condition checks elsewhere with no
      // wearing*Armor signal — they fell back to broken character.equipment lookups.
      if (this.useEffectRegistry) {
        const capEffects = this.effectRegistry.getEffectsForStat('armorDexCap', playerStats,
          this._buildContext(playerStats, inventory, activeQuickBonuses));
        const registryCapOverride = capEffects
          .filter(e => e.operation === 'set')
          .map(e => parseInt(e.value, 10))
          .reduce((max, v) => Math.max(max, v || 0), 0);
        if (registryCapOverride > 0) {
          maxDexBonus = Math.max(maxDexBonus, registryCapOverride);
        }
      }

      // [LEGACY FALLBACK]: Medium Armor Master via hardcoded feat scan
      // [WHY]: Preserved for the (rare) code path where useEffectRegistry is false.
      // Modern path above handles registered armorDexCap effects.
      if (!this.useEffectRegistry && maxDexBonus === 2) {
        const hasMediumArmorMaster = (playerStats.feats || []).some(feat => {
          const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);
          return featData?.id === 'medium_armor_master';
        });

        if (hasMediumArmorMaster && dexMod >= 3) {
          maxDexBonus = 3; // Medium Armor Master: Max +3 DEX (requires 16+ DEX)
        }
      }

      // Apply DEX modifier
      ac += Math.min(dexMod, maxDexBonus);

      // [PHASE 2.1 NOTE]: Magical armor bonuses (+1/+2/+3) are in EffectRegistry from Phase 1.3
      // They are registered by InventoryContext and will be queried separately
    } else {
      // Unarmored AC calculations

      // [FIX APR 2026 — F-6]: Data-driven Unarmored Defense
      // [WHY]: Custom classes with unarmoredDefense property now work (not just Barbarian/Monk)
      const unarmoredClass = (playerStats.classes || []).find(cls => {
        const classData = getClassData(cls.className);
        return classData?.unarmoredDefense;
      });
      if (unarmoredClass) {
        // [FIX APR 2026 — F-7]: Added optional chaining and bounds check for defensive safety
        const uaDef = getClassData(unarmoredClass.className)?.unarmoredDefense;
        if (uaDef && uaDef.abilities?.length >= 2) {
        const secondAbilityMod = this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, uaDef.abilities[1]);
        if (uaDef.noShield) {
          const hasShield = (inventory || []).some(item =>
            item.equipped && (item.equipSlot === 'shield' || item.armorType === 'shield' || item.baseType === 'shield' || item.baseType === 'wooden_shield')
          );
          if (!hasShield) {
            ac = 10 + dexMod + secondAbilityMod;
          } else {
            // [FIX APR 2026 — AUDIT C-1]: Shield invalidates noShield UA, but standard 10+DEX still applies
            ac = 10 + dexMod;
          }
        } else {
          ac = 10 + dexMod + secondAbilityMod;
        }
        } // end uaDef guard
      } else {
        // [FIX APR 2026 — AUDIT C-1]: No armor, no Unarmored Defense — PHB p.14: AC = 10 + DEX modifier
        ac = 10 + dexMod;
      }

      // [FIX Phase6-D — APR 2026]: Enforce AC floor of 10 for ALL unarmored calculations.
      // Spec invariant: a naked character always has AC >= 10. A sufficiently negative DEX
      // modifier (e.g. DEX 3 → mod -4 → AC 6) violates this. The floor applies to the
      // unarmored base before any shield or registry bonuses are added on top, so rings /
      // spells / feats can still push the final value higher than 10.
      // This clamps every unarmored branch: standard 10+DEX, Unarmored Defense with/without
      // shield invalidation. It does NOT apply to the armored path (handled by 'if (armor)').
      ac = Math.max(10, ac);
    }

    // [FIX JAN 2026]: Improved shield detection
    // Check multiple conditions: equipSlot OR baseType OR armorType OR name contains 'shield'
    const shield = (inventory || []).find(item => {
      if (!item.equipped) return false;
      // Check explicit equipSlot
      if (item.equipSlot === 'offhand' || item.equipSlot === 'shield') {
        // Also verify it's actually a shield (not a weapon in offhand)
        // [v1.0]: Property-based shield detection only (no name string matching)
        if (item.baseType === 'shield' || item.baseType === 'wooden_shield' ||
            item.armorType === 'shield' || item.equipSlot === 'shield') {
          return true;
        }
      }
      // Check by baseType/armorType for shields without equipSlot
      if ((item.baseType === 'shield' || item.baseType === 'wooden_shield' || item.armorType === 'shield') &&
          item.category === 'armor') {
        return true;
      }
      return false;
    });

    if (shield) {
      ac += 2; // Standard shield base bonus

      // [FIX JAN 2026]: Add magical shield bonus (+1/+2/+3) directly to base AC
      // This was never being applied - magicalBonus wasn't converted to effects
      if (shield.magicalBonus && (!shield.requiresAttunement || shield.attuned)) {
        ac += shield.magicalBonus;
      }
    }

    return ac;
  }

  /**
   * Calculate Armor Class with ALL modifiers
   * [PHASE 2.1 - NOV 15, 2025]: INTEGRATED WITH EFFECTREGISTRY
   * [BEFORE]: Manual parsing of items, feats, and quick bonuses
   * [AFTER]: Query EffectRegistry for all AC modifiers
   *
   * D&D 5e AC Calculations:
   * 1. Base AC → From _calculateBaseAC() (armor + DEX + shield)
   * 2. Magical enhancements → FROM EffectRegistry (registered by InventoryContext)
   * 3. Other magic items → FROM EffectRegistry (Bracers of Defense, Ring of Protection)
   * 4. Feats → FROM EffectRegistry (registered by FeatsPanel)
   * 5. Spells/Bonuses → FROM EffectRegistry (Shield of Faith, Shield spell)
   * 6. Conditional bonuses → FROM EffectRegistry (Dual Wielder while dual wielding)
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @returns {number} Total armor class
   */
  calculateAC(playerStats, inventory, activeQuickBonuses) {
    const cacheKey = `ac_${playerStats.level}_${inventory?.length}`;

    return this._cached(cacheKey, () => {
      // ===== PHASE 2.1: NEW EFFECTREGISTRY-INTEGRATED FLOW =====

      // Step 1: Calculate base AC (armor + DEX + shield)
      const baseAC = this._calculateBaseAC(playerStats, inventory, activeQuickBonuses);

      // Step 2: Query EffectRegistry for all AC modifiers
      // (magical enhancements, other items, feats, bonuses already registered by components)
      if (this.useEffectRegistry) {
        // [Phase 4a]: Use _buildContext so all condition flags (unarmored/noShield/dualWielding)
        // are consistently populated across every stat calculator.
        const context = this._buildContext(playerStats, inventory, activeQuickBonuses);

        // [PHASE 2.2 - NOV 15, 2025]: Pass playerStats for conditional effect checks
        const finalAC = this.effectRegistry.getEffectiveValue('ac', baseAC, playerStats, context);

        return Math.max(1, finalAC);
      }

      // ===== LEGACY FALLBACK (if EffectRegistry disabled) =====
      let ac = baseAC;

      // 3. Other equipped magic items with AC bonuses (manual parsing - LEGACY)
      (inventory || []).forEach(item => {
        if (!item.equipped) return;

        // Skip armor and shield (already counted in base AC)
        const isArmor = item.equipSlot === 'armor' && item.category === 'armor';
        // [v1.0]: Property-based shield detection (no name string matching)
        const isShield = (item.equipSlot === 'offhand' || item.equipSlot === 'shield') &&
                        (item.baseType === 'shield' || item.baseType === 'wooden_shield' || item.armorType === 'shield');
        if (isArmor || isShield) return;

        // [ATTUNEMENT SYSTEM - OCT 25, 2025]: Only apply AC bonus if attuned (when required)
        if (item.requiresAttunement && !item.attuned) return;

        const statMods = item.statModifications || item.passiveEffects?.statModifications;
        if (statMods?.acBonus) {
          ac += statMods.acBonus;
        }
      });

      // [FIX JAN 2026]: Legacy magical armor/shield bonuses - fixed property name and detection
      // Note: magicalBonus is already added in _calculateBaseAC now, so this is just a fallback
      const legacyArmor = (inventory || []).find(item =>
        item.equipped &&
        (item.equipSlot === 'armor' || (item.category === 'armor' && item.baseType !== 'shield' && item.baseType !== 'wooden_shield'))
      );
      // Use magicalBonus (correct) OR magicBonus (legacy) for backwards compatibility
      const armorMagicBonus = legacyArmor?.magicalBonus || legacyArmor?.magicBonus || 0;
      if (armorMagicBonus && (!legacyArmor?.requiresAttunement || legacyArmor?.attuned)) {
        // Only add if not already in base AC (check if armor was found in _calculateBaseAC)
        // Since _calculateBaseAC now handles this, we can skip this to avoid double-counting
        // ac += armorMagicBonus; // DISABLED - now handled in _calculateBaseAC
      }

      // [v1.0]: Property-based shield detection (no name string matching)
      const legacyShield = (inventory || []).find(item =>
        item.equipped &&
        (item.baseType === 'shield' || item.baseType === 'wooden_shield' ||
         item.armorType === 'shield' || item.equipSlot === 'shield')
      );
      const shieldMagicBonus = legacyShield?.magicalBonus || legacyShield?.magicBonus || 0;
      if (shieldMagicBonus && (!legacyShield?.requiresAttunement || legacyShield?.attuned)) {
        // DISABLED - now handled in _calculateBaseAC
        // ac += shieldMagicBonus;
      }

      // 4. Feat bonuses (manual parsing - LEGACY)
      (playerStats.feats || []).forEach(feat => {
        const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);

        // 4a. Passive effects (always on)
        // [v1.0 ARC-008]: Skip legacy path if feat has modern effects array
        if (featData?.passiveEffects && !(featData.effects && Array.isArray(featData.effects))) {
          // Direct AC bonus
          if (featData.passiveEffects.acBonus) {
            ac += featData.passiveEffects.acBonus;
          }

          // Conditional AC bonuses (Dual Wielder)
          if (featData.passiveEffects.acBonusWhileDualWielding) {
            const dualWielding = this._isDualWielding(inventory);
            if (dualWielding) {
              ac += featData.passiveEffects.acBonusWhileDualWielding;
            }
          }
        }

        // [UX-18 - NOV 15, 2025]: 4b. Toggle effects (when active)
        if (featData?.toggleEffects && feat.activeToggles) {
          if (Array.isArray(featData.toggleEffects)) {
            // NEW FORMAT: Array of toggles
            featData.toggleEffects.forEach(toggleDef => {
              const toggleId = toggleDef.name;
              if (feat.activeToggles[toggleId]) {
                (toggleDef.effects || []).forEach(effect => {
                  if (effect.attribute === 'ac') {
                    ac += effect.value || 0;
                  }
                });
              }
            });
          } else {
            // OLD FORMAT: Object (if old format had ac support)
            Object.entries(featData.toggleEffects).forEach(([toggleId, toggleDef]) => {
              if (feat.activeToggles[toggleId] && toggleDef.ac) {
                ac += toggleDef.ac;
              }
            });
          }
        }
      });

      // 5. Temporary AC bonuses (manual parsing - LEGACY)
      // [Sub-plan 1Q-F-5b-ii — 2026-05-28]: Route through QUICK_BONUS_KINDS registry
      //   instead of raw b.type === 'acBonus' check (Rule 11 / Rule 17 retirement).
      (activeQuickBonuses || []).forEach(bonus => {
        if (bonus.type === QUICK_BONUS_KINDS.acBonus.id && typeof bonus.value === 'number') {
          ac += bonus.value;
        }
      });

      // 6. Active effects stored in playerStats (legacy)
      if (playerStats.acBonus) {
        ac += playerStats.acBonus;
      }

      return Math.max(1, ac);
    });
  }

  /**
   * Helper: Check if character is wearing body armor (not shields).
   * [FIX APR 2026 — Phase 4]: Used for 'unarmored' conditional effect checks.
   */
  _isWearingArmor(inventory) {
    return !!(inventory || []).find(item => {
      if (!item.equipped) return false;
      if (item.equipSlot === 'armor') return true;
      if (item.category === 'armor' &&
          item.baseType !== 'shield' &&
          item.baseType !== 'wooden_shield' &&
          item.armorType !== 'shield') return true;
      return false;
    });
  }

  /**
   * Helper: Check if character is equipping a shield.
   * [FIX APR 2026 — Phase 4a]: for 'noShield' conditional effects
   */
  _isEquippingShield(inventory) {
    return !!(inventory || []).find(item => {
      if (!item.equipped) return false;
      // Check explicit equipSlot
      if (item.equipSlot === 'offhand' || item.equipSlot === 'shield') {
        // Also verify it's actually a shield (not a weapon in offhand)
        // [v1.0]: Property-based shield detection only (no name string matching)
        if (item.baseType === 'shield' || item.baseType === 'wooden_shield' ||
            item.armorType === 'shield' || item.equipSlot === 'shield') {
          return true;
        }
      }
      // Check by baseType/armorType for shields without equipSlot
      if ((item.baseType === 'shield' || item.baseType === 'wooden_shield' || item.armorType === 'shield') &&
          item.category === 'armor') {
        return true;
      }
      return false;
    });
  }

  /**
   * Helper: Check if character is dual wielding
   */
  _isDualWielding(inventory) {
    const mainhand = (inventory || []).find(item => item.equipped && item.equipSlot === 'mainhand');
    const offhand = (inventory || []).find(item => item.equipped && item.equipSlot === 'offhand');

    return mainhand && offhand &&
           mainhand.category === 'weapons' &&
           offhand.category === 'weapons';
  }

  /**
   * [Phase 4a]: Centralises game-state context for effectRegistry condition resolution.
   * [WHY]: Previously only calculateAC populated unarmored/noShield/dualWielding flags.
   *        Other stat calculators (speed, initiative, passivePerception) passed a bare context
   *        object, so conditional effects (e.g. "+10 speed when unarmored") never triggered.
   *        All callers now use this helper so the flags are consistent everywhere.
   * [Phase 4b-infra APR 2026]: Added wearingLightArmor / wearingMediumArmor / wearingHeavyArmor
   *        plus wieldingShield + wieldingPolearm. These replace 5 broken condition checks that
   *        inspected `character.equipment` (a property that doesn't exist — the actual property
   *        is `character.inventory`), silently filtering out their effects in production.
   *        Fixes:
   *          - Bracers of Defense (gameData.js): unarmored
   *          - Dual Wielder (srdFeats): dualWielding
   *          - Polearm Master (srdFeats): wieldingPolearm
   *          - Shield Master (srdFeats): wieldingShield (new)
   *          - Heavy Armor Master (srdFeats): wearingHeavyArmor
   */
  _buildContext(playerStats, inventory, activeQuickBonuses) {
    // [Phase 4b-infra]: Armor type flags — used by feat condition checks
    //                   (Heavy Armor Master, Medium Armor Master, etc.)
    // Match _isWearingArmor exactly: equipped + (equipSlot === 'armor' OR
    // category === 'armor' && not a shield).
    const equippedArmor = (inventory || []).find(item => {
      if (!item?.equipped) return false;
      if (item?.equipSlot === 'armor') return true;
      if (item?.category === 'armor' &&
          item?.baseType !== 'shield' &&
          item?.baseType !== 'wooden_shield' &&
          item?.armorType !== 'shield') {
        return true;
      }
      return false;
    });
    const armorType = (equippedArmor?.armorType || equippedArmor?.type || '').toLowerCase();

    // [Phase 4b-infra]: Polearm-wielding flag — Polearm Master condition check.
    // [PHASE B / #013 — STRING-SIN SWEEP]: Now delegates to shared
    //   isPolearm() helper (src/utils/weaponClassification.js) so a future
    //   addition (CW custom polearm, BASE_ITEMS expansion) updates BOTH
    //   this consumer and srdFeats.js Polearm Master in a single place.
    const mainHandWeapon = (inventory || []).find(item =>
      item?.equipped && (item?.equipSlot === 'mainhand' || item?.equipSlot === 'main_hand')
    );
    const wieldingPolearm = isPolearm(mainHandWeapon);

    const noShield = !this._isEquippingShield(inventory);

    return {
      character: playerStats,
      inventory,
      activeQuickBonuses,
      unarmored: !this._isWearingArmor(inventory),
      noShield,
      // [Phase 4b-infra]: wieldingShield is the inverse of noShield. Adding both lets
      // condition checks read whichever flag reads naturally without negation.
      wieldingShield: !noShield,
      dualWielding: this._isDualWielding(inventory),
      wearingLightArmor: armorType.includes('light'),
      wearingMediumArmor: armorType.includes('medium'),
      wearingHeavyArmor: armorType.includes('heavy'),
      // [Phase 4b bucket 2 — APR 2026]: Negated flag for "not heavy armor" used by
      // Barbarian Fast Movement (PHB p.49 — speed bonus only when not in heavy armor).
      // Modeling negations explicitly avoids hardcoding combat-tracker checks for
      // "if heavy armor, skip fast movement."
      notWearingHeavyArmor: !armorType.includes('heavy'),
      wieldingPolearm,
      // [SUB-PLAN 1H-H-cond Phase 1 — 2026-05-29]: Expose the main-hand weapon's baseType
      // so structured condition leaves (e.g. {category:'equippedWeaponType', op:'hasAny',
      // operands:['longbow','shortbow']}) can filter effects by the equipped weapon type.
      // mainHandWeapon is already computed above (line ~1147) for polearm detection.
      equippedWeaponBaseType: mainHandWeapon?.baseType || null,
      // [Phase 4b bucket 3 — APR 2026]: Combat-toggle flags. Default false; future
      // combat panel will flip these on player declaration (per-attack toggles).
      // Schema seam: playerStats._activeFlags.<name>. UI ticket: BC-2 in spec.
      // Persistence-to-autosave decision is deferred to BC-15 (must match save/load).
      sneakAttackActive: !!playerStats?._activeFlags?.sneakAttackActive,
      huntersMarkTargetActive: !!playerStats?._activeFlags?.huntersMarkTargetActive,
      attackingHexbladeCursedTarget: !!playerStats?._activeFlags?.attackingHexbladeCursedTarget,
      attackingSurprisedTarget: !!playerStats?._activeFlags?.attackingSurprisedTarget,
      targetBelowFullHp: !!playerStats?._activeFlags?.targetBelowFullHp,
      attackingFavoredEnemy: !!playerStats?._activeFlags?.attackingFavoredEnemy,
      attackingVowedTarget: !!playerStats?._activeFlags?.attackingVowedTarget,
      // [Phase 4b bucket 3 — Cleric Task 10]: Cantrip-cast flag for Cleric Potent
      // Spellcasting (Knowledge/Light/Grave/Peace L8). Class-specific to mirror the
      // RAW restriction "cleric cantrips" only — distinct from a future generic
      // castingCantripDamage flag (Wizard Empowered Evocation, Druid Potent
      // Spellcasting, etc., all want their own per-class flag). Flipped by combat
      // panel / spellCastingService when a cleric cantrip's damage is being rolled.
      castingClericCantripDamage: !!playerStats?._activeFlags?.castingClericCantripDamage,
      // [Phase 4b bucket 3 — Sorcerer Task 12]: Draconic-element-spell flag for Draconic
      // Bloodline Elemental Affinity (L6). Flipped by spellCastingService when the cast
      // spell's damage type matches subclassData.dragonAncestor → dragonOptions.damageType
      // (PHB p.102). Enables damageBonus add cha_modifier on the matched damage roll.
      castingDraconicElementSpell: !!playerStats?._activeFlags?.castingDraconicElementSpell,
      // [Phase 4b bucket 3 — Wizard Task 15]: Evocation-spell-damage flag for School of
      // Evocation Empowered Evocation (L10). Flipped by spellCastingService when the
      // cast spell is an evocation with a damage roll. Enables damageBonus add
      // intelligence_modifier on one damage roll per cast (PHB p.117).
      castingEvocationSpellDamage: !!playerStats?._activeFlags?.castingEvocationSpellDamage,
      // [Phase 4b bucket 2 — APR 2026]: Crit-extra-dice flag for Barbarian Brutal
      // Critical (L9: +1 die, L13: +2, L17: +3) and any future feat/feature that
      // adds dice on a confirmed critical hit. Combat panel sets true at the moment
      // a critical hit is rolled; consumers register conditional damageBonus add_dice
      // effects with condition: { when: 'criticalHit' }.
      criticalHit: !!playerStats?._activeFlags?.criticalHit,
      // [Phase 4b bucket 2 — APR 2026]: Convenience flag — true when ANY armor (light,
      // medium, or heavy) is equipped. Used by Fighter/Paladin/Ranger Defense fighting
      // style (+1 AC while in any armor) and similar conditional effects that don't
      // care about the specific armor type.
      wearingAnyArmor: armorType.includes('light') || armorType.includes('medium') || armorType.includes('heavy'),
      // [SUB-PLAN 1H-H-cond Phase 2 — raging — 2026-05-29]: Rage flag for the ~30
      // Barbarian subclass effects (Totem Bear resistances, Berserker Frenzy,
      // Ancestral Guardians, Storm Herald, etc.) that gate on {when:'raging'}.
      // The Rage preset in QuickBonusesPanel stamps type:'rage' (L273).
      // Uses findQuickBonusOfKind so the detection mirrors all other quick-bonus
      // reads (Rule 11 — no raw b.type === 'rage' string check here).
      raging: !!(findQuickBonusOfKind(activeQuickBonuses, 'rage')),
    };
  }

  // ========================================
  // SPEED CALCULATIONS
  // ========================================

  /**
   * Calculate movement speed with ALL modifiers
   * [PHASE 2.1 - NOV 15, 2025]: INTEGRATED WITH EFFECTREGISTRY
   * [BEFORE]: Manual parsing of feats, items, quick bonuses, and conditions
   * [AFTER]: Query EffectRegistry for all modifying effects
   *
   * D&D 5e Speed:
   * 1. Base speed from race (usually 30ft, some 25ft, some 35ft)
   * 2. Feat bonuses → FROM EffectRegistry (registered by FeatsPanel)
   * 3. Magic items → FROM EffectRegistry (registered by InventoryContext)
   * 4. Class features (Barbarian Fast Movement = +10ft at level 5)
   * 5. Conditions → FROM EffectRegistry (registered by ConditionsTracker)
   * 6. Spells/Bonuses → FROM EffectRegistry (registered by QuickBonusesPanel)
   * 7. Exhaustion (permanent character state) - NOT in EffectRegistry
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @param {Object} [settings] - Optional user settings (Batch 5 / P0 C-4).
   *   settings.exhaustionRules takes precedence over playerStats.
   * @returns {number} Speed in feet
   */
  calculateSpeed(playerStats, inventory, activeQuickBonuses, settings) {
    // [BATCH 5 / P0 C-4 — APR 2026]: Cache key must include exhaustion
    //   inputs so flipping settings.exhaustionRules invalidates the entry.
    //   Pre-fix the cache returned stale results when settings changed.
    const cacheKey = `speed_${playerStats.level}_${playerStats.exhaustionLevel || 0}_${settings?.exhaustionRules || playerStats.exhaustionRules || '5e'}`;

    return this._cached(cacheKey, () => {
      // ===== PHASE 2.1: NEW EFFECTREGISTRY-INTEGRATED FLOW =====

      // Step 1: Base speed (default 30ft)
      let baseSpeed = playerStats.speed || 30;

      // Step 2: Query EffectRegistry for speed modifiers
      // (feats like Mobile, items, bonuses like Haste, conditions like Grappled)
      if (this.useEffectRegistry) {
        // [Phase 4a]: _buildContext propagates unarmored/noShield/dualWielding flags so
        // conditional speed effects (e.g. "+10 speed when unarmored") resolve correctly.
        const context = this._buildContext(playerStats, inventory, activeQuickBonuses);

        // [PHASE 2.2 - NOV 15, 2025]: Pass playerStats for conditional effect checks
        let finalSpeed = this.effectRegistry.getEffectiveValue('speed', baseSpeed, playerStats, context);

        // Step 3: Apply exhaustion effects (PERMANENT character state)
        // [NOTE]: Exhaustion is not in EffectRegistry - it's a permanent character property
        // [BATCH 5 / P0 C-4 — APR 2026]: settings.exhaustionRules wins over playerStats.
        const exhaustionLevel = playerStats.exhaustionLevel || 0;
        const exhaustionRules = settings?.exhaustionRules || playerStats.exhaustionRules || '5e';

        if (exhaustionRules === '5e') {
          if (exhaustionLevel >= 5) {
            finalSpeed = 0; // Level 5+: Speed reduced to 0
          } else if (exhaustionLevel >= 2) {
            finalSpeed = Math.floor(finalSpeed / 2); // Level 2-4: Speed halved
          }
        }

        return Math.max(0, finalSpeed);
      }

      // ===== LEGACY FALLBACK (if EffectRegistry disabled) =====
      let speed = baseSpeed;

      // 2. Feat bonuses (manual parsing - LEGACY)
      (playerStats.feats || []).forEach(feat => {
        const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);

        // 2a. Passive effects (always on)
        // [v1.0 ARC-008]: Skip legacy path if feat has modern effects array
        if (featData?.effects && Array.isArray(featData.effects)) return;
        if (featData?.passiveEffects?.speed) {
          speed += featData.passiveEffects.speed;
        }

        // [UX-18 - NOV 15, 2025]: 2b. Toggle effects (when active)
        if (featData?.toggleEffects && feat.activeToggles) {
          if (Array.isArray(featData.toggleEffects)) {
            // NEW FORMAT: Array of toggles
            featData.toggleEffects.forEach(toggleDef => {
              const toggleId = toggleDef.name;
              if (feat.activeToggles[toggleId]) {
                (toggleDef.effects || []).forEach(effect => {
                  if (effect.attribute === 'speed') {
                    speed += effect.value || 0;
                  }
                });
              }
            });
          } else {
            // OLD FORMAT: Object (if old format had speed support)
            Object.entries(featData.toggleEffects).forEach(([toggleId, toggleDef]) => {
              if (feat.activeToggles[toggleId] && toggleDef.speed) {
                speed += toggleDef.speed;
              }
            });
          }
        }
      });

      // 3. Magic item bonuses (manual parsing - LEGACY)
      (inventory || []).forEach(item => {
        if (!item.equipped) return;

        const statMods = item.statModifications || item.passiveEffects?.statModifications;
        if (statMods?.speed) {
          speed += statMods.speed;
        }
      });

      // 4. Temporary effects (manual parsing - LEGACY)
      (activeQuickBonuses || []).forEach(bonus => {
        if (bonus.type === 'speedBonus') {
          speed += bonus.value || 0;
        }
        if (bonus.type === 'speedMultiplier') {
          speed *= bonus.value || 1;
        }
      });

      // 5. Condition effects (manual parsing - LEGACY)
      speed = this.getConditionSpeedModifier(playerStats, speed);

      // 6. Exhaustion effects (permanent character state)
      // [BATCH 5 / P0 C-4 — APR 2026]: settings.exhaustionRules wins.
      const exhaustionLevel = playerStats.exhaustionLevel || 0;
      const exhaustionRules = settings?.exhaustionRules || playerStats.exhaustionRules || '5e';

      if (exhaustionRules === '5e') {
        if (exhaustionLevel >= 5) {
          speed = 0;
        } else if (exhaustionLevel >= 2) {
          speed = Math.floor(speed / 2);
        }
      }

      return Math.max(0, speed);
    });
  }

  /**
   * [PURPOSE]: Calculate passive perception centrally
   * [WHY]: Was computed differently in VitalStatsCard (missing expertise + effectRegistry)
   *        vs SensesMovementPanel (correct). Now a single source of truth.
   * [D&D 5e]: Passive Perception = 10 + WIS mod + proficiency (if proficient) + bonuses
   * [INTEGRATION]: Queries effectRegistry for Observant feat and other passivePerception bonuses
   */
  calculatePassivePerception(playerStats, inventory, activeQuickBonuses) {
    const wisMod = this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, 'wisdom');
    const profBonus = this.getProficiencyBonus(playerStats);
    const isProficient = playerStats.proficiencies?.skills?.includes('perception') || false;
    const hasExpertise = playerStats.proficiencies?.expertise?.includes('perception') || false;

    let base = 10 + wisMod;
    if (hasExpertise) {
      base += profBonus * 2;
    } else if (isProficient) {
      base += profBonus;
    }

    // [Phase 4a]: _buildContext propagates unarmored/noShield/dualWielding flags so
    // conditional passive perception effects resolve correctly.
    // NOTE: Observant's +5 already routes through effectRegistry's _generateCalculatedStatEffects
    // virtual effects path (effectRegistry.js line ~917 — feat-by-name match). We deliberately do NOT
    // also query 'skill:perception' here, since that would double-count Observant.
    return effectRegistry.getEffectiveValue('passivePerception', base, playerStats, this._buildContext(playerStats, inventory, activeQuickBonuses));
  }

  // ========================================
  // SKILL BONUS CALCULATIONS
  // ========================================

  /**
   * Calculate skill bonus with proficiency, expertise, and feat effects
   *
   * D&D 5e Skills System:
   * - Base: Ability modifier
   * - Proficiency levels:
   *   - 0 (none): No proficiency bonus (unless Jack of All Trades)
   *   - 1 (proficient): +proficiency bonus
   *   - 2 (expertise): +proficiency bonus × 2
   * - Jack of All Trades: Add ⌊proficiency bonus / 2⌋ to unproficient skills
   * - Feat bonuses:
   *   - Observant: +5 to Passive Perception/Investigation
   *   - Skill Expert: +proficiency to one skill
   *   - Prodigy: +expertise to one skill
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of equipped items
   * @param {Array} activeQuickBonuses - Array of temporary bonuses
   * @param {String} skillKey - Skill identifier (e.g., 'perception', 'athletics')
   * @param {String} skillAbility - Associated ability score (e.g., 'wisdom', 'strength')
   * @param {Number} proficiencyLevel - 0 (none), 1 (proficient), 2 (expertise)
   * @returns {Object} { total, breakdown: { ability, proficiency, jackOfAllTrades, feat } }
   */
  calculateSkillBonus(playerStats, inventory, activeQuickBonuses, skillKey, skillAbility, proficiencyLevel = 0) {
    // [Phase 4a]: Check effectRegistry for granted proficiencies (custom feats, invocations)
    const registryProfLevel = this.effectRegistry.getGrantedProficiencyLevel(skillKey, playerStats);
    const effectiveProfLevel = Math.max(proficiencyLevel, registryProfLevel);

    const cacheKey = `skill_${skillKey}_${effectiveProfLevel}_${playerStats.level}`;

    return this._cached(cacheKey, () => {
      // 1. Get effective ability modifier (includes items, feats, temp effects)
      const effectiveScore = this.getEffectiveAbilityScore(
        playerStats,
        inventory,
        activeQuickBonuses,
        skillAbility
      );
      const abilityMod = Math.floor((effectiveScore - 10) / 2);

      // 2. Get proficiency bonus
      const profBonus = this.getProficiencyBonus(playerStats);

      // 3. Calculate proficiency bonus to apply
      let proficiencyBonus = 0;
      let jackOfAllTradesBonus = 0;

      if (effectiveProfLevel === 1) {
        // Proficient: +proficiency bonus
        proficiencyBonus = profBonus;
      } else if (effectiveProfLevel === 2) {
        // Expertise: +proficiency bonus × 2
        proficiencyBonus = profBonus * 2;
      } else if (effectiveProfLevel === 0) {
        // Not proficient: Check for Jack of All Trades
        // [FIX JAN 2026]: Check both string and object formats for features
        // [WHY]: features can be stored as strings OR objects with .name property
        // [Phase 4b bucket 2 — APR 2026]: Also check the registry feature flag
        // 'bard_jack_of_all_trades' (registered by srdClassFeatures Bard L2). This
        // makes the wiring consistent regardless of whether the wizard adds 'Jack
        // of All Trades' to playerStats.features (legacy path) or relies on the
        // class-feature registry (modern path). Custom Bard-style classes can
        // register the same flag.
        const hasJackOfAllTrades = playerStats.jackOfAllTrades ||
          playerStats.features?.some(f =>
            (typeof f === 'string' ? f : f.name) === 'Jack of All Trades'
          ) ||
          this.hasFeatureFlag(playerStats, inventory || [], 'bard_jack_of_all_trades');
        if (hasJackOfAllTrades) {
          jackOfAllTradesBonus = Math.floor(profBonus / 2);
        }
      }

      // 4. Apply feat bonuses
      let featBonus = 0;
      (playerStats.feats || []).forEach(feat => {
        const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);
        if (!featData?.passiveEffects) return;
        // [v1.0 ARC-008]: Skip legacy path if feat has modern effects array (handled by effectRegistry)
        if (featData.effects && Array.isArray(featData.effects)) return;

        // Observant feat: +5 to Passive Perception and Investigation
        if (featData.id === 'observant') {
          if (skillKey === 'perception' || skillKey === 'investigation') {
            featBonus += 5;
          }
        }

        // Check for skill-specific bonuses in passiveEffects
        if (featData.passiveEffects.skills?.[skillKey]) {
          const skillEffect = featData.passiveEffects.skills[skillKey];

          // Skill Expert / Prodigy: Grant proficiency or expertise
          if (skillEffect.proficiency && effectiveProfLevel === 0) {
            proficiencyBonus = profBonus;
          }
          if (skillEffect.expertise && effectiveProfLevel === 1) {
            proficiencyBonus = profBonus * 2;
          }

          // Direct bonus
          if (skillEffect.bonus) {
            featBonus += skillEffect.bonus;
          }
        }
      });

      // 5. Apply registry-based skill bonuses (modern effects: 'skill' type, target = skill key)
      // [Phase 4b bucket 5 — APR 2026]: Consumes type: 'skill', target: <skillKey> effects from
      // feats (Observant, Skilled-style customs) and other sources via _getStatKey('skill:perception').
      // Legacy hardcoded path above (featBonus +5 for Observant) is gated to feats WITHOUT modern
      // effects arrays — modern feats route here so we don't double-count.
      const registrySkillBonus = this.effectRegistry.getEffectiveValue(
        `skill:${skillKey}`,
        0,
        playerStats,
        this._buildContext(playerStats, inventory, activeQuickBonuses)
      );

      // 6. Calculate total
      const total = abilityMod + proficiencyBonus + jackOfAllTradesBonus + featBonus + registrySkillBonus;

      return {
        total,
        breakdown: {
          ability: abilityMod,
          proficiency: proficiencyBonus,
          jackOfAllTrades: jackOfAllTradesBonus,
          feat: featBonus,
          registry: registrySkillBonus
        }
      };
    });
  }

  // ========================================
  // WEAPON COMBAT CALCULATIONS
  // ========================================

  /**
   * Calculate weapon attack bonus
   *
   * D&D 5e Attack Roll Formula:
   * - d20 + ability modifier + proficiency bonus + magic bonus + feat bonuses
   * - Ability modifier:
   *   - Finesse weapons: Use higher of STR or DEX
   *   - Thrown/Ammunition: Use DEX
   *   - Default (melee): Use STR
   * - Proficiency: Based on character level
   * - Magic bonus: +1, +2, +3 from weapon
   * - Feat bonuses: Archery (+2 ranged), etc.
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of equipped items
   * @param {Array} activeQuickBonuses - Array of temporary bonuses
   * @param {Object} weapon - Specific weapon object
   * @param {Object} weaponData - Weapon template from gameData
   * @returns {Object} { total, dice, breakdown: { proficiency, ability, magic, feat, temporary } }
   */
  calculateWeaponAttackBonus(playerStats, inventory, activeQuickBonuses, weapon, weaponData) {
    const cacheKey = `attack_${weapon.id}_${playerStats.level}`;

    return this._cached(cacheKey, () => {
      // 1. Get proficiency bonus
      const profBonus = this.getProficiencyBonus(playerStats);

      // 2. Determine which ability modifier to use
      const effectiveSTR = this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, 'strength');
      const effectiveDEX = this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, 'dexterity');

      const strMod = Math.floor((effectiveSTR - 10) / 2);
      const dexMod = Math.floor((effectiveDEX - 10) / 2);

      let abilityMod = strMod; // Default to STR for melee

      // Finesse weapons: Use higher of STR or DEX
      if (weaponData?.properties?.includes('Finesse')) {
        abilityMod = Math.max(strMod, dexMod);
      }
      // Ammunition (ranged): Use DEX
      else if (weaponData?.properties?.some(prop => prop.includes('Ammunition'))) {
        abilityMod = dexMod;
      }

      // 3. Get magic weapon bonus
      // [ATTUNEMENT SYSTEM - OCT 25, 2025]: Only apply magic bonus if attuned (when required)
      // WHY: +1/+2/+3 weapons that require attunement don't provide bonus without attunement
      let magicBonus = 0;
      if (weapon.requiresAttunement && !weapon.attuned) {
        magicBonus = 0; // No bonus if attunement required but not attuned
      } else {
        magicBonus = weapon.magicalBonus || weapon.magicBonus || 0;
      }

      // 4. Apply feat bonuses
      let featBonus = 0;
      (playerStats.feats || []).forEach(feat => {
        const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);
        if (!featData) return;

        // Passive feat effects
        // [v1.0 ARC-008]: Skip legacy path if feat has modern effects array
        if (featData.passiveEffects && !(featData.effects && Array.isArray(featData.effects))) {
          // Archery fighting style: +2 to ranged weapon attacks
          if (featData.passiveEffects.archery && weaponData?.properties?.some(p => p.includes('Ammunition'))) {
            featBonus += 2;
          }

          // General attack bonus from feats
          if (featData.passiveEffects.attackBonus) {
            featBonus += featData.passiveEffects.attackBonus;
          }
        }

        // [FEAT TOGGLE SYSTEM - OCT 25, 2025]: Check for active toggle effects
        // PURPOSE: Apply attack penalties/bonuses from player-activated feat modes
        // WHY: Great Weapon Master and Sharpshooter allow trading accuracy for damage (-5 hit, +10 damage)
        // INTEGRATION: Reads from playerStats.feats[].activeToggles, applies attackMod from srdFeats toggleEffects
        // [UX-18 - NOV 15, 2025]: Support both OLD object format and NEW array format
        if (featData.toggleEffects && feat.activeToggles) {
          // [UX-18]: Check if using NEW array format or OLD object format
          if (Array.isArray(featData.toggleEffects)) {
            // [UX-18]: NEW FORMAT - Array of toggle objects with flexible effects
            // Format: [{ name: 'Power Attack', description: '...', effects: [{attribute, value}], appliesTo: '...' }]
            featData.toggleEffects.forEach(toggleDef => {
              const toggleId = toggleDef.name; // Use name as toggle ID

              // Check if this toggle is active
              if (feat.activeToggles[toggleId]) {
                // [UX-18]: Check weapon type applicability (if specified)
                const appliesTo = toggleDef.appliesTo;
                let applies = !appliesTo; // If no appliesTo, always applies

                if (appliesTo) {
                  // [FIX APR 2026 sub-project G]: Use weaponAppliesTo helper (SSOT).
                  applies = weaponAppliesTo(appliesTo, weaponData);
                }

                if (applies) {
                  // [UX-18]: Apply effects from array
                  (toggleDef.effects || []).forEach(effect => {
                    if (effect.attribute === 'attackBonus') {
                      featBonus += effect.value || 0;
                    }
                  });
                }
              }
            });
          } else {
            // [UX-18]: OLD FORMAT - Object with toggle IDs as keys (backward compatibility)
            // Format: { powerAttack: { name, description, attackMod, damageMod, appliesTo } }
            Object.entries(featData.toggleEffects).forEach(([toggleId, toggleDef]) => {
              // Check if this toggle is active
              if (feat.activeToggles[toggleId]) {
                // [FEAT TOGGLE WEAPON TYPE CHECKS - OCT 25, 2025]: Complete weapon type support
                // PURPOSE: Ensure feat toggles apply to correct weapon types
                // WHY: GWM only works with heavy/two-handed, Sharpshooter only with ranged
                // [FIX APR 2026 sub-project G]: Delegates to weaponAppliesTo SSOT.
                if (weaponAppliesTo(toggleDef.appliesTo, weaponData)) {
                  featBonus += toggleDef.attackMod || 0;
                }
              }
            });
          }
        }
      });

      // 5. Apply temporary bonuses
      let tempBonus = 0;
      if (playerStats.temporaryStats?.attackBonus) {
        tempBonus += playerStats.temporaryStats.attackBonus;
      }

      // 6. Check for Bless (adds 1d4 as dice, not flat bonus)
      // [PHASE B / #039 — 2026-05-06]: data-block detection on `type:` /
      //   `id:` only. Legacy `b.name === 'Bless'` fallback dropped per
      //   `feedback_no_users_no_migration.md` — schema is FREE.
      let blessDice = 0;
      if (activeQuickBonuses) {
        // [Sub-plan 1Q-F-5b-ii — 2026-05-28]: findQuickBonusOfKind replaces dual
        //   b.id || b.type check; the helper preserves both id and type compat.
        const bless = findQuickBonusOfKind(activeQuickBonuses, 'bless');
        if (bless) {
          blessDice = '1d4';
        }
      }

      // 7. [Phase 4b-infra → Phase B / #308]: Apply effectRegistry attack bonuses.
      // Universal `attackBonus` (e.g., +1 to all attack rolls feats) buffs weapon AND spell.
      // Weapon-specific `weaponAttackBonus` only buffs weapon attacks (counterpart to
      // `spellAttackBonus` in calculateSpellAttackBonus). Legacy passiveEffects and
      // toggleEffects paths above remain authoritative for their sources (gated on
      // no-effects-array via ARC-008).
      const universalBonus = this.effectRegistry.getEffectiveValue('attackBonus', 0, playerStats);
      const weaponSpecificBonus = this.effectRegistry.getEffectiveValue('weaponAttackBonus', 0, playerStats);
      const registryBonus = universalBonus + weaponSpecificBonus;

      // 8. Calculate total
      const total = profBonus + abilityMod + magicBonus + featBonus + tempBonus + registryBonus;

      return {
        total,
        dice: blessDice,
        breakdown: {
          proficiency: profBonus,
          ability: abilityMod,
          magic: magicBonus,
          feat: featBonus,
          temporary: tempBonus,
          registry: registryBonus
        }
      };
    });
  }

  /**
   * Calculate weapon damage bonus
   *
   * D&D 5e Damage Roll Formula:
   * - Weapon damage dice + ability modifier + magic bonus + feat bonuses
   * - Ability modifier: Same logic as attack bonus (Finesse, Thrown, etc.)
   * - Magic bonus: +1, +2, +3 from weapon
   * - Feat bonuses:
   *   - Great Weapon Master: +10 (if toggled) for heavy/two-handed
   *   - Sharpshooter: +10 (if toggled) for ranged
   *   - Dueling: +2 for one-handed melee
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of equipped items
   * @param {Array} activeQuickBonuses - Array of temporary bonuses
   * @param {Object} weapon - Specific weapon object
   * @param {Object} weaponData - Weapon template from gameData
   * @returns {Number} Total damage bonus
   */
  calculateWeaponDamageBonus(playerStats, inventory, activeQuickBonuses, weapon, weaponData) {
    const cacheKey = `damage_${weapon.id}_${playerStats.level}`;

    return this._cached(cacheKey, () => {
      // 1. Determine which ability modifier to use (same logic as attack)
      const effectiveSTR = this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, 'strength');
      const effectiveDEX = this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, 'dexterity');

      const strMod = Math.floor((effectiveSTR - 10) / 2);
      const dexMod = Math.floor((effectiveDEX - 10) / 2);

      let abilityMod = strMod; // Default to STR

      // Finesse weapons: Use higher of STR or DEX
      if (weaponData?.properties?.includes('Finesse')) {
        abilityMod = Math.max(strMod, dexMod);
      }
      // Ammunition (ranged): Use DEX
      else if (weaponData?.properties?.some(prop => prop.includes('Ammunition'))) {
        abilityMod = dexMod;
      }

      // 2. Get magic weapon bonus
      // [ATTUNEMENT SYSTEM - OCT 25, 2025]: Only apply magic bonus if attuned (when required)
      let magicBonus = 0;
      if (weapon.requiresAttunement && !weapon.attuned) {
        magicBonus = 0; // No bonus if attunement required but not attuned
      } else {
        magicBonus = weapon.magicalBonus || weapon.magicBonus || 0;
      }

      // 3. Apply feat bonuses
      let featBonus = 0;
      (playerStats.feats || []).forEach(feat => {
        const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);
        if (!featData?.passiveEffects) return;
        // [v1.0 ARC-008]: Skip legacy path if feat has modern effects array (handled by effectRegistry)
        if (featData.effects && Array.isArray(featData.effects)) return;

        // Dueling fighting style: +2 to one-handed melee
        if (featData.passiveEffects.dueling) {
          // Check if wielding one-handed melee weapon with no offhand weapon
          const isOneHanded = !weaponData?.properties?.some(p =>
            p.includes('Two-Handed')
          );
          const isMelee = !weaponData?.properties?.some(p => p.includes('Ammunition'));

          // TODO: Check for offhand weapon in inventory
          if (isOneHanded && isMelee) {
            featBonus += 2;
          }
        }

        // General damage bonus from feats
        if (featData.passiveEffects.damageBonus) {
          featBonus += featData.passiveEffects.damageBonus;
        }
      });

      // 4. Check for active toggle effects (Great Weapon Master, Sharpshooter, etc.)
      // [FEAT TOGGLE SYSTEM - OCT 25, 2025]: Apply damage bonuses from player-activated feat modes
      // PURPOSE: Apply damage bonuses from feat toggles (GWM +10, Sharpshooter +10)
      // WHY: Great Weapon Master and Sharpshooter allow trading accuracy for damage (-5 hit, +10 damage)
      // INTEGRATION: Reads from playerStats.feats[].activeToggles, applies damageMod from srdFeats toggleEffects
      // [UX-18 - NOV 15, 2025]: Support both OLD object format and NEW array format
      (playerStats.feats || []).forEach(feat => {
        const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);
        if (!featData?.toggleEffects || !feat.activeToggles) return;

        // [UX-18]: Check if using NEW array format or OLD object format
        if (Array.isArray(featData.toggleEffects)) {
          // [UX-18]: NEW FORMAT - Array of toggle objects with flexible effects
          featData.toggleEffects.forEach(toggleDef => {
            const toggleId = toggleDef.name; // Use name as toggle ID

            // Is this toggle active?
            if (feat.activeToggles[toggleId]) {
              // [FIX APR 2026 sub-project G]: Delegates to weaponAppliesTo SSOT.
              if (weaponAppliesTo(toggleDef.appliesTo, weaponData)) {
                // [UX-18]: Apply effects from array
                (toggleDef.effects || []).forEach(effect => {
                  if (effect.attribute === 'damageBonus') {
                    featBonus += effect.value || 0;
                  }
                });
              }
            }
          });
        } else {
          // [UX-18]: OLD FORMAT - Object with toggle IDs as keys (backward compatibility)
          // Check each toggle effect
          Object.entries(featData.toggleEffects).forEach(([toggleId, toggleDef]) => {
            // Is this toggle active?
            if (feat.activeToggles[toggleId]) {
              // [FEAT TOGGLE WEAPON TYPE CHECKS - OCT 25, 2025]: Complete weapon type support
              // PURPOSE: Ensure feat toggles apply to correct weapon types
              // WHY: GWM only works with heavy/two-handed, Sharpshooter only with ranged
              // [FIX APR 2026 sub-project G]: Delegates to weaponAppliesTo SSOT.
              if (weaponAppliesTo(toggleDef.appliesTo, weaponData)) {
                featBonus += toggleDef.damageMod || 0;
              }
            }
          });
        }
      });

      // 5. Apply temporary bonuses
      let tempBonus = 0;
      if (playerStats.temporaryStats?.damageBonus) {
        tempBonus += playerStats.temporaryStats.damageBonus;
      }

      // 6. [Phase 4b]: Apply effectRegistry damage bonuses (invocations, class features,
      // custom feats with effects arrays). The legacy passive/toggle paths above remain
      // authoritative for feats that do NOT have an effects array (ARC-008 skip rule).
      // This catches registered effects like agonizing_blast (cha_modifier).
      const registryBonus = this.effectRegistry.getEffectiveValue('damageBonus', 0, playerStats);

      // 7. Calculate total
      const total = abilityMod + magicBonus + featBonus + tempBonus + registryBonus;

      return total;
    });
  }

  // ========================================
  // CARRY CAPACITY CALCULATIONS
  // ========================================

  /**
   * Calculate carry capacity
   *
   * D&D 5e Rules:
   * - Base: STR score × 15 pounds
   * - Powerful Build (Goliath, Firbolg): STR score × 30 pounds
   * - Encumbrance levels:
   *   - Normal: Up to STR × 5
   *   - Encumbered: STR × 5 to STR × 10 (speed -10)
   *   - Heavily Encumbered: STR × 10 to STR × 15 (speed -20, disadvantage on checks using STR/DEX/CON)
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @returns {Object} { capacity, encumbered, heavilyEncumbered, powerfulBuild }
   */
  calculateCarryCapacity(playerStats, inventory, activeQuickBonuses) {
    const str = this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, 'strength');

    // Check for Powerful Build feature
    const hasPowerfulBuild = (playerStats.features || []).some(f =>
      f.name?.toLowerCase().includes('powerful build')
    );

    const multiplier = hasPowerfulBuild ? 30 : 15;
    const baseCapacity = str * multiplier;

    // [Phase 4b-infra]: Apply registry multiply/add effects for carryCapacity.
    // effectRegistry.convertLegacyEffect (effectRegistry.js:~1095) actively produces
    // carryCapacity multiply effects from the legacy 'carryCapacityMultiplier' key —
    // this consumer closes the producer/consumer loop. Races like Goliath / Bugbear
    // and items with carryCapacityMultiplier metadata now actually affect capacity.
    // Powerful Build feature detection above remains (so Goliath features still give
    // their 2× without needing a legacy effect registered separately).
    const capacity = this.effectRegistry.getEffectiveValue('carryCapacity', baseCapacity, playerStats);

    // Encumbrance thresholds
    const encumberedThreshold = str * 5;
    const heavilyEncumberedThreshold = str * 10;

    return {
      capacity,
      encumberedAt: encumberedThreshold,
      heavilyEncumberedAt: heavilyEncumberedThreshold,
      powerfulBuild: hasPowerfulBuild
    };
  }

  // ========================================
  // PROFICIENCY BONUS CALCULATION
  // ========================================

  /**
   * Calculate proficiency bonus based on character level
   *
   * D&D 5e Proficiency Bonus:
   * - Levels 1-4: +2
   * - Levels 5-8: +3
   * - Levels 9-12: +4
   * - Levels 13-16: +5
   * - Levels 17-20: +6
   *
   * [CLR-003 NOTE — MAY 2026]: There is a parallel formula at App.jsx:5290
   * (`Math.ceil(level/4) + 1`). Both produce identical results for L1-L20
   * but are mathematically distinct expressions. THIS method is the
   * canonical proficiency-bonus path; App.jsx call site should migrate
   * to use this in v1.0.x. Keep both identical until then; if either
   * formula changes, BOTH must change in lockstep.
   *
   * @param {Object} playerStats - Character stats object
   * @returns {number} Proficiency bonus
   */
  getProficiencyBonus(playerStats) {
    const level = playerStats.level || 1;
    return Math.floor((level - 1) / 4) + 2;
  }

  /**
   * Count extra attacks granted by registered effects (invocations, class features, feats).
   * Base "Extra Attack" class feature is tracked separately (it's a class feature, not an effect).
   * This returns only the extras added via effectRegistry (e.g., thirsting_blade).
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {number} Number of additional attacks to add to baseline attack count
   */
  getExtraAttacks(playerStats, inventory) {
    // [Phase 4b]: _buildContext propagates unarmored/noShield/dualWielding flags so future
    // conditional extra_attack effects (e.g., "extra attack when dual wielding") work uniformly.
    const context = this._buildContext(playerStats, inventory || [], []);
    return this.effectRegistry.getEffectiveValue('extra_attack', 0, playerStats, context);
  }

  /**
   * Returns the lowest natural d20 roll that scores a critical hit, given
   * any conditional context (target type, weapon, etc.). Default: 20.
   *
   * [Phase 4b bucket 3 — APR 2026]: New criticalRange consumer. Producers:
   * Champion Improved Critical (L3 → 19), Superior Critical (L15 → 18);
   * Hexblade's Curse autocrit-on-19 vs cursed; Assassin Assassinate autocrit
   * vs surprised. UI consumers (DiceTab + future combat panel) land in
   * separate tickets — this method closes the producer/consumer loop on commit
   * per silent-gap audit protocol.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @param {Object|null} context - optional pre-built context; rebuilds if null
   * @returns {number} The minimum natural d20 roll for a crit (default 20, floor 2)
   */
  getCriticalThreshold(playerStats, inventory, context = null) {
    const ctx = context || this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('criticalRange', playerStats, ctx);
    if (!effects || effects.length === 0) return 20;
    let threshold = 20;
    for (const e of effects) {
      if (e.operation === 'min') {
        const v = this.effectRegistry._getValue(e.value, false, playerStats);
        if (typeof v === 'number' && v < threshold) {
          threshold = v;
        }
      }
    }
    // Floor at 2 — d20 cannot roll less than 1, and natural 1 cannot crit per RAW
    return Math.max(2, threshold);
  }

  /**
   * Returns the array of currently-active damage bonuses (passing all condition
   * filters), with source attribution + formula resolution. Used by DiceTab to
   * surface "what's actually adding to my damage roll right now" so the player
   * can see Sneak Attack +5d6, Hexblade's Curse +PB, etc., before rolling.
   *
   * Each entry:
   *   sourceId: string  — the registration ID (e.g., 'class:Rogue:Sneak Attack')
   *   label:    string  — human-readable label (parsed from sourceId)
   *   kind:     'dice' | 'flat'
   *   value:    string  — dice notation ('5d6') for kind='dice', numeric string for kind='flat'
   *   operation: string — 'add' | 'add_dice' | 'multiply' (for breakdown display)
   *
   * [Phase 4b dice-tab integration — APR 2026]: Closes the visible-effect gap
   * for conditional damage bonuses. Without this surface, the registered
   * effects fired silently — the player had no way to see what was active.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @param {Array} activeQuickBonuses
   * @returns {Array<{sourceId, label, kind, value, operation}>}
   */
  getActiveDamageBonuses(playerStats, inventory, activeQuickBonuses = []) {
    if (!playerStats) return [];
    const context = this._buildContext(playerStats, inventory || [], activeQuickBonuses || []);
    const effects = this.effectRegistry.getEffectsForStat('damageBonus', playerStats, context);
    if (!Array.isArray(effects)) return [];

    const result = [];
    for (const effect of effects) {
      if (!effect || !effect.value) continue;
      const op = effect.operation;
      // Resolve the value through the formula resolver. Pass preserveDice=true
      // so formulas that produce dice strings (e.g., 'rogueLevel/2:d6:roundUp'
      // → '5d6') keep their dice form for display.
      const resolvedDice = this.effectRegistry._getValue(effect.value, true, playerStats);
      const resolvedNumeric = this.effectRegistry._getValue(effect.value, false, playerStats);

      let kind;
      let value;
      if (op === 'add_dice') {
        kind = 'dice';
        value = typeof resolvedDice === 'string' ? resolvedDice : String(resolvedDice || '');
        if (!value || value === '0d0' || value === '0') continue;
      } else if (op === 'add' || op === 'multiply') {
        kind = 'flat';
        value = String(resolvedNumeric || 0);
        if (!resolvedNumeric || resolvedNumeric === 0) continue;
      } else {
        // Unknown operation — skip rather than guess
        continue;
      }

      result.push({
        sourceId: effect.source || '',
        label: this._labelFromSourceId(effect.source || ''),
        kind,
        value,
        operation: op,
      });
    }
    return result;
  }

  /**
   * Parses a registration sourceId like 'class:Rogue:Sneak Attack' or
   * 'subclass:Warlock:The Hexblade:Hexblades Curse' and returns the most
   * meaningful display label (last segment after final ':').
   *
   * Internal helper for getActiveDamageBonuses (and any future per-source
   * surfacers). Falls back to the sourceId if no segment found.
   *
   * @param {string} sourceId
   * @returns {string}
   */
  _labelFromSourceId(sourceId) {
    if (!sourceId || typeof sourceId !== 'string') return '';
    const parts = sourceId.split(':');
    if (parts.length === 0) return sourceId;
    // Last segment is the most specific (feature name / option name).
    return parts[parts.length - 1] || sourceId;
  }

  /**
   * Check whether a registered effect overrides a weapon property at attack time.
   * Used by Crossbow Expert (ignores 'loading' on crossbows) and similar feats.
   *
   * [Phase 4b-infra APR 2026]: Closes the producer/consumer loop for the
   * 'weaponProperty' effect type. Crossbow Expert (srdFeats.js + Tasha's variant)
   * declares { type: 'weaponProperty', target: 'crossbow', operation: 'ignore',
   * value: 'loading' } — this method makes that data queryable. UI integration
   * (combat panel reading this at attack-roll time) is a separate ticket.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @param {string} weaponCategory - e.g., 'crossbow', 'bow', 'thrown'
   * @param {string} property - the property to check (e.g., 'loading')
   * @returns {boolean} true if any effect ignores this property for this weapon category
   */
  isWeaponPropertyIgnored(playerStats, inventory, weaponCategory, property) {
    if (!weaponCategory || !property) return false;
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('weaponProperty', playerStats, context);
    return effects.some(e =>
      e.operation === 'ignore' &&
      String(e.target || '').toLowerCase() === String(weaponCategory).toLowerCase() &&
      String(e.value || '').toLowerCase() === String(property).toLowerCase()
    );
  }

  /**
   * Check whether ranged-in-melee disadvantage is removed by registered effects.
   * Crossbow Expert grants this for any ranged weapon (PHB p.195).
   *
   * [Phase 4b-infra APR 2026]: Closes the producer/consumer loop for the
   * 'combatAdvantage' effect type with target 'ranged_in_melee'. UI integration
   * (combat panel using this to set the roll mode) is a separate ticket.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {boolean}
   */
  hasNoRangedInMeleeDisadvantage(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('combatAdvantage', playerStats, context);
    return effects.some(e =>
      e.operation === 'remove_disadvantage' &&
      String(e.target || '').toLowerCase() === 'ranged_in_melee' &&
      e.value === true
    );
  }

  /**
   * Check whether long-range disadvantage is removed by registered effects.
   * Sharpshooter grants this for any ranged weapon (PHB p.147).
   *
   * [Phase 4b-infra APR 2026]: Closes the producer/consumer loop for the
   * 'combatAdvantage' effect type with target 'ranged_long_range'. UI integration
   * (combat panel using this to set the roll mode) is a separate ticket.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {boolean}
   */
  hasNoLongRangeDisadvantage(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('combatAdvantage', playerStats, context);
    return effects.some(e =>
      e.operation === 'remove_disadvantage' &&
      String(e.target || '').toLowerCase() === 'ranged_long_range' &&
      e.value === true
    );
  }

  /**
   * Get the minimum HP recovery per Hit Die from registered effects.
   * Durable feat (PHB p.166): min recovery = max(2 × CON mod, 2).
   *
   * [Phase 4b-infra APR 2026]: Closes the producer/consumer loop for the
   * 'hitDieRecovery' effect type. Durable's effect uses a dynamic value
   * formula string ('max(2*constitutionModifier, 2)') which is resolved per
   * call against the current ability scores.
   *
   * Wired into rechargeTypes.rollHitDice: when a player spends a Hit Die
   * during a short rest, the gained HP = max(roll + CON mod, durable floor, 1).
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {number} 0 if no effect; the highest minimum value otherwise
   */
  getHitDieRecoveryMinimum(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('hitDieRecovery', playerStats, context);

    let minimum = 0;
    for (const effect of effects) {
      if (effect.operation !== 'minimum') continue;
      let value = 0;
      if (typeof effect.value === 'number') {
        value = effect.value;
      } else if (typeof effect.value === 'string') {
        // Resolve the formula. Currently only Durable uses 'max(2*constitutionModifier, 2)'.
        // Generalize: detect the common 'max(N*<ability>Modifier, M)' pattern.
        const match = effect.value.match(/max\(\s*(\d+)\s*\*\s*(\w+)Modifier\s*,\s*(\d+)\s*\)/);
        if (match) {
          const multiplier = parseInt(match[1], 10);
          const abilityName = match[2].toLowerCase(); // 'constitution', 'strength', etc.
          const floor = parseInt(match[3], 10);
          const score = playerStats[abilityName] || 10;
          const mod = Math.floor((score - 10) / 2);
          value = Math.max(multiplier * mod, floor);
        } else {
          // Fallback: try to resolve as a plain ability modifier or numeric string
          value = this.effectRegistry._getValue(effect.value, false, playerStats) || 0;
        }
      }
      if (value > minimum) minimum = value;
    }
    return minimum;
  }

  /**
   * Get the minimum value per damage die for a spell of a given damage type.
   * Elemental Adept (PHB p.166): treat any 1 on a damage die as a 2 (for the chosen element).
   *
   * [Phase 4b-infra APR 2026]: Closes the producer/consumer loop for the
   * 'damage' effect type with operation 'minimum'. The condition.check on the
   * Elemental Adept effect inspects context.damageType + context.isSpell, so this
   * method primes both before querying the registry. UI integration (rolling spell
   * damage applies this floor per die) is a separate ticket — methods are queryable now.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @param {string} damageType - e.g., 'fire', 'cold', 'acid'
   * @returns {number} 0 if no effect; the highest minimum value otherwise
   */
  getSpellDamageDieMinimum(playerStats, inventory, damageType) {
    if (!damageType) return 0;
    const context = this._buildContext(playerStats, inventory || [], []);
    context.damageType = String(damageType).toLowerCase();
    context.isSpell = true;
    const effects = this.effectRegistry.getEffectsForStat('damage', playerStats, context);

    let minimum = 0;
    for (const effect of effects) {
      if (effect.operation !== 'minimum') continue;
      const value = parseInt(effect.value, 10);
      if (value > minimum) minimum = value;
    }
    return minimum;
  }

  // ========================================
  // INITIATIVE CALCULATIONS
  // ========================================

  /**
   * Calculate initiative bonus
   *
   * D&D 5e Initiative:
   * - Base: DEX modifier
   * - Feat bonuses (Alert = +5)
   * - Magic items
   * - Class features (some Rogues get bonuses)
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @returns {number} Initiative bonus
   */
  calculateInitiative(playerStats, inventory, activeQuickBonuses) {
    // [PURPOSE]: Calculate initiative bonus with all modifiers
    // [WHY]: D&D 5e initiative = DEX mod + feat bonuses + item bonuses + manual bonus
    // [PHASE 3 - APR 2026]: Route through effectRegistry instead of manual feat/item parsing

    let initiative = this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, 'dexterity');

    // ===== EFFECTREGISTRY-INTEGRATED FLOW =====
    if (this.useEffectRegistry) {
      // [Phase 4a]: _buildContext propagates unarmored/noShield/dualWielding flags so
      // conditional initiative effects resolve correctly.
      const context = this._buildContext(playerStats, inventory, activeQuickBonuses);

      // Query EffectRegistry for initiative modifiers
      // (Alert feat +5, item effects, toggle effects — registered by FeatsPanel/InventoryContext)
      initiative = this.effectRegistry.getEffectiveValue('initiative', initiative, playerStats, context);

      // [FIX APR 2026 — AUDIT H-3]: Jack of All Trades applies to initiative (PHB p.54, Sage Advice)
      // [WHY]: Initiative is a DEX ability check. JoAT adds half proficiency (rounded down) to
      //        ability checks that don't include proficiency. Initiative never includes proficiency.
      // [Phase 4b bucket 2 — APR 2026]: Also check the registry feature flag for parity
      // with calculateSkillBonus (registered via srdClassFeatures Bard L2).
      const hasJackOfAllTrades = playerStats.jackOfAllTrades ||
        playerStats.features?.some(f =>
          (typeof f === 'string' ? f : f?.name) === 'Jack of All Trades'
        ) ||
        this.hasFeatureFlag(playerStats, inventory || [], 'bard_jack_of_all_trades');
      if (hasJackOfAllTrades) {
        initiative += Math.floor(this.getProficiencyBonus(playerStats) / 2);
      }

      // Manual initiative bonus (from StatsPanel input, not in EffectRegistry)
      if (playerStats.initiativeBonus) {
        initiative += playerStats.initiativeBonus;
      }

      return initiative;
    }

    // ===== LEGACY FALLBACK (if EffectRegistry disabled) =====

    // Feat bonuses (passive and toggle effects)
    (playerStats.feats || []).forEach(feat => {
      const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);

      // Passive effects (always on)
      // [v1.0 ARC-008]: Skip legacy path if feat has modern effects array
      if (featData?.passiveEffects?.initiative && !(featData?.effects && Array.isArray(featData.effects))) {
        initiative += featData.passiveEffects.initiative;
      }

      // [UX-18 - NOV 15, 2025]: Toggle effects (when active)
      if (featData?.toggleEffects && feat.activeToggles) {
        if (Array.isArray(featData.toggleEffects)) {
          // NEW FORMAT: Array of toggles
          featData.toggleEffects.forEach(toggleDef => {
            const toggleId = toggleDef.name;
            if (feat.activeToggles[toggleId]) {
              (toggleDef.effects || []).forEach(effect => {
                if (effect.attribute === 'initiative') {
                  initiative += effect.value || 0;
                }
              });
            }
          });
        } else {
          // OLD FORMAT: Object (if old format had initiative support)
          Object.entries(featData.toggleEffects).forEach(([toggleId, toggleDef]) => {
            if (feat.activeToggles[toggleId] && toggleDef.initiative) {
              initiative += toggleDef.initiative;
            }
          });
        }
      }
    });

    // Magic items
    (inventory || []).forEach(item => {
      if (!item.equipped) return;

      const statMods = item.statModifications || item.passiveEffects?.statModifications;
      if (statMods?.initiative) {
        initiative += statMods.initiative;
      }
    });

    // Active bonuses
    if (playerStats.initiativeBonus) {
      initiative += playerStats.initiativeBonus;
    }

    return initiative;
  }

  // ========================================
  // SPELL DC AND ATTACK BONUS
  // ========================================

  /**
   * Calculate spell save DC
   *
   * D&D 5e Formula: 8 + proficiency bonus + spellcasting ability modifier
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @returns {number} Spell save DC
   */
  calculateSpellDC(playerStats, inventory, activeQuickBonuses) {
    if (!playerStats.spellcastingAbility) return 10; // No spellcasting

    const ability = playerStats.spellcastingAbility;
    const abilityMod = this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, ability);
    const profBonus = this.getProficiencyBonus(playerStats);
    // [Phase 4b-infra]: Pick up spellDC effects registered via effectRegistry
    // (items that boost DC, class features, custom feats with effects arrays).
    // Same silent-gap pattern as b4e273c (damageBonus) and a964ea7 (attackBonus).
    const registryBonus = this.effectRegistry.getEffectiveValue('spellDC', 0, playerStats);

    return 8 + profBonus + abilityMod + registryBonus;
  }

  /**
   * Calculate spell attack bonus
   *
   * D&D 5e Formula: proficiency bonus + spellcasting ability modifier
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @returns {number} Spell attack bonus
   */
  calculateSpellAttackBonus(playerStats, inventory, activeQuickBonuses) {
    if (!playerStats.spellcastingAbility) return 0; // No spellcasting

    const ability = playerStats.spellcastingAbility;
    const abilityMod = this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, ability);
    const profBonus = this.getProficiencyBonus(playerStats);
    // [Phase B / #308 — 2026-05-12]: Spell attack bonuses query two shapes:
    //
    // 1. Universal `attackBonus`: feats like "+1 to all attack rolls" that buff
    //    both weapon and spell attacks. Returns a delta (no virtual effects for
    //    this key), so we can add it directly.
    //
    // 2. Spell-specific `spellAttackBonus`: items like Wand of the War Mage.
    //    NOTE: effectRegistry._generateCalculatedStatEffects treats 'spellAttackBonus'
    //    as a COMPLETE value and auto-adds profBonus + abilityMod as virtual effects.
    //    So getEffectiveValue('spellAttackBonus', 0, playerStats) = profBonus + abilityMod
    //    + registered item bonuses. We subtract the virtual base to isolate the item delta.
    //
    // See spec docs/superpowers/specs/2026-05-12-post-audit-integration-triage.md axis A.1.
    const universalBonus = this.effectRegistry.getEffectiveValue('attackBonus', 0, playerStats);
    const fullSpellAttackFromRegistry = this.effectRegistry.getEffectiveValue('spellAttackBonus', 0, playerStats);
    const spellSpecificBonus = fullSpellAttackFromRegistry - profBonus - abilityMod;

    return profBonus + abilityMod + universalBonus + spellSpecificBonus;
  }

  /**
   * Calculate spell save DC
   *
   * D&D 5e Formula: 8 + proficiency bonus + spellcasting ability modifier
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @returns {number} Spell save DC
   */
  calculateSpellSaveDC(playerStats, inventory, activeQuickBonuses) {
    if (!playerStats.spellcastingAbility) return 8; // Base DC with no spellcasting

    const ability = playerStats.spellcastingAbility;
    const abilityMod = this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, ability);
    const profBonus = this.getProficiencyBonus(playerStats);

    // Base DC is 8 + prof + ability mod
    let dc = 8 + profBonus + abilityMod;

    // Check for items that increase spell save DC
    inventory?.forEach(item => {
      if (item.equipped && item.statBonuses) {
        // Some items like Rod of the Pact Keeper increase spell save DC
        if (item.statBonuses.spellSaveDC) {
          dc += item.statBonuses.spellSaveDC;
        }
      }
    });

    // [Phase 4b-infra]: Pick up spellDC effects registered via effectRegistry.
    // NOTE: We intentionally query the SAME 'spellDC' type that calculateSpellDC
    // uses, mirroring the precedent where calculateWeaponAttackBonus and
    // calculateSpellAttackBonus both query 'attackBonus'. One shared bonus type
    // for any kind of spell DC boost (items, feats, class features).
    // Why NOT 'spellSaveDC': effectRegistry.getEffectsForStat auto-injects a
    // full virtual 8+prof+mod formula for the 'spellsavedc' stat key (see
    // _generateCalculatedStatEffects line 983). Querying that here would
    // double-count the base DC. 'spellDC' has no virtual generator and is safe.
    // Legacy item.statBonuses.spellSaveDC path above is preserved for existing
    // items; registry path is additive and produces no double-count because
    // nothing in-tree registers raw 'spellSaveDC' effects.
    dc += this.effectRegistry.getEffectiveValue('spellDC', 0, playerStats);

    return dc;
  }

  /**
   * Calculate concentration save DC
   *
   * D&D 5e PHB p.203: DC = 10 or half damage taken, whichever is higher
   * Save modifier = Constitution modifier + proficiency (if proficient in Con saves)
   *
   * @param {number} damageTaken - Amount of damage that triggered the save
   * @returns {number} Concentration save DC
   */
  calculateConcentrationSaveDC(damageTaken) {
    // Minimum DC is 10, or half the damage if higher
    return Math.max(10, Math.floor(damageTaken / 2));
  }

  /**
   * Calculate concentration save bonus
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @returns {number} Bonus to concentration saves
   */
  calculateConcentrationSaveBonus(playerStats, inventory, activeQuickBonuses) {
    // Concentration saves are Constitution saves
    let bonus = this.calculateSavingThrow(playerStats, inventory, activeQuickBonuses, 'constitution').total;

    // War Caster feat gives advantage on concentration saves (handled elsewhere)
    // Resilient (Constitution) adds proficiency (already handled in calculateSavingThrow)

    return bonus;
  }

  /**
   * Calculate rage damage bonus
   *
   * D&D 5e PHB p.48: Barbarian rage damage increases with level (+2 L1, +3 L9, +4 L16).
   *
   * [Phase 4b bucket 2 — APR 2026]: Migrated from a hardcoded rageDamageProgression
   * lookup (string-match on class data) to the registry-driven 'rage_damage_bonus'
   * formula resolved by effectRegistry._getValue. The Rage class feature itself is
   * registered as a damageBonus effect by useClassFeatureEffects, gated on the
   * 'raging' context flag at consumption time.
   *
   * This method returns the raw rage bonus VALUE (regardless of whether the
   * character is currently raging) so other UI/calculator paths can display the
   * potential bonus. Whether to actually apply the bonus to a damage roll is
   * decided by the caller via the `raging` context flag.
   *
   * @param {Object} playerStats - Character stats object
   * @returns {number} Rage damage bonus (0 if not Barbarian)
   */
  calculateRageDamage(playerStats) {
    return this.effectRegistry._getValue('rage_damage_bonus', false, playerStats) || 0;
  }

  /**
   * Calculate number of rages per day
   * [FIX APR 2026 — AUDIT M-1]: Data-driven via classResources.js maxTable (custom class support)
   *
   * @param {Object} playerStats - Character stats object
   * @returns {number} Number of rages
   */
  calculateRagesPerDay(playerStats) {
    // Find class with rage resource definition in classResources
    const { CLASS_RESOURCE_DEFINITIONS, resolveLevelTable } = require('../data/classResources');

    for (const cls of (playerStats.classes || [])) {
      const defs = CLASS_RESOURCE_DEFINITIONS[cls.className];
      if (!defs) continue;
      const rageDef = defs.find(d => d.id === 'rage');
      if (rageDef && rageDef.maxTable) {
        const max = resolveLevelTable(rageDef.maxTable, cls.level || 1);
        // 999 in the table means unlimited (level 20 Barbarian)
        return max >= 999 ? Infinity : (max || 0);
      }
    }

    return 0; // No rage class
  }

  // ========================================
  // SAVING THROW CALCULATIONS
  // ========================================

  /**
   * Calculate saving throw bonus with detailed breakdown
   *
   * D&D 5e Saving Throws:
   * - Base: Ability modifier
   * - Proficiency bonus (if proficient in that save)
   * - Feat bonuses (Resilient adds proficiency)
   * - Magic items (Cloak of Protection, etc.)
   * - Temporary bonuses (Bless, etc.)
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @param {string} ability - Ability name: 'strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'
   * @returns {Object} { total, breakdown: { ability, proficiency, feat, item } }
   */
  calculateSavingThrow(playerStats, inventory, activeQuickBonuses, ability) {
    // [PURPOSE]: Calculate saving throw bonus with full breakdown for display
    // [WHY]: QuickActionsBar needs detailed breakdown for status messages
    // [D&D RULES]: Save bonus = ability mod + prof (if proficient) + feat bonuses + item bonuses
    // [PHASE 3 - APR 2026]: Route feat/spell effects through effectRegistry

    const abilityMod = this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, ability);
    const profBonus = this.getProficiencyBonus(playerStats);

    // Check if proficient in this save
    const shortAbility = ability.slice(0, 3); // 'str', 'dex', etc.
    const classProficient = playerStats.proficiencies?.savingThrows?.includes(shortAbility) ||
                        playerStats.proficiencies?.savingThrows?.includes(ability);

    // [Phase 4b bucket 2 — APR 2026]: Also check registry for granted save
    // proficiency (Rogue Slippery Mind L15 → save:wisdom; Monk Diamond Soul L14 →
    // save:all; Resilient feat → save:<chosen>; etc.). getGrantedProficiencyLevel
    // accepts 'save:<ability>' since the Bucket 2 generalization, returning 0/1/2.
    // Saves cannot have expertise per RAW, so we collapse to a boolean for the
    // proficiency-bonus addition.
    const registryProf = this.effectRegistry.getGrantedProficiencyLevel(`save:${ability}`, playerStats);
    const isProficient = classProficient || registryProf > 0;

    let breakdown = {
      ability: abilityMod,
      proficiency: isProficient ? profBonus : 0,
      feat: 0,
      item: 0
    };

    // ===== EFFECTREGISTRY-INTEGRATED FLOW =====
    if (this.useEffectRegistry) {
      // [SUB-PLAN 1H-H-cond Phase 2 — E-5 — 2026-05-29]: Use _buildContext so ALL
      // armor/combat flags are available to save-conditioned effects (e.g. Paladin
      // Heavy Armor Master, raging). Previously this was built inline as a bare
      // {character, inventory, activeQuickBonuses} object, meaning every
      // equipment-state condition was silently false at save-query time.
      // Callers may spread save-specific extras (e.g. rollSourceType) on top.
      const context = this._buildContext(playerStats, inventory, activeQuickBonuses);

      // [Sub-plan 1H-H-cond GAP-8 — 2026-05-29]: Stamp rollSourceType when the
      // "Saving vs Spells" quick-bar toggle is active.  The staff_of_the_magi
      // (and any future item/feat) uses condition:{category:'rollSourceType',
      // op:'hasAny', operands:['vs_spells']} — the token MUST match 'vs_spells'
      // exactly because _resolveCategory returns a Set from context.rollSourceType.
      context.rollSourceType = findQuickBonusOfKind(activeQuickBonuses, 'spellSaveSource')
        ? 'vs_spells'
        : null;

      // Query EffectRegistry for save effects (registered by FeatsPanel/InventoryContext)
      // Effects indexed under 'save:<ability>' (e.g., Shield Master → 'save:dexterity')
      const abilityEffects = this.effectRegistry.getEffectsForStat(`save:${ability}`, playerStats, context);

      abilityEffects.forEach(effect => {
        // [WHY]: Resolve effect value — numbers pass through, formulas like 'cha_modifier'
        // route through _getValue, dice strings parse to integer averages.
        // [Phase 4b bucket 2 — APR 2026]: Was previously parseInt which dropped formula
        // strings to 0. This broke Paladin Aura of Protection (+CHA mod to saves) which
        // registers as { type: 'save:<ability>', operation: 'add', value: 'cha_modifier' }.
        const rawValue = effect.value;
        const resolved = typeof rawValue === 'number'
          ? rawValue
          : this.effectRegistry._getValue(rawValue, false, playerStats);
        const value = typeof resolved === 'number' ? resolved : (parseInt(resolved, 10) || 0);
        if (effect.source?.startsWith('item:')) {
          breakdown.item += value;
        } else {
          // Feats, spells, conditions, class features → feat bucket
          breakdown.feat += value;
        }
      });

      // Items with statModifications.saves (not registered in effectRegistry)
      // [WHY]: User-created items via EditItemDialog use statModifications, not effects arrays
      (inventory || []).forEach(item => {
        if (!item.equipped) return;
        const statMods = item.statModifications || item.passiveEffects?.statModifications;
        if (!statMods?.saves) return;

        if (statMods.saves[shortAbility]) {
          breakdown.item += statMods.saves[shortAbility];
        }
        if (statMods.saves.all) {
          breakdown.item += statMods.saves.all;
        }
      });

      // [STRUCT-C / FIX-10 — 2026-05-16]: Artificer Soul of Artifice (TCoE p.12).
      //   +1 to ALL saves per magic item attuned. Dynamic computation because
      //   the bonus depends on current inventory state (the flag tells us the
      //   feature is active; attunedItems.length tells us the magnitude).
      //   Cache invalidation: attuneToItem / unattuneItem in InventoryContext
      //   already call `effectCalculator.clearCache()` so the next save-bonus
      //   query reflects the change.
      if (this.hasFeatureFlag(playerStats, inventory || [], 'artificer_soul_of_artifice_save_bonus')) {
        const attunedCount = (inventory || []).filter(i => i?.attuned).length;
        if (attunedCount > 0) {
          breakdown.feat += attunedCount;
        }
      }

      const total = breakdown.ability + breakdown.proficiency + breakdown.feat + breakdown.item;
      return { total, breakdown };
    }

    // ===== LEGACY FALLBACK (if EffectRegistry disabled) =====

    // Feat bonuses (Resilient gives proficiency, some feats give flat bonuses)
    (playerStats.feats || []).forEach(feat => {
      const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);
      if (!featData?.passiveEffects) return;

      // Check for save bonuses
      if (featData.passiveEffects.saves) {
        // Specific save bonus
        if (featData.passiveEffects.saves[shortAbility]) {
          breakdown.feat += featData.passiveEffects.saves[shortAbility];
        }
        // All saves bonus
        if (featData.passiveEffects.saves.all) {
          breakdown.feat += featData.passiveEffects.saves.all;
        }
      }
    });

    // Magic item bonuses (Cloak of Protection, Ring of Protection, etc.)
    (inventory || []).forEach(item => {
      if (!item.equipped) return;

      const statMods = item.statModifications || item.passiveEffects?.statModifications;
      if (!statMods) return;

      // Specific save bonus
      if (statMods.saves?.[shortAbility]) {
        breakdown.item += statMods.saves[shortAbility];
      }
      // All saves bonus
      if (statMods.saves?.all) {
        breakdown.item += statMods.saves.all;
      }
    });

    const total = breakdown.ability + breakdown.proficiency + breakdown.feat + breakdown.item;

    return { total, breakdown };
  }

  // ========================================
  // UNARMED STRIKE DAMAGE
  // ========================================

  /**
   * Get the unarmed strike damage die size.
   * [Phase 4b-infra APR 2026]: Consumes the previously orphaned `unarmedDamage` effect type.
   * [WHY]: Producers (Tavern Brawler PHB p.170, Monk Martial Arts via class features,
   *        Xanathar's / Fizban's draconic options) registered `{ type: 'unarmedDamage',
   *        operation: 'set', value: '1dN' }` effects but no consumer applied them. UI rendered
   *        the base 1+STR die regardless of feats / class features.
   * [HOW]: Highest "set" die wins (PHB rule: a Monk who takes Tavern Brawler keeps their
   *        Martial Arts die because it's larger). Returns 1 when no upgrade is registered
   *        (base unarmed strike — 1 + STR mod per PHB p.195).
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items (currently unused but reserved for context)
   * @returns {number} Die size (1, 4, 6, 8, 10, 12) — highest registered upgrade or 1 if none
   */
  getUnarmedStrikeDamageDie(playerStats, inventory) {
    if (!this.useEffectRegistry) {
      // Legacy fallback: scan playerStats.feats for tavern_brawler-like passiveEffects
      const feats = playerStats?.feats || [];
      let maxDie = 1;
      feats.forEach(feat => {
        const featData = SRD_FEATS[feat.id] || playerStats?.customFeats?.find(f => f.id === feat.id);
        const die = featData?.passiveEffects?.unarmedDamage;
        if (die) {
          const match = String(die).match(/d?(\d+)/);
          if (match) maxDie = Math.max(maxDie, parseInt(match[1], 10) || 0);
        }
      });
      return maxDie;
    }

    const context = this._buildContext(playerStats, inventory || [], []);
    const sets = this.effectRegistry.getEffectsForStat('unarmedDamage', playerStats, context)
      .filter(e => e.operation === 'set');

    return sets.reduce((max, e) => {
      const match = String(e.value).match(/1d(\d+)/);
      const die = match ? parseInt(match[1], 10) : 0;
      return Math.max(max, die);
    }, 1); // baseline 1 = "1 + STR mod" base unarmed strike (PHB p.195)
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Calculate all effective stats at once
   * This is the main method to use for getting complete character stats
   *
   * @param {Object} playerStats - Character stats object
   * @param {Array} inventory - Array of items
   * @param {Array} activeQuickBonuses - Temporary effect bonuses
   * @returns {Object} Complete stat object with all modifications
   */
  calculateAllStats(playerStats, inventory, activeQuickBonuses) {
    return {
      // Ability scores
      strength: this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, 'strength'),
      dexterity: this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, 'dexterity'),
      constitution: this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, 'constitution'),
      intelligence: this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, 'intelligence'),
      wisdom: this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, 'wisdom'),
      charisma: this.getEffectiveAbilityScore(playerStats, inventory, activeQuickBonuses, 'charisma'),

      // Ability modifiers
      strMod: this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, 'strength'),
      dexMod: this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, 'dexterity'),
      conMod: this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, 'constitution'),
      intMod: this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, 'intelligence'),
      wisMod: this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, 'wisdom'),
      chaMod: this.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, 'charisma'),

      // Derived stats
      maxHP: this.calculateMaxHP(playerStats, inventory, activeQuickBonuses),
      ac: this.calculateAC(playerStats, inventory, activeQuickBonuses),
      speed: this.calculateSpeed(playerStats, inventory, activeQuickBonuses),
      initiative: this.calculateInitiative(playerStats, inventory, activeQuickBonuses),
      proficiencyBonus: this.getProficiencyBonus(playerStats),

      // Spellcasting
      spellDC: this.calculateSpellDC(playerStats, inventory, activeQuickBonuses),
      spellAttackBonus: this.calculateSpellAttackBonus(playerStats, inventory, activeQuickBonuses),

      // Carry capacity
      carryCapacity: this.calculateCarryCapacity(playerStats, inventory, activeQuickBonuses)
    };
  }

  // =====================================
  // EFFECT TRACKING & REVERSAL SYSTEM
  // =====================================
  // [NEW 2025-10-21]: Methods for tracking and reversing effects
  //
  // [ESR-006 DEPRECATION — MAY 2026]: This subsystem is dead code in
  //   production. Verified zero external callers via grep (`applyEffect`
  //   `removeEffectsFromSource` `getEffectBreakdown` are not invoked from
  //   any non-test file). The runtime canonical effect path is
  //   `effectRegistry.register/remove/getEffectsForStat/getEffectiveValue`
  //   in src/services/effectRegistry.js. THIS subsystem (`_activeEffects`,
  //   `_effectBreakdown` Maps + the methods below) was written for the
  //   pre-Phase-2 effect model and never wired to consumers.
  //
  //   Removal target: v1.0.1. Marked @deprecated for v1.0 (safer than
  //   deletion — if a downstream tool DID start using it, deletion
  //   would surprise; deprecation lets consumers migrate.)

  /**
   * Apply an effect and track its source.
   * @deprecated Since 2026-05-10 (ESR-006). Use effectRegistry.register
   *   instead. Will be removed in v1.0.1.
   * @param {string} source - Source identifier (e.g., 'feat_tough', 'item_123')
   * @param {string} type - Effect type (feat, item, spell, etc.)
   * @param {string} target - What stat/property is affected
   * @param {number|string} value - Effect value
   * @param {string} operation - How to apply ('add', 'set', 'multiply')
   * @returns {string} Effect ID for later removal
   */
  applyEffect(source, type, target, value, operation = 'add') {
    const effectId = `${source}_${type}_${target}_${Date.now()}`;
    const effect = {
      id: effectId,
      source,
      type,
      target,
      value,
      operation,
      timestamp: Date.now()
    };

    // Add to history
    // [ESR-007 FIX — MAY 2026]: Cap _effectHistory at 100 most-recent entries
    //   (FIFO). Pre-fix this array grew unbounded — long sessions leaked
    //   memory at the rate of one entry per registration. Mirrors
    //   systemBus.history's 100-entry cap. The Map-based _activeEffects /
    //   _effectBreakdown subsystems are dead code (ESR-006); only this array
    //   is consumer-readable via getEffectBreakdown().
    this._effectHistory.push(effect);
    if (this._effectHistory.length > 100) {
      this._effectHistory.splice(0, this._effectHistory.length - 100);
    }

    // Track by source
    if (!this._activeEffects.has(source)) {
      this._activeEffects.set(source, []);
    }
    this._activeEffects.get(source).push(effect);

    // Track by target stat
    if (!this._effectBreakdown.has(target)) {
      this._effectBreakdown.set(target, []);
    }
    this._effectBreakdown.get(target).push(effect);

    // Clear cache when effects change
    this.clearCache();

    return effectId;
  }

  /**
   * Remove all effects from a specific source.
   * @deprecated Since 2026-05-10 (ESR-006). Use effectRegistry.remove
   *   instead. Will be removed in v1.0.1.
   * @param {string} source - Source identifier to remove
   */
  removeEffectsFromSource(source) {
    if (!this._activeEffects.has(source)) {
      console.warn(`[EffectCalculator] No effects found for source: ${source}`);
      return;
    }

    const effects = this._activeEffects.get(source);

    // Remove from breakdown
    effects.forEach(effect => {
      const targetEffects = this._effectBreakdown.get(effect.target);
      if (targetEffects) {
        const index = targetEffects.indexOf(effect);
        if (index > -1) {
          targetEffects.splice(index, 1);
        }
        if (targetEffects.length === 0) {
          this._effectBreakdown.delete(effect.target);
        }
      }
    });

    // Remove from active effects
    this._activeEffects.delete(source);

    // Clear cache
    this.clearCache();
  }

  /**
   * Get breakdown of all effects affecting a specific stat.
   * @deprecated Since 2026-05-10 (ESR-006). Use
   *   effectRegistry.getEffectsForStat instead. Will be removed in v1.0.1.
   * @param {string} stat - Stat name to get breakdown for
   * @returns {Array} Array of effects with their sources
   */
  getEffectBreakdown(stat) {
    const effects = this._effectBreakdown.get(stat) || [];
    const breakdown = {};

    // Group by source
    effects.forEach(effect => {
      if (!breakdown[effect.source]) {
        breakdown[effect.source] = {
          source: effect.source,
          type: effect.type,
          effects: [],
          total: 0
        };
      }
      breakdown[effect.source].effects.push({
        target: effect.target,
        value: effect.value,
        operation: effect.operation
      });

      // Calculate total (simplified - doesn't handle all operations)
      if (effect.operation === 'add') {
        breakdown[effect.source].total += effect.value;
      } else if (effect.operation === 'set' && effect.value > breakdown[effect.source].total) {
        breakdown[effect.source].total = effect.value;
      }
    });

    return Object.values(breakdown);
  }

  /**
   * Remove all tracked effects (for respec)
   */
  removeAllEffects() {
    this._activeEffects.clear();
    this._effectBreakdown.clear();
    this._effectHistory = [];
    this.clearCache();
  }

  /**
   * Get all active effects (for debugging)
   */
  getActiveEffects() {
    const result = {};
    for (const [source, effects] of this._activeEffects) {
      result[source] = effects;
    }
    return result;
  }

  /**
   * Get damage resistances for a character based on race, class, and subclass
   * [PURPOSE]: Centralize resistance calculation for UI display and combat
   * [INTEGRATION]: Used by CharacterBasicsPanel to show resistances
   * [EMC-2 2026-07-04]: Rage resistances (base + Totem Bear) apply only WHILE RAGING
   *   (PHB p.48 "while raging"). Pass activeQuickBonuses so the rage shadow entry
   *   (buildRageShadowEntry, stamped by ClassResourcesPanel on a Rage consume) can be
   *   detected — same findQuickBonusOfKind signal as _buildContext's raging flag.
   *   Callers that omit the param get the RAW-correct non-raging result.
   */
  getDamageResistances(playerStats, activeQuickBonuses = []) {
    const resistances = new Set();

    if (!playerStats || !playerStats.classes || !Array.isArray(playerStats.classes)) {
      return Array.from(resistances);
    }

    // [EMC-2]: single raging signal, mirrors _buildContext (Rule 11 — no raw b.type check)
    const isRaging = !!findQuickBonusOfKind(activeQuickBonuses, 'rage');

    // [FIX APR 2026 — AUDIT M-1]: Data-driven rage resistances (custom class support)
    playerStats.classes.forEach(classEntry => {
      const className = classEntry.className;
      const classLevel = classEntry.level || 1;
      const subclass = classEntry.subclass;
      const subclassData = classEntry.subclassData;
      const classData = getClassData(className);

      // Check if class has rage resistances (Barbarian or custom rage class) AND the
      // character is actually raging (EMC-2 gate — covers base rage + Totem Bear below)
      if (classData?.rageResistances && isRaging) {
        // Totem Warrior Bear: resist all damage except psychic (level 3+)
        if (subclass === 'Totem Warrior' && subclassData?.totemSpirit === 'Bear' && classLevel >= 3) {
          ['bludgeoning', 'piercing', 'slashing', 'acid', 'cold', 'fire', 'lightning', 'thunder', 'necrotic', 'radiant', 'poison', 'force'].forEach(r => resistances.add(r));
        }
        // Base rage: resistances from class data
        classData.rageResistances.forEach(r => resistances.add(r));
      }
    });

    // [FIX APR 2026 — F-7/F-33]: Data-driven racial resistances
    // [WHY]: Custom races with damageResistance property now work (not just hardcoded tiefling)
    const raceName = playerStats.basics?.race;
    if (raceName) {
      const raceData = getRaceData(raceName);
      if (raceData?.damageResistance) {
        (Array.isArray(raceData.damageResistance) ? raceData.damageResistance : [raceData.damageResistance])
          .forEach(r => resistances.add(r));
      }
    }

    // [Phase 4a fix]: Query effectRegistry for all registered resistance/damageReduction effects.
    // [WHY]: The taken-feat stub (playerStats.feats[i]) only has {id, name, customChoices, activeUses}
    //        — no .effects or .passiveEffects fields. Reading from the stub always produced nothing.
    //        FeatsPanel.js already registers every feat's effects array into effectRegistry on mount,
    //        so querying effectRegistry is the correct and complete source of truth.
    //        This also picks up custom-feat resistances and invocation-granted resistances for free.
    // [Phase 4b bucket 3 followup — APR 2026]: Both operation:'grant' and operation:'immunity' surface
    //        in this list (any defense → UI shows the pill). Use getDamageResistanceDetails() or
    //        getDamageImmunities() for combat-resolution code that needs the precision split.
    // [EMC-2 2026-07-04]: thread the raging flag so {when:'raging'}-conditioned registry
    // resistances (1H-H-cond subclass effects) surface while raging and hide otherwise —
    // getEffectsForStat evaluates `context[condition.when]`, and the prior {} context
    // suppressed them unconditionally.
    this.effectRegistry.getEffectsForStat('resistance', playerStats, { raging: isRaging }).forEach(effect => {
      if ((effect.operation === 'grant' || effect.operation === 'immunity') && effect.value) {
        resistances.add(effect.value);
      }
    });
    // damageReduction effects are registered separately and map to 'physical_reduction' sentinel
    this.effectRegistry.getEffectsForStat('damageReduction', playerStats, { raging: isRaging }).forEach(effect => {
      if (effect.operation === 'grant' || effect.type === 'damageReduction') {
        resistances.add('physical_reduction');
      }
    });

    // [FIX APR 2026 — AUDIT M-10]: Removed clearCache() from read-only method

    return Array.from(resistances);
  }

  /**
   * Returns structured resistance details for combat-resolution precision.
   *
   * [Phase 4b bucket 3 followup — APR 2026]: Some subclass features grant resistance
   * only from a specific damage source — e.g., Cleric War Avatar of Battle (L17)
   * resists bludgeoning/piercing/slashing from NONMAGICAL attacks only (PHB p.62).
   * The existing getDamageResistances API flattens to a string[] and loses this
   * distinction. This method preserves the damageSource field + operation so combat-
   * resolution code can apply the right defense based on attacker context.
   *
   * Schema returned per entry:
   *   { damageType: string, damageSource?: 'nonmagical' | undefined, operation: 'grant' | 'immunity' }
   *
   * NOTE on field name: 'damageSource' (not 'source'). The effectRegistry already
   * uses 'source' on every stored effect for the registration ID (e.g.,
   * 'subclass:Cleric:War Domain:Avatar of Battle'). Using a different field name
   * avoids collision and prevents removeByPrefix cleanup from being confused by
   * an effect-schema source field overriding the registration source.
   *
   * Source values currently supported: 'nonmagical' (covers Cleric War, Paladin
   * Redemption, Forge heavy-armor B/P/S, etc.). Future: 'magical', 'silvered',
   * 'adamantine' if SRD content ever requires those qualifiers.
   *
   * @param {Object} playerStats
   * @returns {Array<{damageType: string, damageSource?: string, operation: string}>}
   */
  getDamageResistanceDetails(playerStats) {
    if (!playerStats) return [];
    const details = [];
    this.effectRegistry.getEffectsForStat('resistance', playerStats, {}).forEach(effect => {
      if (!effect.value) return;
      if (effect.operation !== 'grant' && effect.operation !== 'immunity') return;
      const entry = { damageType: effect.value, operation: effect.operation };
      if (effect.damageSource) entry.damageSource = effect.damageSource;
      details.push(entry);
    });
    return details;
  }

  /**
   * Returns the array of damage types the character is IMMUNE to (operation:'immunity').
   *
   * [Phase 4b bucket 3 followup — APR 2026]: Storm Sorcery Wind Soul L18 grants
   * immunity to lightning + thunder (XGE p.51); Forge Saint of Forge and Fire L17
   * grants immunity to fire (XGE p.19). Resistance and immunity are mechanically
   * different (half damage vs zero damage). Combat-resolution code calls this
   * method to apply the precision; UI display lists are still surfaced via
   * getDamageResistances (any defense → show pill).
   *
   * @param {Object} playerStats
   * @returns {Array<string>} damage type names (e.g., ['lightning', 'thunder'])
   */
  getDamageImmunities(playerStats) {
    if (!playerStats) return [];
    const immunities = new Set();
    this.effectRegistry.getEffectsForStat('resistance', playerStats, {}).forEach(effect => {
      if (effect.operation === 'immunity' && effect.value) {
        immunities.add(effect.value);
      }
    });
    return Array.from(immunities);
  }

  /**
   * Returns the array of damage types the character/condition is VULNERABLE to.
   *
   * [PB-1 — 2026-06-13]: Reader for vulnerability effects authored via
   * CustomConditionCreator → EffectComposer(kind:'vulnerability') →
   * vulnerabilityRowToEffect() → effectRegistry stat key 'vulnerability'.
   *
   * Symmetrical to getDamageImmunities() (which reads operation:'immunity' from
   * the 'resistance' stat bucket). Vulnerability uses its own stat bucket
   * ('vulnerability') so effectRegistry stores and retrieves it independently.
   *
   * NOTE: MonsterEditor stores damageVulnerabilities[] as a flat string array
   * on the monster object — that field is currently inert (not registered via
   * effectRegistry, not consumed here). This reader only covers the EffectComposer
   * pathway (custom conditions/feats/items authored via the CW). The monster
   * field is a separate breadcrumb. See 2F-breadcrumb in docs.
   *
   * @param {Object} playerStats
   * @returns {Array<string>} damage type names (e.g., ['fire', 'radiant'])
   */
  getDamageVulnerabilities(playerStats) {
    if (!playerStats) return [];
    const vulnerabilities = new Set();
    this.effectRegistry.getEffectsForStat('vulnerability', playerStats, {}).forEach(effect => {
      if (effect.operation === 'grant' && effect.value) {
        vulnerabilities.add(effect.value);
      }
    });
    return Array.from(vulnerabilities);
  }

  /**
   * Returns true if any registered effect grants advantage on the given check type.
   * [Phase 4a]: Enables custom feats to grant advantage on skills/saves/initiative.
   * @param {Object} playerStats
   * @param {string} checkType — 'stealth', 'initiative', 'strSave', 'int_wis_cha_magic_saves', etc.
   */
  hasAdvantageOnCheck(playerStats, checkType) {
    const effects = this.effectRegistry.getEffectsForStat('advantage', playerStats, {});
    return effects.some(e =>
      e.operation === 'advantage' &&
      (e.target === checkType || e.target === 'all')
    );
  }

  /**
   * Returns true if any registered effect forces autoFail on the given save ability.
   * [Sub-plan 1Q-F-7b 2026-05-28]: Condition rows (Paralyzed/Petrified/Stunned/Unconscious
   *   STR+DEX saves) emit { type:'save', operation:'autoFail' } and consume here.
   *   effectRegistry._getStatKey indexes these at 'save:<ability>' per L1346.
   *
   * [BREADCRUMB Rule 25 — Sub-plan 1Q-F-7b-i]: This reader is ready but not yet wired into
   *   the save-roll resolution path. The legacy hasDisadvantageOnSavingThrow (L4415) still has
   *   hardcoded condition-name checks that cover the same cases. Future Combat Tracker / save-roll
   *   UI work should call hasAutoFailSave() (registry-driven) and retire the hardcoded path.
   *   Tracked as Sub-plan 1Q-F-7b-i in docs/audits/master-queue.md.
   *
   * @param {string} ability - 'strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'
   * @param {Object} character
   * @returns {boolean}
   */
  hasAutoFailSave(ability, character) {
    const effects = this.effectRegistry.getEffectsForStat(`save:${ability}`, character, {});
    return effects.some(e => e.operation === 'autoFail');
  }

  /**
   * Returns true if any registered effect grants disadvantage on the given save ability.
   * [Sub-plan 1Q-F-7b 2026-05-28]: Restrained DEX save emits
   *   { type:'save', operation:'disadvantage' } and consumes here.
   *
   * [BREADCRUMB Rule 25 — Sub-plan 1Q-F-7b-i]: Same integration note as hasAutoFailSave above.
   *   hasDisadvantageOnSavingThrow (L4415) already handles Restrained DEX via hardcoded check.
   *   Future save-roll work should migrate to this registry-driven reader.
   *
   * @param {string} ability
   * @param {Object} character
   * @returns {boolean}
   */
  hasDisadvantageOnSave(ability, character) {
    const effects = this.effectRegistry.getEffectsForStat(`save:${ability}`, character, {});
    return effects.some(e => e.operation === 'disadvantage');
  }

  /**
   * Returns true if any registered effect causes auto-fail on the given ability check.
   * [Sub-plan 1Q-F-7b-ii 2026-05-28]: Mirrors hasAutoFailSave but for ability checks.
   * Queries both ability-specific 'check:<ability>' and 'check:all' scopes (the latter
   * covers Unconscious blanket auto-fail + Paralyzed/Stunned/Petrified STR+DEX auto-fail).
   *
   * @param {string} ability - 'strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma', or 'any'
   * @param {Object} character
   * @returns {boolean}
   */
  hasAutoFailCheck(ability, character) {
    const specific = this.effectRegistry.getEffectsForStat(`check:${ability}`, character, {});
    const allScope = this.effectRegistry.getEffectsForStat('check:all', character, {});
    return [...specific, ...allScope].some(e => e.operation === 'autoFail');
  }

  /**
   * Returns true if any registered effect grants disadvantage on the given ability check.
   * [Sub-plan 1Q-F-7b-ii 2026-05-28]: Mirrors hasDisadvantageOnSave for ability checks.
   * 'all'-scope handles Poisoned + Frightened (PHB p.290+).
   *
   * @param {string} ability
   * @param {Object} character
   * @returns {boolean}
   */
  hasDisadvantageOnCheck(ability, character) {
    const specific = this.effectRegistry.getEffectsForStat(`check:${ability}`, character, {});
    const allScope = this.effectRegistry.getEffectsForStat('check:all', character, {});
    return [...specific, ...allScope].some(e => e.operation === 'disadvantage');
  }

  /**
   * Returns the list of condition names to which this character is immune,
   * sourced from any registered effect with type 'conditionImmunity' and
   * operation 'grant'.
   *
   * [Sub-plan 1H-C 2026-05-28]: Closes the BREADCRUMB at
   *   itemEffectsRegistration.js L152-163 + sub-plan-1Ig-immunity-breadcrumb-audit.test.js.
   *   buildEffectsFromForm emits {type:'conditionImmunity', target, operation:'grant'}
   *   rows (Sub-plan 1K). Nothing read them — Ring of Free Action / Periapt immunities
   *   were authored but silently inert at runtime. This reader + isImmuneToCondition
   *   make the data path live.
   *
   * [BREADCRUMB Rule 25 — Sub-plan 1H-C consumer-application]: The application site
   *   (checking immunity before applying a condition to the character) lives in App.jsx
   *   (applyCondition / toggleCondition flow). App.jsx is owned by a sibling agent in
   *   this parallel batch — the wiring is DEFERRED. Call site: search App.jsx for
   *   `applyCondition` / `setCondition` / `toggleCondition`. Once wired, call
   *   `effectCalculator.isImmuneToCondition(playerStats, conditionName)` before
   *   adding the condition to `playerStats.conditions`. Tracked as Sub-plan 1H-C-i
   *   in docs/audits/master-queue.md.
   *
   * @param {Object} character
   * @returns {string[]} Condition names (lower-cased, deduped) that the character is immune to.
   */
  getConditionImmunities(character) {
    const effects = this.effectRegistry.getEffectsForStat('conditionImmunity', character, {});
    const seen = new Set();
    for (const e of effects) {
      if (e.operation === 'grant' && e.target) {
        seen.add(String(e.target).toLowerCase());
      }
    }
    return [...seen];
  }

  /**
   * Returns true if the character is immune to the given condition name.
   * Case-insensitive comparison against all registered conditionImmunity effects.
   *
   * [Sub-plan 1H-C 2026-05-28]: Companion to getConditionImmunities above.
   *   Application wiring (before applyCondition in App.jsx) is deferred to Sub-plan 1H-C-i.
   *
   * @param {Object} character
   * @param {string} conditionName - e.g. 'paralyzed', 'Poisoned', 'charmed'
   * @returns {boolean}
   */
  isImmuneToCondition(character, conditionName) {
    if (!conditionName) return false;
    const needle = String(conditionName).toLowerCase();
    return this.getConditionImmunities(character).includes(needle);
  }

  /**
   * Check whether disadvantage on a check is removed by registered effects.
   * Distinct from hasAdvantageOnCheck — removing disadvantage does NOT grant advantage.
   * Used by Skulker (no Perception disadvantage in dim light), Medium Armor Master
   * (no Stealth disadvantage in medium armor), etc.
   *
   * @param {Object} playerStats
   * @param {string} checkType - 'skill:perception', 'skill:stealth', 'all', etc.
   * @returns {boolean}
   */
  hasDisadvantageRemoved(playerStats, checkType) {
    const effects = this.effectRegistry.getEffectsForStat('advantage', playerStats, {});
    return effects.some(e => {
      if (e.operation !== 'remove_disadvantage') return false;
      const target = String(e.target || 'all').toLowerCase();
      const queryKey = String(checkType).toLowerCase();
      return target === queryKey || target === 'all' || target === 'skill:all';
    });
  }

  /**
   * Returns true if any registered effect grants advantage on attacks against
   * this character (e.g. Paralyzed, Petrified, Restrained, Stunned, Unconscious).
   *
   * [Sub-plan 1Q-F-7d 2026-05-28]: Closes the BREADCRUMB at L3073-3077.
   *   Thin wrapper around getEffectsForStat('attacksAgainst').
   *   UI consumers (StatsPanel, CombatTab) query this to render banners.
   *   System-agnostic: any RPG condition that registers { type:'attacksAgainst',
   *   operation:'advantage' } will be reflected here automatically.
   *
   * @param {Object} character
   * @returns {boolean}
   */
  hasAttacksAgainst(character) {
    const effects = this.effectRegistry.getEffectsForStat('attacksAgainst', character, {});
    return effects.some(e => e.operation === 'advantage');
  }

  /**
   * Returns true if any registered effect makes hits against this character
   * auto-crits (Paralyzed + Unconscious within 5 ft).
   *
   * [Sub-plan 1Q-F-7d 2026-05-28]: UI advisory — warns the player that a
   *   condition makes them susceptible to auto-crits within 5 ft.
   *
   *   IMPLEMENTATION NOTE: The attacksAgainstCrit row has a runtime condition
   *   guard: `check: (context) => (context?.distance || Infinity) <= 5`.
   *   getEffectsForStat applies this filter, so it returns false unless the
   *   caller provides a context with `distance <= 5`. For the UI banner we
   *   want to show "you are susceptible" whenever the condition is REGISTERED,
   *   regardless of current attacker distance. We use getAllEffects() (the
   *   unfiltered index) and look for the registered type directly. The banner
   *   text already says "within 5 ft" so the player understands it's
   *   conditional.
   *
   * @param {Object} character
   * @returns {boolean}
   */
  hasAutoCritWithin5ft(character) {
    // Use unfiltered registry scan — we want presence of the condition row,
    // not the resolved (distance-gated) result.
    const allEffects = this.effectRegistry.getAllEffects();
    return allEffects.some(e =>
      (e.type === 'attacksAgainstCrit' || e.type === 'attacksagainstcrit') &&
      e.operation === 'autoSuccess'
    );
  }

  /**
   * Check whether a narrative/rule feature flag is granted by registered effects.
   * Use for rule exceptions that aren't numeric stats: cover handling, hide rules,
   * sleep immunity, etc. UI components consult this to decide whether to apply
   * a rule modification.
   *
   * Examples of valid flag values:
   *   'ignore_half_cover', 'ignore_three_quarters_cover' (Sharpshooter)
   *   'hide_in_light_obscurement' (Skulker)
   *   'ranged_miss_doesnt_reveal' (Skulker)
   *   'dual_wield_non_light' (Dual Wielder)
   *   'no_sleep_required' (Aspect of the Moon — when wired)
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @param {string} flagName - the feature flag value to check
   * @returns {boolean}
   */
  hasFeatureFlag(playerStats, inventory, flagName) {
    if (!flagName) return false;
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('feature', playerStats, context);
    return effects.some(e =>
      e.operation === 'grant' &&
      String(e.value || '').toLowerCase() === String(flagName).toLowerCase()
    );
  }

  /**
   * Returns array of spell names granted as at-will (no slot consumption).
   * Sources: race grantedSpells with atWill flag, feat/invocation effects[]
   * with type 'atWillSpell'.
   *
   * [Phase 4b-CW Apr 2026]: Closes the "at-will spell" CW gap. Consumers:
   *   - SpellsPanel: render ∞ uses for matching spells (Phase C2)
   *   - spellCastingService: skip slot consumption when isSpellAtWill is true (Phase C2)
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {string[]} Lowercased spell names
   */
  getAtWillSpells(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('atWillSpell', playerStats, context);
    const names = new Set();
    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const value = effect.value;
      // [Spec A — Wizard capstones]: pickedAtLevelUp grants resolve from
      //   playerStats.customClassPicks[className][featureId] (Wizard Spell
      //   Mastery L18, CW custom-class player-picked at-will spells).
      if (value && typeof value === 'object' && value.pickedAtLevelUp) {
        const className = value.className;
        const featureId = value.featureId;
        if (className && featureId) {
          const picks = playerStats?.customClassPicks?.[className]?.[featureId];
          if (Array.isArray(picks)) {
            for (const name of picks) {
              if (typeof name === 'string' && name.trim()) {
                names.add(name.trim().toLowerCase());
              }
            }
          }
        }
        continue;
      }
      // Existing string-value path (race grants, feat grants, invocations).
      const stringValue = String(value || '').trim();
      if (stringValue) names.add(stringValue.toLowerCase());
    }
    return Array.from(names);
  }

  /**
   * True if the given spell is granted at-will via any registered effect.
   * Match is case-insensitive on the spell name.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @param {string} spellName
   * @returns {boolean}
   */
  isSpellAtWill(playerStats, inventory, spellName) {
    if (!spellName) return false;
    return this.getAtWillSpells(playerStats, inventory)
      .includes(String(spellName).toLowerCase());
  }

  /**
   * Returns array of spell names granted as "learned" (added to known spells, slots still consume).
   * Distinct from at-will: learnedSpell adds to the spell list but casting is normal (slot consumed,
   * components needed). Used for invocations that grant a specific spell (e.g., Thief of Five Fates
   * adds Bane), races that grant innate spells with limited daily uses, etc.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {string[]} Lowercased spell names
   */
  getLearnedSpells(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('learnedSpell', playerStats, context);
    const names = new Set();
    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const value = effect.value;
      if (Array.isArray(value)) {
        for (const v of value) {
          const name = String(v || '').trim();
          if (name) names.add(name.toLowerCase());
        }
      } else {
        const name = String(value || '').trim();
        if (name) names.add(name.toLowerCase());
      }
    }
    return Array.from(names);
  }

  /**
   * True if the given spell is granted as a learned spell via any registered effect.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @param {string} spellName
   * @returns {boolean}
   */
  isSpellLearnedFromEffect(playerStats, inventory, spellName) {
    if (!spellName) return false;
    return this.getLearnedSpells(playerStats, inventory)
      .includes(String(spellName).toLowerCase());
  }

  /**
   * [INNATE-SPELL-1, 2026-06-14]: Returns the character's racial innate spells,
   * surfaced from custom-race grantedSpells (registered as `racialInnateSpell`
   * effects by useRaceFeatureEffects.getActiveCustomRaceFeatures). Filters by the
   * authored unlock level vs the character's level and computes each spell's save
   * DC from its authored casting ability (8 + proficiency + ability modifier).
   *
   * Distinct from at-will / learned / ritual grants: these are race-granted innate
   * spells with a fixed casting ability + frequency (Tiefling-style). Display:
   * SpellbookTab "Racial Innate Spells" callout. Casting from the section + per-rest
   * use-tracking are breadcrumbed (INNATE-SPELL-CAST-FROM-SECTION / -PERREST-RESOURCE).
   * Trace: .claude/findings/2026-06-14-datamodel-racial-innate-spell-surfacing-ris1.md.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {Array<{name:string, level:number, castingAbility:string|null, frequency:string, dc:number|null, unlockLevel:number}>}
   */
  getRacialInnateSpells(playerStats, inventory) {
    if (!playerStats) return [];
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('racialInnateSpell', playerStats, context);
    const charLevel = playerStats.level || 1;
    // G7 castingAbility selectors emit 3-letter codes; getEffectiveModifier wants full names.
    const ABILITY_FULL = {
      str: 'strength', dex: 'dexterity', con: 'constitution',
      int: 'intelligence', wis: 'wisdom', cha: 'charisma',
    };

    const seen = new Set();
    const out = [];
    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const v = effect.value;
      if (!v || typeof v !== 'object') continue;
      const name = String(v.name || '').trim();
      if (!name) continue;

      const unlockLevel = (typeof v.unlockLevel === 'number' && v.unlockLevel > 0) ? v.unlockLevel : 1;
      if (unlockLevel > charLevel) continue; // not yet unlocked at this character level

      const key = name.toLowerCase();
      if (seen.has(key)) continue; // dedupe by name (base + subrace may both grant)
      seen.add(key);

      // Save DC from the authored casting ability (null when no ability is set).
      let dc = null;
      const castingAbility = v.castingAbility || null;
      if (castingAbility) {
        const fullAbility = ABILITY_FULL[castingAbility] || castingAbility;
        const profBonus = this.getProficiencyBonus(playerStats);
        const abilityMod = this.getEffectiveModifier(playerStats, inventory || [], [], fullAbility);
        dc = 8 + profBonus + abilityMod;
      }

      out.push({
        name,
        level: typeof v.level === 'number' ? v.level : 0,
        castingAbility,
        frequency: v.frequency || 'at-will',
        dc,
        unlockLevel,
      });
    }
    return out;
  }

  /**
   * [Phase 5 Track B]: Resolve perRestSpell.uses to a concrete integer.
   * Accepts a static number or an ability-modifier formula string.
   * Always returns at least 1.
   */
  _resolvePerRestUses(uses, playerStats) {
    if (typeof uses === 'number') return uses > 0 ? uses : 1;
    if (typeof uses !== 'string' || uses === '') return 1;
    const abilityMap = {
      wisMod:   'wisdom',
      strMod:   'strength',
      dexMod:   'dexterity',
      conMod:   'constitution',
      intMod:   'intelligence',
      chaMod:   'charisma',
    };
    if (abilityMap[uses]) {
      const score = (playerStats && playerStats[abilityMap[uses]]) || 10;
      return Math.max(1, Math.floor((score - 10) / 2));
    }
    if (uses === 'profBonus') {
      return Math.max(1, this.getProficiencyBonus(playerStats));
    }
    return 1; // unknown formula string → default
  }

  /**
   * [Phase 4b BC-13 follow-up — APR 2026]: Per-rest spell consumer.
   *
   * Returns the list of spells that registered effects have granted on a
   * per-rest basis. Each entry exposes:
   *   - spellName: canonical name (display-cased)
   *   - recharge:  'short_rest' | 'long_rest'
   *   - maxUses:   the number of free casts per rest cycle (default 1)
   *   - currentUses: remaining uses, read from playerStats._perRestSpellUses
   *                  Defaults to maxUses if no usage state has been recorded.
   *
   * Sources are effect entries with `type: 'perRestSpell'`. Producer schema:
   *   {
   *     type: 'perRestSpell',
   *     operation: 'grant',
   *     value: 'Spell Name',
   *     recharge: 'short_rest' | 'long_rest',
   *     uses?: 1
   *   }
   *
   * Consumers:
   *   - SpellsPanel: render "1/long rest" badge and decrement on cast.
   *   - spellCastingService: skip slot consumption when a free use is available.
   *   - RestPanel: reset _perRestSpellUses entries that match the rest type.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {Array<{spellName, recharge, maxUses, currentUses}>}
   */
  getPerRestSpells(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('perRestSpell', playerStats, context);
    const usageState = playerStats?._perRestSpellUses || {};
    const result = [];
    const seen = new Set(); // dedupe by spellName

    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const value = effect.value;

      // [Spec A — Wizard capstones]: pickedAtLevelUp grants resolve from
      //   playerStats.customClassPicks[className][featureId] (Wizard
      //   Signature Spells L20, CW custom-class player-picked per-rest).
      //   Metadata fields (usesPerDay, rechargeOn) live INSIDE value.
      if (value && typeof value === 'object' && value.pickedAtLevelUp) {
        const className = value.className;
        const featureId = value.featureId;
        if (!className || !featureId) continue;
        const picks = playerStats?.customClassPicks?.[className]?.[featureId];
        if (!Array.isArray(picks)) continue;

        const recharge = value.rechargeOn === 'long_rest' ? 'long_rest' : 'short_rest';
        const maxUses = this._resolvePerRestUses(value.usesPerDay ?? 1, playerStats);

        for (const name of picks) {
          if (typeof name !== 'string' || !name.trim()) continue;
          const spellName = name.trim();
          const key = spellName.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);

          const recordedUses = usageState[spellName];
          const currentUses = (typeof recordedUses === 'number')
            ? Math.max(0, Math.min(recordedUses, maxUses))
            : maxUses;

          result.push({ spellName, recharge, maxUses, currentUses });
        }
        continue;
      }

      // Existing string-value path (Mystic Arcanum / subclass per-rest grants).
      const spellName = String(value || '').trim();
      if (!spellName) continue;
      const key = spellName.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);

      const recharge = effect.recharge === 'short_rest' ? 'short_rest' : 'long_rest';
      const maxUses = this._resolvePerRestUses(effect.uses, playerStats);
      const recordedUses = usageState[spellName];
      // recordedUses === undefined means "untracked" — full uses available.
      // recordedUses === null is treated as untracked too (defensive).
      const currentUses = (typeof recordedUses === 'number')
        ? Math.max(0, Math.min(recordedUses, maxUses))
        : maxUses;

      result.push({ spellName, recharge, maxUses, currentUses });
    }

    return result;
  }

  /**
   * [Spec A — Wizard capstones]: Slot-recovery actions registered via
   *   effectRegistry. Wizard Arcane Recovery is the SRD use; CW custom-class
   *   slot-recovery features share the same shape.
   *
   * Sources are effect entries with `type: 'slotRecoveryAction'`. Producer
   * shape:
   *   {
   *     type: 'slotRecoveryAction',
   *     operation: 'grant',
   *     value: {
   *       actionName: string,         // e.g., 'Arcane Recovery'
   *       budgetFormula: string,      // 'ceil(classLevel/2)', 'classLevel',
   *                                   // 'floor(classLevel/N)', or numeric literal
   *       maxSlotTier: number,        // Highest slot level recoverable (5 for AR)
   *       spellSlotPool: string,      // 'spellSlots' (consumer reads this from playerStats)
   *       classKey: string,           // 'Wizard' — for classLevel resolution
   *       poolResourceId: string,     // 'arcane_recovery' — for usage gating
   *     }
   *   }
   *
   * Consumers:
   *   - RestPanel "Class Actions" section: render button per entry whose
   *     pool's current > 0. Click opens SlotRecoveryModal with the entry's
   *     props.
   *   - SlotRecoveryModal: applies slotDeltas to playerStats[spellSlotPool]
   *     and decrements playerStats.classes[classIndex][poolResourceId].current.
   *
   * Returns entries only when:
   *   1. The class declared by classKey exists in playerStats.classes
   *   2. The pool (poolResourceId) has current > 0
   *
   * Pool storage: classes[i][poolResourceId] (snake_case) OR
   * classes[i][camelCaseEquivalent] (legacy compat, mirrors ClassResourcesPanel
   * read at line ~132).
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {Array<{actionName, budgetFormula, maxSlotTier, spellSlotPool, classKey, poolResourceId, classLevel, resolvedBudget}>}
   */
  getSlotRecoveryActions(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('slotRecoveryAction', playerStats, context);
    const result = [];

    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const value = effect.value;
      if (!value || typeof value !== 'object') continue;
      const { actionName, budgetFormula, maxSlotTier, spellSlotPool, classKey, poolResourceId } = value;
      if (!classKey || !poolResourceId) continue;

      const classEntry = (playerStats?.classes || []).find(c => c && c.className === classKey);
      if (!classEntry) continue;
      const classLevel = classEntry.level || 1;

      // Pool storage compat: snake_case primary, camelCase fallback.
      const camelKey = poolResourceId.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
      const stored = classEntry[poolResourceId] || classEntry[camelKey];
      const poolCurrent = stored?.current ?? 0;
      if (poolCurrent <= 0) continue;

      const resolvedBudget = _resolveSlotRecoveryBudget(budgetFormula, classLevel);

      // [A49 AT-2 — 2026-06-03]: Pass through invokeAnytime so ClassResourcesPanel
      //   can render an at-will invoke button for actions with invokeAnytime===true.
      //   Default false keeps existing Wizard Arcane Recovery rest-only behavior.
      result.push({
        actionName,
        budgetFormula,
        maxSlotTier,
        spellSlotPool,
        classKey,
        poolResourceId,
        classLevel,
        resolvedBudget,
        poolNounPlural: value.poolNounPlural || '',
        tierNoun: value.tierNoun || '',
        invokeAnytime: value.invokeAnytime === true,
      });
    }

    return result;
  }

  /**
   * [Spec B Task 2 — 2026-05-16]: Spend-action menus attached to resource
   *   pools. UI consumer: ClassResourcesPanel renders a "Spend Options"
   *   pane per pool, iterating menus whose attachedToResource matches.
   *
   * Producer shape:
   *   { type: 'spendActionMenu', operation: 'grant', value: {
   *       menuId, attachedToResource, classKey,
   *       options: [{ name, cost, minLevel, description? }, ...],
   *   } }
   *
   * Returns: [{ menuId, attachedToResource, classKey, options: [...] }, ...]
   * Each emitted menu's options are filtered by character level meeting
   * each option's minLevel. Multiple grants targeting the same
   * attachedToResource are NOT coalesced into one menu — they're emitted
   * as separate entries; ClassResourcesPanel merges by attachedToResource
   * for rendering. (Keeps menuId stable per grant for traceability.)
   *
   * Class level for the minLevel filter: looked up from
   * playerStats.classes.find(c => c.className === value.classKey).
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {Array}
   */
  getSpendActionMenus(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('spendActionMenu', playerStats, context);
    const result = [];
    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const v = effect.value;
      if (!v || typeof v !== 'object') continue;
      const { menuId, attachedToResource, classKey, options } = v;
      if (!menuId || !attachedToResource || !Array.isArray(options)) continue;

      // Resolve class level for minLevel filter (default 0 if class missing)
      const classEntry = classKey
        ? (playerStats?.classes || []).find(c => c && c.className === classKey)
        : null;
      const classLevel = classEntry?.level || 0;

      const filteredOptions = options.filter(opt =>
        opt && typeof opt === 'object' && opt.name && (opt.minLevel || 1) <= classLevel
      );

      result.push({ menuId, attachedToResource, classKey, options: filteredOptions });
    }
    return result;
  }

  /**
   * [Spec B Task 3 — 2026-05-16]: Reaction triggers — invokable actions
   *   labeled as reactions. UI consumer: StatsPanel "Reactions" section.
   *
   * Producer shape:
   *   { type: 'reactionTrigger', operation: 'grant', value: {
   *       actionName, description,
   *       attachedToResource?, cost?,  // optional pool gate
   *       classKey?,                   // for pool lookup
   *   } }
   *
   * Returns: [{ actionName, description, attachedToResource, cost,
   *            classKey, currentUses, maxUses }, ...]
   * - currentUses / maxUses populated from classes[classKey][attachedToResource]
   *   when attachedToResource is set
   * - currentUses: null when no attachedToResource (always invokable)
   *
   * Reserved-path safety (Rule 18): this getter reads from
   * effectRegistry; it does NOT touch the reserved playerStats.reactionTriggers
   * (plural) path. The singular reactionTrigger effect type lives only in
   * registrations + classActiveSelections (post-invoke state).
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {Array}
   */
  getReactionTriggers(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('reactionTrigger', playerStats, context);
    const result = [];
    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const v = effect.value;
      if (!v || typeof v !== 'object' || !v.actionName) continue;

      const { actionName, description, attachedToResource, cost, classKey, raceKey, rollSpec, minLevel } = v;
      let currentUses = null;
      let maxUses = null;
      if (attachedToResource && classKey) {
        const classEntry = (playerStats?.classes || []).find(c => c && c.className === classKey);
        if (classEntry) {
          const camelKey = attachedToResource.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
          const pool = classEntry[attachedToResource] || classEntry[camelKey];
          currentUses = pool?.current ?? 0;
          maxUses = pool?.max ?? null;
        } else {
          currentUses = 0;
        }
      } else if (attachedToResource && raceKey) {
        // [Sub-plan 1H-G-bw-S3a — 2026-06-03]: race-resource pool reaction (Hellish
        // Rebuke). Race traits carry no minLevel (useRaceFeatureEffects.js:46), so
        // the pool def's minLevel is mirrored on the effect value (v.minLevel) and
        // gates visibility here — the single read-time level check (skip below it).
        // currentUses lazy-defaults to max, mirroring ClassResourcesPanel.js:263
        // (playerStats.raceResources[id] is written only on first use).
        const charLevel = playerStats?.level || 1;
        if (typeof minLevel === 'number' && charLevel < minLevel) continue;
        const pool = playerStats?.raceResources?.[attachedToResource];
        const resolvedMax = (typeof v.maxUses === 'number' ? v.maxUses : (pool?.max ?? 1));
        currentUses = pool?.current ?? resolvedMax;
        maxUses = resolvedMax;
      }
      result.push({
        actionName,
        description: description || '',
        attachedToResource: attachedToResource || null,
        cost: cost ?? null,
        classKey: classKey || null,
        raceKey: raceKey || null,
        rollSpec: rollSpec || null,
        currentUses,
        maxUses,
      });
    }
    return result;
  }

  /**
   * [Spec B Task 4 — 2026-05-16]: Long-rest pickers — features that
   *   prompt the player to choose N items from a source list after a
   *   long rest. UI consumer: RestPanel mounts DailySetupModal which
   *   iterates registered pickers in sequence.
   *
   * Producer shape (Infuse Item / Spell-Storing Item):
   *   { type: 'longRestPicker', operation: 'grant', value: {
   *       pickerId, knownTable?, activeTable, pickSource,
   *       pickConstraints?, bindTarget?, pickKnownAtLevelUp?,
   *       chargesFormula?, classKey,
   *   } }
   *
   * Returns: [{ ...originalValue, classLevel, resolvedKnownCount,
   *            resolvedActiveCount }, ...]
   *
   * Uses lookupSparseTable for knownTable / activeTable resolution
   * (find-highest-declared-level-≤-target, per Rule 21 / Q56 precedent).
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {Array}
   */
  getLongRestPickers(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('longRestPicker', playerStats, context);
    const result = [];
    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const v = effect.value;
      if (!v || typeof v !== 'object' || !v.pickerId) continue;

      const classEntry = v.classKey
        ? (playerStats?.classes || []).find(c => c && c.className === v.classKey)
        : null;
      const classLevel = classEntry?.level || 0;

      const resolvedKnownCount = _resolveLevelTableLookup(v.knownTable, classLevel);
      const resolvedActiveCount = _resolveLevelTableLookup(v.activeTable, classLevel);

      result.push({
        ...v,
        classLevel,
        resolvedKnownCount,
        resolvedActiveCount,
      });
    }
    return result;
  }

  /**
   * [Spec C Task 2 — 2026-05-16/17]: Pure discovery — return all registered
   * `triggeredEffect` grants whose value.triggerId matches the requested
   * triggerId. No condition filtering — used by tooling, Author UI preview,
   * and tests that want to see all registrations.
   *
   * Producer shape:
   *   { type: 'triggeredEffect', operation: 'grant', value: {
   *       triggerId, condition?, payload[], onceThenConsume?, sourceLabel?
   *   } }
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @param {string} triggerId   Required — one of TRIGGER_VOCABULARY values
   * @returns {Array<Object>}    Raw effect entries (full registry shape)
   */
  getTriggeredEffects(playerStats, inventory, triggerId) {
    if (!triggerId) return [];
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('triggeredEffect', playerStats, context);
    const result = [];
    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const v = effect.value;
      if (!v || typeof v !== 'object') continue;
      if (v.triggerId !== triggerId) continue;
      result.push(effect);
    }
    return result;
  }

  /**
   * [Spec C Task 2 — 2026-05-16/17]: Discovery + condition filter + payload
   * resolution. Called by the 5 firer sites (rollWeaponAttack on_hit/on_crit,
   * save-resolution on_save_rolled, HP-decrement on_take_damage, RestPanel
   * on_rest_completed). Returns deltas the caller folds into local state —
   * the service is STATELESS (no setPlayerStats writes).
   *
   * Rule 11 compliance: triggerId is enum; condition keys are enum;
   * payload type/operation reuse the existing effect-row vocabulary.
   *
   * Rule 18 compliance: no write to reserved fields. `triggeredEffect`
   * lives in effectRegistry (registration-time); context is firer-local.
   *
   * Return shape per firing result:
   *   {
   *     sourceLabel,        // Activity-log friendly source string
   *     triggerId,
   *     payload[],          // Effect-row entries the caller folds in
   *     shouldConsume,      // True if onceThenConsume — caller calls
   *                         //   consumeTriggeredSource(sourceRef)
   *     sourceRef,          // Optional { type: 'item'|'infusion', id } for consume
   *     condition,          // Echoed for activity log / debugging
   *   }
   *
   * @param {string} triggerId   One of TRIGGER_VOCABULARY values
   * @param {Object} playerStats
   * @param {Array}  inventory
   * @param {Object} eventContext  Firer-specific context (ammo, target, ability, damageType, restType, ...)
   * @returns {Array<Object>}      Firing results — empty array if nothing matched
   */
  fireTriggeredEffects(triggerId, playerStats, inventory, eventContext = {}) {
    if (!triggerId) return [];
    const buildContext = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('triggeredEffect', playerStats, buildContext);
    const result = [];
    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const v = effect.value;
      if (!v || typeof v !== 'object') continue;
      if (v.triggerId !== triggerId) continue;

      const condition = v.condition || {};
      if (!_triggeredEffectMatches(condition, eventContext, buildContext)) continue;

      result.push({
        sourceLabel: v.sourceLabel || 'Triggered Effect',
        triggerId,
        // Shallow-copy each payload row so callers can mutate without affecting
        // the registry's stored data. Dice strings / formula values are passed
        // through verbatim — callers (rollWeaponAttack damage line, save-roll
        // advantage flip, etc.) resolve them in their own pipeline.
        payload: Array.isArray(v.payload) ? v.payload.map((p) => ({ ...p })) : [],
        shouldConsume: !!v.onceThenConsume,
        sourceRef: v.sourceRef || null,
        condition,
      });
    }
    return result;
  }

  /**
   * Convenience predicate: is the named spell currently available as a free
   * (no-slot) per-rest cast for this character?
   */
  hasPerRestSpellUseAvailable(playerStats, inventory, spellName) {
    if (!spellName) return false;
    const target = String(spellName).toLowerCase();
    return this.getPerRestSpells(playerStats, inventory)
      .some(s => s.spellName.toLowerCase() === target && s.currentUses > 0);
  }

  /**
   * [Phase 4b BC-13 closeout — APR 2026]: Granted ritual spell consumer.
   *
   * Returns spells the player can cast AS RITUALS even if those spells are
   * not on their class spell list. Sources: registered effects with
   *   { type: 'ritualSpell', operation: 'grant', value: '<Spell Name>' }
   *
   * Producers: Barbarian Path of the Totem Warrior Spirit Seeker (Beast
   * Sense, Speak with Animals), Spirit Walker (Commune with Nature).
   *
   * Consumers:
   *   - App.canRitualCast: extends the "may I ritual-cast?" check beyond the
   *     existing knownSpells/preparedSpells gate so granted ritual spells
   *     unlock even though the player doesn't otherwise know them.
   *   - SpellbookTab "Granted Rituals" callout (UI consumer landing in a
   *     follow-up commit).
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {string[]} Display-cased spell names (deduplicated)
   */
  getGrantedRitualSpells(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('ritualSpell', playerStats, context);
    const seen = new Set();
    const result = [];
    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const value = String(effect.value || '').trim();
      if (!value) continue;
      const key = value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(value);
    }
    return result;
  }

  /**
   * Convenience predicate: is the named spell granted to this character as a
   * ritual-only cast (case-insensitive)?
   */
  isSpellGrantedAsRitual(playerStats, inventory, spellName) {
    if (!spellName) return false;
    const target = String(spellName).toLowerCase();
    return this.getGrantedRitualSpells(playerStats, inventory)
      .some(name => name.toLowerCase() === target);
  }

  /**
   * [Phase 4b BC-13 closeout — APR 2026]: Resource-cost spell consumer.
   *
   * Returns the list of spells that registered effects have granted on a
   * "spend N <resource> to cast (no slot)" basis. Each entry exposes:
   *   - spellName:    canonical name (display-cased)
   *   - cost:         number of resource points required
   *   - resource:     classResources resource id (e.g., 'ki_points')
   *   - currentResource: current value from playerStats.classes[i][resource]
   *   - maxResource:  max value
   *   - available:    currentResource >= cost
   *
   * Producer schema:
   *   { type: 'resourceCostSpell', operation: 'grant',
   *     value: '<Spell Name>', cost: 2, resource: 'ki_points' }
   *
   * Producers (SRD):
   *   - Way of Shadow "Shadow Arts" L3: Darkness/Darkvision/Pass without
   *     Trace/Silence at 2 ki each
   *   - Sorcerer Shadow Magic "Eyes of the Dark" L1: Darkness at 2 sorcery
   *     points
   *
   * Consumers:
   *   - SpellbookTab "Resource-Cost Spells" callout (badge + cast button)
   *   - spellCastingService.castSpell skips slot consumption when
   *     useResourceCost is set; decrements via spendResource
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @returns {Array<{spellName, cost, resource, currentResource, maxResource, available}>}
   */
  getResourceCostSpells(playerStats, inventory) {
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('resourceCostSpell', playerStats, context);
    const result = [];
    const seen = new Set(); // dedupe by spellName + resource pair (not just spellName,
                             // since a player might have BOTH a 2-ki cast and a different
                             // resource path for the same spell, though that's edge-case)

    for (const effect of effects) {
      if (effect.operation !== 'grant') continue;
      const spellName = String(effect.value || '').trim();
      const resourceId = String(effect.resource || '').trim();
      const cost = Number(effect.cost);
      if (!spellName || !resourceId || !Number.isFinite(cost) || cost <= 0) continue;

      const key = `${spellName.toLowerCase()}|${resourceId.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);

      // Read resource state from playerStats.classes[i][resourceId]
      let currentResource = 0;
      let maxResource = 0;
      for (const c of (playerStats?.classes || [])) {
        if (c && c[resourceId] != null) {
          currentResource = Number(c[resourceId].current) || 0;
          maxResource = Number(c[resourceId].max) || 0;
          break;
        }
      }

      result.push({
        spellName,
        cost,
        resource: resourceId,
        currentResource,
        maxResource,
        available: currentResource >= cost,
      });
    }

    return result;
  }

  /**
   * Predicate: can the character afford to cast `spellName` via a registered
   * resource-cost grant right now? (case-insensitive on the spell name).
   */
  canCastResourceCostSpell(playerStats, inventory, spellName) {
    if (!spellName) return false;
    const target = String(spellName).toLowerCase();
    return this.getResourceCostSpells(playerStats, inventory)
      .some(s => s.spellName.toLowerCase() === target && s.available);
  }

  /**
   * [Phase 4b CW-parity (C) follow-up — APR 2026]: Active spell effects
   * sources currently registered into effectRegistry.
   *
   * Returns one entry per `spell:cast:*` registration:
   *   - sourceId:        the prefix used to register
   *   - spellName:       from metadata
   *   - castLevel:       from metadata
   *   - castStamp:       Date.now() at cast time
   *   - durationMinutes: parsed from spell.duration (null = open-ended)
   *   - concentration:   true if a concentration spell
   *   - expiresAtMs:     castStamp + durationMinutes*60_000 (null when null)
   *
   * Used by:
   *   - Active Spell Effects panel (UI viewer + manual dismiss)
   *   - debugging / "what's currently boosting my damage?" introspection
   *
   * Note: this is read straight from effectRegistry.metadata — concentration
   * spells appear here AND on concentrationSystem.activeConcentration. Both
   * end-paths still work (manual dismiss here calls effectRegistry.remove
   * directly; concentration end calls breakConcentration which uses the same
   * sourceIdPrefix).
   *
   * @returns {Array<{sourceId, spellName, castLevel, castStamp, durationMinutes, concentration, expiresAtMs}>}
   */
  getActiveSpellEffectSources() {
    const result = [];
    const SPELL_PREFIX = 'spell:cast:';
    for (const [sourceId, meta] of this.effectRegistry.metadata.entries()) {
      if (!sourceId.startsWith(SPELL_PREFIX)) continue;
      if (!meta) continue;
      const durationMinutes = typeof meta.durationMinutes === 'number' ? meta.durationMinutes : null;
      const castStamp = typeof meta.castStamp === 'number' ? meta.castStamp : null;
      const expiresAtMs = (castStamp != null && durationMinutes != null && durationMinutes > 0)
        ? castStamp + (durationMinutes * 60 * 1000)
        : null;
      result.push({
        sourceId,
        spellName: meta.spellName || '<unknown>',
        castLevel: meta.castLevel || null,
        castStamp,
        durationMinutes,
        concentration: !!meta.concentration,
        expiresAtMs,
      });
    }
    // Most-recently-cast first (descending castStamp)
    result.sort((a, b) => (b.castStamp || 0) - (a.castStamp || 0));
    return result;
  }

  /**
   * [Phase 4b CW-parity (C) follow-up — APR 2026]: Manually dismiss a spell
   * effect. Retracts the source from the registry AND clears the calculator
   * cache so consumers re-query fresh values. Concentration spells should
   * route through concentrationSystem.endConcentration instead so the
   * activeConcentration tracker also updates.
   *
   * @param {string} sourceId
   * @returns {boolean} true if a source was removed
   */
  dismissSpellEffect(sourceId) {
    if (!sourceId) return false;
    const removed = this.effectRegistry.remove(sourceId);
    this.clearCache();
    // [A7 — 2026-05-25]: Emit STATS_CHANGED so the 14 character-sheet panels
    //   subscribed to it (VitalStatsCard, EquipmentPanel, SkillsPanel,
    //   FeatsPanel, RestPanel, etc.) re-render and reflect the dismissed
    //   effect. Pre-fix the calculator's internal cache was cleared, but
    //   consumer components kept their useMemo'd values from before the
    //   dismissal until something else triggered a re-render. Emit is
    //   gated on a successful removal so no-op dismiss calls don't churn
    //   subscribers. Per CLAUDE.md Rule 4 ("systemBus events on ALL state
    //   changes"). Payload carries `sourceId` so granular subscribers
    //   (e.g., a single-spell badge) can filter without re-querying.
    if (removed) {
      try {
        systemBus.emit(EVENTS.STATS_CHANGED, {
          source: 'dismissSpellEffect',
          sourceId,
        });
      } catch (e) {
        // systemBus.emit must never throw into the calculator's caller; if
        // a subscriber misbehaves the dismiss should still report success.
        // Log to console for diagnostics in dev.
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('[effectCalculator] dismissSpellEffect: STATS_CHANGED emit failed', e);
        }
      }
    }
    return removed;
  }

  /**
   * Get a spell modifier value (e.g., extended range, knockback) applied to a target spell.
   * Sources: feat/invocation effects with type 'spellModifier' targeting the spell name.
   *
   * Used by:
   *   - Eldritch Spear (sets Eldritch Blast range to 300 ft)
   *   - Repelling Blast (adds 10 ft knockback to Eldritch Blast hits)
   *
   * The modifier is selected by (spellName, property) pair. Operations:
   *   - 'set': returns the value if any matching effect uses set; multiple sets pick the max
   *   - 'add': returns the sum of all matching add operations
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @param {string} spellName - the spell whose property is modified (case-insensitive)
   * @param {string} property - e.g., 'range', 'knockback'
   * @returns {{ operation: string, value: number } | null} the resolved modifier or null if none
   */
  getSpellModifier(playerStats, inventory, spellName, property) {
    if (!spellName || !property) return null;
    const context = this._buildContext(playerStats, inventory || [], []);
    const effects = this.effectRegistry.getEffectsForStat('spellModifier', playerStats, context);

    const targetSpell = String(spellName).toLowerCase();
    const targetProperty = String(property).toLowerCase();

    let setMax = null;
    let addSum = 0;
    let hasAdd = false;

    for (const effect of effects) {
      if (String(effect.target || '').toLowerCase() !== targetSpell) continue;
      if (String(effect.property || '').toLowerCase() !== targetProperty) continue;

      const value = parseInt(effect.value, 10);
      if (Number.isNaN(value)) continue;

      if (effect.operation === 'set') {
        if (setMax === null || value > setMax) setMax = value;
      } else if (effect.operation === 'add') {
        addSum += value;
        hasAdd = true;
      }
    }

    // 'set' takes precedence over 'add' (RAW: "the range is 300 feet", absolute override)
    if (setMax !== null) return { operation: 'set', value: setMax };
    if (hasAdd) return { operation: 'add', value: addSum };
    return null;
  }

  /**
   * Get the effective range of a spell after applying any spellModifier 'range' effects.
   * Returns the spell's base range when no modifier is registered.
   *
   * @param {Object} playerStats
   * @param {Array} inventory
   * @param {string} spellName
   * @param {number} baseRange - the spell's RAW range in feet
   * @returns {number} effective range in feet
   */
  getSpellRange(playerStats, inventory, spellName, baseRange) {
    const mod = this.getSpellModifier(playerStats, inventory, spellName, 'range');
    if (!mod) return baseRange;
    if (mod.operation === 'set') return mod.value;
    if (mod.operation === 'add') return baseRange + mod.value;
    return baseRange;
  }

  /**
   * Get subclass spells for domain clerics, oath paladins, expanded warlocks, etc.
   * [PURPOSE]: Centralize subclass spell calculation for spell lists
   * [INTEGRATION]: Used by CharacterBasicsPanel and spell selection
   */
  getSubclassSpells(playerStats) {
    const spells = [];

    if (!playerStats || !playerStats.classes || !Array.isArray(playerStats.classes)) {
      return spells;
    }

    // [v1.0]: Use contentResolver — no inline require needed (already imported at top)

    // Iterate through each class the character has
    playerStats.classes.forEach(classEntry => {
      const className = classEntry.className;
      const classLevel = classEntry.level || 1;
      const subclassName = classEntry.subclass;

      if (!className || !subclassName) return;

      // [v1.0]: Use contentResolver for unified SRD + custom class/subclass lookup
      const classData = getClassData(className);
      if (!classData) return;

      // Check embedded subclasses first, then custom sources
      const subclassData = classData.subclasses?.[subclassName];
      if (!subclassData) return;

      // [CLERIC]: Domain Spells (automatically prepared)
      if (subclassData.domainSpells) {
        Object.entries(subclassData.domainSpells).forEach(([level, domainSpellList]) => {
          const spellLevel = parseInt(level, 10);
          if (classLevel >= spellLevel) {
            domainSpellList.forEach(spellName => {
              spells.push({
                spell: spellName,
                source: `${className} - ${subclassName}`,
                level: spellLevel,
                type: 'domain',
                alwaysPrepared: true
              });
            });
          }
        });
      }

      // [PALADIN]: Oath Spells (automatically prepared)
      if (subclassData.oathSpells) {
        Object.entries(subclassData.oathSpells).forEach(([level, oathSpellList]) => {
          const spellLevel = parseInt(level, 10);
          if (classLevel >= spellLevel) {
            oathSpellList.forEach(spellName => {
              spells.push({
                spell: spellName,
                source: `${className} - ${subclassName}`,
                level: spellLevel,
                type: 'oath',
                alwaysPrepared: true
              });
            });
          }
        });
      }

      // [WARLOCK]: Expanded Spell List (added to list, not auto-known)
      if (subclassData.expandedSpellList) {
        Object.entries(subclassData.expandedSpellList).forEach(([level, expandedSpellList]) => {
          const spellLevel = parseInt(level, 10);
          if (classLevel >= spellLevel) {
            expandedSpellList.forEach(spellName => {
              spells.push({
                spell: spellName,
                source: `${className} - ${subclassName}`,
                level: spellLevel,
                type: 'expanded',
                alwaysPrepared: false
              });
            });
          }
        });
      }

      // [DRUID - CIRCLE OF THE LAND]: Circle Spells (automatically prepared)
      if (subclassData.circleSpells && classEntry.subclassData?.landType) {
        const landType = classEntry.subclassData.landType;
        const circleSpellsForLand = subclassData.circleSpells[landType];

        if (circleSpellsForLand) {
          Object.entries(circleSpellsForLand).forEach(([level, circleSpellList]) => {
            const spellLevel = parseInt(level, 10);
            if (classLevel >= spellLevel) {
              circleSpellList.forEach(spellName => {
                spells.push({
                  spell: spellName,
                  source: `${className} - ${subclassName} (${landType})`,
                  level: spellLevel,
                  type: 'circle',
                  alwaysPrepared: true
                });
              });
            }
          });
        }
      }

      // [MONK - WAY OF SHADOW]: Shadow Spells (can cast using ki points)
      if (subclassData.shadowSpells) {
        Object.entries(subclassData.shadowSpells).forEach(([level, shadowSpellList]) => {
          const spellLevel = parseInt(level, 10);
          if (classLevel >= spellLevel) {
            shadowSpellList.forEach(spellName => {
              spells.push({
                spell: spellName,
                source: `${className} - ${subclassName}`,
                level: spellLevel,
                type: 'shadow',
                alwaysPrepared: false,
                usesKi: true
              });
            });
          }
        });
      }
    });

    // [FIX NOV 28, 2025]: Check for optional class features from enabled sources (like Tasha's)
    // [WHY]: Tasha's provides Primal Awareness bonus spells for Rangers when enabled
    // [INTEGRATION]: Imports sourcesManager to check enabled sources
    try {
      const sourcesManager = require('../data/sources/initializeSources').sourcesManager;
      const enabledSources = sourcesManager?.getAllSources() || {};

      Object.entries(enabledSources).forEach(([sourceId, source]) => {
        if (!source.enabled || !source.content?.optionalClassFeatures) return;

        // Check each class the character has
        playerStats.classes.forEach(classEntry => {
          const className = classEntry.className;
          const classLevel = classEntry.level || 1;

          // Get optional features for this class from this source
          const classOptionalFeatures = source.content.optionalClassFeatures[className];
          if (!classOptionalFeatures) return;

          // Check for Primal Awareness (Ranger) or similar bonus spell features
          Object.entries(classOptionalFeatures).forEach(([featureKey, feature]) => {
            if (feature.bonusSpells) {
              Object.entries(feature.bonusSpells).forEach(([level, spellList]) => {
                const requiredLevel = parseInt(level, 10);
                if (classLevel >= requiredLevel) {
                  spellList.forEach(spellName => {
                    // Don't add duplicates
                    if (!spells.find(s => s.spell === spellName)) {
                      spells.push({
                        spell: spellName,
                        source: `${className} - ${feature.name} (${source.name})`,
                        level: requiredLevel,
                        type: 'optional',
                        alwaysPrepared: true
                      });
                    }
                  });
                }
              });
            }
          });
        });
      });
    } catch (e) {
      // Sources manager may not be available during initial load
      console.debug('[effectCalculator] Could not load optional class features:', e.message);
    }

    // [FIX APR 2026 — AUDIT M-10]: Removed clearCache() from read-only method

    return spells;
  }

  // ========================================
  // SPELL SLOT CALCULATIONS
  // ========================================

  /**
   * Calculate multiclass spell slots according to D&D 5e PHB p.164-165
   *
   * [PURPOSE]: Correctly calculate spell slots for multiclass characters
   * [WHY]: Single/multiclass characters must use combined caster level, not individual class slots
   * [INTEGRATION]: Replaces incorrect per-class tracking with unified calculation
   *
   * @param {Object} playerStats - Character stats with classes array
   * @returns {Object} Spell slots object with levels 1-9, each with max and used counts
   */
  calculateMulticlassSpellSlots(playerStats) {
    // Initialize empty spell slots
    const spellSlots = {
      1: { max: 0, used: 0 },
      2: { max: 0, used: 0 },
      3: { max: 0, used: 0 },
      4: { max: 0, used: 0 },
      5: { max: 0, used: 0 },
      6: { max: 0, used: 0 },
      7: { max: 0, used: 0 },
      8: { max: 0, used: 0 },
      9: { max: 0, used: 0 }
    };

    // Get all classes (handle both single class and multiclass)
    let allClasses = [];
    if (playerStats.classes && playerStats.classes.length > 0) {
      allClasses = playerStats.classes;
    }

    // If no class, return empty slots
    if (allClasses.length === 0) {
      return spellSlots;
    }

    // Calculate combined spellcaster level per PHB p.164
    let spellcasterLevel = 0;
    let hasWarlockLevels = false;
    let warlockLevel = 0;

    // [v1.0]: Use getCasterType() for unified SRD + custom class resolution
    allClasses.forEach(cls => {
      const className = cls.className || '';
      const classLevel = cls.level || 0;
      const subclass = (cls.subclass || '').toLowerCase();
      const casterType = getCasterType(className);
      // [FIX APR 2026 — F-9]: Data-driven subclass caster detection
      // [WHY]: getCasterType('Fighter') returns null, but Eldritch Knight is a third-caster subclass
      // [HOW]: getCasterType() accepts subclass names and searches class subclass data for spellcasting
      const subclassCasterType = cls.subclass ? getCasterType(cls.subclass) : null;

      // Full casters (add full level)
      if (casterType === 'full') {
        spellcasterLevel += classLevel;
      }
      // Half casters (add half level, rounded down — except Artificer rounds UP per TCoE p.9)
      else if (casterType === 'half') {
        // [FIX APR 2026 — AUDIT C-2]: Artificer uses Math.ceil, all other half-casters use Math.floor
        const classSpellcasting = getClassData(className)?.spellcasting;
        spellcasterLevel += classSpellcasting?.halfCasterRoundUp
          ? Math.ceil(classLevel / 2)
          : Math.floor(classLevel / 2);
      }
      // Pact Magic (Warlock) — separate from Spellcasting
      else if (casterType === 'pact') {
        hasWarlockLevels = true;
        warlockLevel = classLevel;
        // Warlocks don't contribute to multiclass spellcaster level
      }
      // Third casters (add third level, rounded down)
      // [NOTE]: These are subclass-level casters, not class-level
      else if (casterType === 'third') {
        spellcasterLevel += Math.floor(classLevel / 3);
      }
      // Subclass-level casting for non-caster base classes (Eldritch Knight, Arcane Trickster, custom)
      else if (subclassCasterType === 'third') {
        spellcasterLevel += Math.floor(classLevel / 3);
      }
      else if (subclassCasterType === 'half') {
        spellcasterLevel += Math.floor(classLevel / 2);
      }
      else if (subclassCasterType === 'full') {
        spellcasterLevel += classLevel;
      }
    });

    // Multiclass Spellcaster table (PHB p.165)
    // This is the same as the full caster progression
    const multiclassSlotTable = [
      [0,0,0,0,0,0,0,0,0], // Level 0 (not used)
      [2,0,0,0,0,0,0,0,0], // Level 1
      [3,0,0,0,0,0,0,0,0], // Level 2
      [4,2,0,0,0,0,0,0,0], // Level 3
      [4,3,0,0,0,0,0,0,0], // Level 4
      [4,3,2,0,0,0,0,0,0], // Level 5
      [4,3,3,0,0,0,0,0,0], // Level 6
      [4,3,3,1,0,0,0,0,0], // Level 7
      [4,3,3,2,0,0,0,0,0], // Level 8
      [4,3,3,3,1,0,0,0,0], // Level 9
      [4,3,3,3,2,0,0,0,0], // Level 10
      [4,3,3,3,2,1,0,0,0], // Level 11
      [4,3,3,3,2,1,0,0,0], // Level 12
      [4,3,3,3,2,1,1,0,0], // Level 13
      [4,3,3,3,2,1,1,0,0], // Level 14
      [4,3,3,3,2,1,1,1,0], // Level 15
      [4,3,3,3,2,1,1,1,0], // Level 16
      [4,3,3,3,2,1,1,1,1], // Level 17
      [4,3,3,3,3,1,1,1,1], // Level 18
      [4,3,3,3,3,2,1,1,1], // Level 19
      [4,3,3,3,3,2,2,1,1]  // Level 20
    ];

    // Apply multiclass spell slots (if any spellcaster levels)
    if (spellcasterLevel > 0) {
      const slots = multiclassSlotTable[Math.min(spellcasterLevel, 20)];
      for (let i = 0; i < 9; i++) {
        spellSlots[i + 1].max = slots[i];
      }
    }

    // Handle Warlock Pact Magic separately (PHB p.164)
    // Pact Magic slots are SEPARATE from Spellcasting slots
    if (hasWarlockLevels && warlockLevel > 0) {
      // Warlock Pact Magic progression (PHB p.106)
      const warlockSlotsByLevel = [
        { slots: 0, level: 0 }, // Level 0
        { slots: 1, level: 1 }, // Level 1: 1 slot, 1st level
        { slots: 2, level: 1 }, // Level 2: 2 slots, 1st level
        { slots: 2, level: 2 }, // Level 3: 2 slots, 2nd level
        { slots: 2, level: 2 }, // Level 4: 2 slots, 2nd level
        { slots: 2, level: 3 }, // Level 5: 2 slots, 3rd level
        { slots: 2, level: 3 }, // Level 6: 2 slots, 3rd level
        { slots: 2, level: 4 }, // Level 7: 2 slots, 4th level
        { slots: 2, level: 4 }, // Level 8: 2 slots, 4th level
        { slots: 2, level: 5 }, // Level 9: 2 slots, 5th level
        { slots: 2, level: 5 }, // Level 10: 2 slots, 5th level
        { slots: 3, level: 5 }, // Level 11: 3 slots, 5th level
        { slots: 3, level: 5 }, // Level 12: 3 slots, 5th level
        { slots: 3, level: 5 }, // Level 13: 3 slots, 5th level
        { slots: 3, level: 5 }, // Level 14: 3 slots, 5th level
        { slots: 3, level: 5 }, // Level 15: 3 slots, 5th level
        { slots: 3, level: 5 }, // Level 16: 3 slots, 5th level
        { slots: 4, level: 5 }, // Level 17: 4 slots, 5th level
        { slots: 4, level: 5 }, // Level 18: 4 slots, 5th level
        { slots: 4, level: 5 }, // Level 19: 4 slots, 5th level
        { slots: 4, level: 5 }  // Level 20: 4 slots, 5th level
      ];

      const warlockSlots = warlockSlotsByLevel[Math.min(warlockLevel, 20)];

      // Add warlock pact slots to the appropriate spell level
      // Per PHB, these are separate but we combine them for simplicity in UI
      if (warlockSlots.slots > 0 && warlockSlots.level > 0) {
        spellSlots[warlockSlots.level].max += warlockSlots.slots;
      }
    }

    return spellSlots;
  }

  // ========================================
  // [PHASE 6B]: CONDITION MECHANICAL EFFECTS
  // ========================================
  // [PURPOSE]: Implement D&D 5e condition mechanical effects
  // [WHY]: Conditions were tracked but didn't affect mechanics (gap identified in self-assessment)
  // [INTEGRATION]: Used by dice rolling, combat, and UI components

  /**
   * Check if a character has a specific condition
   * @param {Object} playerStats - Character stats object
   * @param {string} conditionKey - Condition key (e.g., 'poisoned', 'restrained')
   * @returns {boolean} True if condition is active
   */
  hasCondition(playerStats, conditionKey) {
    const conditions = playerStats?.conditions || [];
    return conditions.includes(conditionKey);
  }

  /**
   * Get active conditions for a character
   * @param {Object} playerStats - Character stats object
   * @returns {Array} Array of active condition keys
   */
  getActiveConditions(playerStats) {
    return playerStats?.conditions || [];
  }

  /**
   * Check if character has disadvantage on ability checks from conditions
   * @param {Object} playerStats - Character stats object
   * @param {string} ability - Ability being checked (strength, dexterity, etc.)
   * @returns {boolean} True if disadvantage applies
   */
  hasDisadvantageOnAbilityCheck(playerStats, ability, settings) {
    // [Spec 1b — Phase B / #324]: PHB p.144 armor-proficiency penalty.
    // Disadvantage on STR/DEX ability checks if equipped armor or shield is non-proficient.
    // Guard: only fires when the feature flag is explicitly enabled.
    if (settings?.enableArmorProficiencyPenalties && (ability === 'strength' || ability === 'dexterity' || ability === 'any')) {
      const inv = playerStats?.inventory || [];
      const equippedArmor = inv.find(
        i => i.equipped && i.armorType && i.armorType !== 'shield' && i.armorType !== 'unarmored'
      );
      if (equippedArmor && !hasArmorProficiency(playerStats, equippedArmor)) {
        return true;
      }
      const equippedShield = inv.find(i => i.equipped && i.armorType === 'shield');
      if (equippedShield && !hasShieldProficiency(playerStats, equippedShield)) {
        return true;
      }
    }

    // Exhaustion level 1+: Disadvantage on ability checks (from playerStats, not registry)
    if (playerStats.exhaustionLevel >= 1) return true;

    // [Sub-plan 1Q-F-7b-ii 2026-05-28]: REPLACED 6 hardcoded condition-name checks
    //   (poisoned/frightened disadvantage + paralyzed/stunned/petrified STR+DEX auto-fail
    //   + unconscious all-abilities auto-fail) with registry-driven queries. srdConditions
    //   numericEffects now emit type:'check' rows; effectRegistry routes 'check:<ability>'
    //   keys via _getStatKey branch added this sub-plan. Retires Rule 11/17 anti-pattern,
    //   sibling pattern to hasDisadvantageOnSavingThrow refactor (1Q-F-7b-i).
    if (this.hasAutoFailCheck(ability, playerStats)) return 'auto-fail';
    if (this.hasDisadvantageOnCheck(ability, playerStats)) return true;

    return false;
  }

  /**
   * Check if character has disadvantage on saving throws from conditions
   * @param {Object} playerStats - Character stats object
   * @param {string} ability - Save type (strength, dexterity, etc.)
   * @returns {boolean|string} True for disadvantage, 'auto-fail' for automatic failure
   */
  hasDisadvantageOnSavingThrow(playerStats, ability, settings) {
    // [Spec 1b — Phase B / #325]: PHB p.144 armor-proficiency penalty.
    // Disadvantage on STR/DEX saving throws if equipped armor is non-proficient.
    if (settings?.enableArmorProficiencyPenalties && (ability === 'strength' || ability === 'dexterity')) {
      const inv = playerStats?.inventory || [];
      const equippedArmor = inv.find(i => i.equipped && i.armorType && i.armorType !== 'shield' && i.armorType !== 'unarmored');
      if (equippedArmor && !hasArmorProficiency(playerStats, equippedArmor)) {
        return true;
      }
      const equippedShield = inv.find(i => i.equipped && i.armorType === 'shield');
      if (equippedShield && !hasShieldProficiency(playerStats, equippedShield)) {
        return true;
      }
    }

    // Exhaustion level 3+: Disadvantage on saving throws (from playerStats, not registry)
    if (playerStats.exhaustionLevel >= 3) return true;

    // [Sub-plan 1Q-F-7b-i 2026-05-28]: REPLACED 4 hardcoded condition-name checks
    //   (Restrained DEX disadvantage; Paralyzed/Stunned/Petrified/Unconscious STR+DEX auto-fail)
    //   with registry-driven queries. Patch 3 renamed srdConditions type:'savingThrow' →
    //   type:'save' so save:<ability> keys are indexable; Patch 3 added hasAutoFailSave +
    //   hasDisadvantageOnSave methods (this file L3083+L3101). Retires Rule 11/17 anti-pattern.
    //   See Sub-plan 1Q-F-7b-i in docs/audits/master-queue.md.
    if (this.hasAutoFailSave(ability, playerStats)) return 'auto-fail';
    if (this.hasDisadvantageOnSave(ability, playerStats)) return true;

    return false;
  }

  /**
   * Check if character has disadvantage on attack rolls from conditions
   * @param {Object} playerStats - Character stats object
   * @param {Object} [settings] - App settings (optional, for feature flags)
   * @returns {boolean} True if disadvantage applies
   */
  hasDisadvantageOnAttackRolls(playerStats, settings) {
    // [Spec 1b — Phase B / #326]: PHB p.144 armor-proficiency penalty.
    // RAW: disadvantage on any attack roll involving STR or DEX while wearing
    // non-proficient armor. Nearly all weapon attacks are STR/DEX-driven; the
    // few exceptions (spell attacks) are also blocked via spellCastingService.
    // For simplicity we apply disadvantage to ALL attack rolls here.
    if (settings?.enableArmorProficiencyPenalties) {
      const inv = playerStats?.inventory || [];
      const equippedArmor = inv.find(
        i => i.equipped && i.armorType && i.armorType !== 'shield' && i.armorType !== 'unarmored'
      );
      if (equippedArmor && !hasArmorProficiency(playerStats, equippedArmor)) {
        return true;
      }
      const equippedShield = inv.find(i => i.equipped && i.armorType === 'shield');
      if (equippedShield && !hasShieldProficiency(playerStats, equippedShield)) {
        return true;
      }
    }

    const conditions = this.getActiveConditions(playerStats);

    // Multiple conditions cause disadvantage on attack rolls
    if (conditions.includes('blinded')) return true;      // Can't see targets
    if (conditions.includes('poisoned')) return true;     // Poisoned state
    if (conditions.includes('prone')) return true;        // Prone (ranged attacks)
    if (conditions.includes('restrained')) return true;   // Movement restricted
    if (conditions.includes('frightened')) return true;   // Frightened (if source in sight)

    // Exhaustion level 3+: Disadvantage on attack rolls
    if (playerStats.exhaustionLevel >= 3) return true;

    return false;
  }

  /**
   * Check if attacks against character have advantage from conditions
   * @param {Object} playerStats - Character stats object
   * @returns {boolean} True if attackers have advantage
   */
  attackersHaveAdvantage(playerStats) {
    const conditions = this.getActiveConditions(playerStats);

    // Multiple conditions grant advantage to attackers
    if (conditions.includes('blinded')) return true;      // Can't see attackers
    if (conditions.includes('paralyzed')) return true;    // Can't move
    if (conditions.includes('petrified')) return true;    // Turned to stone
    if (conditions.includes('restrained')) return true;   // Movement restricted
    if (conditions.includes('stunned')) return true;      // Incapacitated
    if (conditions.includes('unconscious')) return true;  // Helpless

    // Prone grants advantage to melee attacks (within 5 feet)
    // Note: Ranged attacks have disadvantage, but we're checking advantage here
    if (conditions.includes('prone')) return 'melee-only';

    return false;
  }

  /**
   * Calculate speed modifications from conditions
   * @param {Object} playerStats - Character stats object
   * @param {number} baseSpeed - Base movement speed
   * @returns {number} Modified speed
   */
  getConditionSpeedModifier(playerStats, baseSpeed) {
    const conditions = this.getActiveConditions(playerStats);

    // Conditions that reduce speed to 0
    if (conditions.includes('grappled')) return 0;
    if (conditions.includes('paralyzed')) return 0;
    if (conditions.includes('petrified')) return 0;
    if (conditions.includes('restrained')) return 0;
    if (conditions.includes('stunned')) return 0;
    if (conditions.includes('unconscious')) return 0;

    // Prone: Speed halved (crawling) unless you stand up
    if (conditions.includes('prone')) {
      return Math.floor(baseSpeed / 2);
    }

    // Exhaustion effects on speed
    if (playerStats.exhaustionLevel >= 2) {
      // Level 2: Speed halved
      baseSpeed = Math.floor(baseSpeed / 2);
    }
    if (playerStats.exhaustionLevel >= 5) {
      // Level 5: Speed reduced to 0
      return 0;
    }

    return baseSpeed;
  }

  /**
   * Check if character is incapacitated (can't take actions or reactions)
   * @param {Object} playerStats - Character stats object
   * @returns {boolean} True if incapacitated
   */
  isIncapacitated(playerStats) {
    const conditions = this.getActiveConditions(playerStats);

    // Direct incapacitation
    if (conditions.includes('incapacitated')) return true;

    // Conditions that include incapacitation
    if (conditions.includes('paralyzed')) return true;
    if (conditions.includes('petrified')) return true;
    if (conditions.includes('stunned')) return true;
    if (conditions.includes('unconscious')) return true;

    return false;
  }

  /**
   * Get comprehensive condition effects summary
   * @param {Object} playerStats - Character stats object
   * @param {Object} [settings] - App settings (optional, for feature flags)
   * @returns {Object} Summary of all active condition effects
   */
  getConditionEffectsSummary(playerStats, settings) {
    const conditions = this.getActiveConditions(playerStats);
    const summary = {
      activeConditions: conditions,
      disadvantageOnAbilityChecks: false,
      disadvantageOnSavingThrows: [],
      disadvantageOnAttackRolls: false,
      attackersHaveAdvantage: false,
      speedModifier: null,
      incapacitated: false,
      autoFailChecks: [],
      autoFailSaves: [],
      additionalEffects: []
    };

    // Check each type of effect
    const abilityCheckEffect = this.hasDisadvantageOnAbilityCheck(playerStats, 'any', settings);
    if (abilityCheckEffect === 'auto-fail') {
      summary.autoFailChecks.push('all');
    } else if (abilityCheckEffect) {
      summary.disadvantageOnAbilityChecks = true;
    }

    // Check saves for each ability
    ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].forEach(ability => {
      const saveEffect = this.hasDisadvantageOnSavingThrow(playerStats, ability, settings);
      if (saveEffect === 'auto-fail') {
        summary.autoFailSaves.push(ability);
      } else if (saveEffect) {
        summary.disadvantageOnSavingThrows.push(ability);
      }
    });

    summary.disadvantageOnAttackRolls = this.hasDisadvantageOnAttackRolls(playerStats, settings);
    summary.attackersHaveAdvantage = this.attackersHaveAdvantage(playerStats);
    summary.incapacitated = this.isIncapacitated(playerStats);

    // Add specific condition notes
    if (conditions.includes('invisible')) {
      summary.additionalEffects.push('Attacks have advantage, attackers have disadvantage');
    }
    if (conditions.includes('charmed')) {
      summary.additionalEffects.push("Can't attack charmer");
    }
    if (conditions.includes('frightened')) {
      summary.additionalEffects.push("Can't willingly move closer to source of fear");
    }
    if (conditions.includes('deafened')) {
      summary.additionalEffects.push('Automatically fails ability checks that require hearing');
    }

    return summary;
  }

  // ========================================
  // EFFECT REGISTRY INTEGRATION METHODS
  // ========================================
  // [PHASE 0.1 - NOV 14, 2025]: Methods to sync effects with centralized registry
  // PURPOSE: Bridge between existing calculation logic and new unified effect system
  // WHY: Allows gradual migration without breaking existing functionality

  /**
   * Enable EffectRegistry integration
   * [USAGE]: Call this to start using the unified effect system
   */
  enableEffectRegistry() {
    this.useEffectRegistry = true;
    this.clearCache();
  }

  /**
   * Disable EffectRegistry integration (fallback to manual calculations)
   * [USAGE]: Call this if issues are found with the new system
   */
  disableEffectRegistry() {
    this.useEffectRegistry = false;
    this.clearCache();
  }

  /**
   * Sync feats to EffectRegistry
   * [PURPOSE]: Register all feat effects with the unified registry
   * [WHEN]: Call when feats change (add/remove)
   */
  syncFeatsToRegistry(feats, customFeats = []) {
    if (!this.useEffectRegistry) return;

    // Remove all existing feat effects
    const existingFeatSources = Array.from(this.effectRegistry.effects.keys())
      .filter(key => key.startsWith('feat:'));
    existingFeatSources.forEach(source => this.effectRegistry.remove(source));

    // Register each feat's effects
    (feats || []).forEach(feat => {
      const featData = SRD_FEATS[feat.id] || customFeats.find(f => f.id === feat.id);
      if (!featData || !featData.passiveEffects) return;
      // [v1.0 ARC-008]: Skip if feat already has modern effects array (registered by useFeatEffects)
      if (featData.effects && Array.isArray(featData.effects)) return;

      const sourceId = `feat:${feat.id}`;

      // Convert legacy passiveEffects to unified schema
      const effects = this.effectRegistry.constructor.convertLegacyEffect(
        featData.passiveEffects,
        sourceId
      );

      // Handle feat choices (like Resilient choosing an ability)
      if (feat.choices?.ability) {
        effects.push({
          type: 'ability',
          target: feat.choices.ability,
          operation: 'add',
          value: 1
        });
      }

      if (effects.length > 0) {
        this.effectRegistry.register(sourceId, effects);
      }
    });
  }

  /**
   * Sync equipped items to EffectRegistry
   * [PURPOSE]: Register all item effects with the unified registry
   * [WHEN]: Call when equipment changes (equip/unequip)
   */
  syncItemsToRegistry(inventory) {
    if (!this.useEffectRegistry) return;

    // Remove all existing item effects
    const existingItemSources = Array.from(this.effectRegistry.effects.keys())
      .filter(key => key.startsWith('item:'));
    existingItemSources.forEach(source => this.effectRegistry.remove(source));

    // Register each equipped item's effects
    (inventory || []).forEach(item => {
      if (!item.equipped) return;

      // Skip items requiring attunement that aren't attuned
      if (item.requiresAttunement && !item.attuned) return;

      const sourceId = `item:${item.id || item.name}`;
      const statMods = item.statModifications || item.passiveEffects?.statModifications;

      if (statMods) {
        // Convert legacy statModifications to unified schema
        const effects = this.effectRegistry.constructor.convertLegacyEffect(
          statMods,
          sourceId
        );

        if (effects.length > 0) {
          this.effectRegistry.register(sourceId, effects);
        }
      }
    });
  }

  /**
   * Sync active quick bonuses to EffectRegistry
   * [PURPOSE]: Register temporary buff effects with the unified registry
   * [WHEN]: Call when quick bonuses change
   * [PHASE 1.5 - NOV 15, 2025]: Updated to prioritize bonus.effects array (Phase 1.4 format)
   */
  syncQuickBonusesToRegistry(activeQuickBonuses) {
    if (!this.useEffectRegistry) return;

    // Remove all existing quick bonus effects
    const existingBonusSources = Array.from(this.effectRegistry.effects.keys())
      .filter(key => key.startsWith('buff:'));
    existingBonusSources.forEach(source => this.effectRegistry.remove(source));

    // Register each active bonus
    (activeQuickBonuses || []).forEach((bonus, index) => {
      const sourceId = `buff:${bonus.name || bonus.type}_${index}`;
      let effects = [];

      // [PHASE 1.5 - NOV 15, 2025]: Check for unified effects array first (Phase 1.4 format)
      // WHY: Preset bonuses from QuickBonusesPanel now have effects arrays
      // EXAMPLE: Bless has [{type: 'attackRoll', operation: 'addDice', value: '1d4'}, ...]
      if (bonus.effects && Array.isArray(bonus.effects)) {
        effects = bonus.effects;
      }
      // Fall back to legacy type parsing for custom bonuses created by users
      else if (bonus.type) {
        // Parse bonus type
        const match = bonus.type.match(/^(set|add|subtract)([A-Z][a-z]+)$/);
        if (match) {
          const operation = match[1] === 'set' ? 'set' : 'add';
          const target = match[2].toLowerCase();

          // Handle ability scores
          const abilities = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];
          if (abilities.includes(target)) {
            effects.push({
              type: 'ability',
              target: target.substring(0, 3),
              operation,
              value: operation === 'subtract' ? -Math.abs(bonus.value || 0) : (bonus.value || 0),
              condition: 'always'
            });
          }
        }
        // Handle special bonus types
        // [Sub-plan 1Q-F-5b-ii — 2026-05-28]: QUICK_BONUS_KINDS.acBonus.id replaces raw
        //   'acBonus' string literal; speedBonus/speedMultiplier are not quickBonusKinds
        //   (they are a different shape) so they keep their literal checks for now.
        else if (bonus.type === 'speedBonus') {
          effects.push({ type: 'speed', operation: 'add', value: bonus.value || 0, condition: 'always' });
        } else if (bonus.type === 'speedMultiplier') {
          effects.push({ type: 'speed', operation: 'multiply', value: bonus.value || 1, condition: 'always' });
        } else if (bonus.type === QUICK_BONUS_KINDS.acBonus.id) {
          effects.push({ type: 'ac', operation: 'add', value: bonus.value || 0, condition: 'always' });
        }
      }

      if (effects.length > 0) {
        this.effectRegistry.register(sourceId, effects);
      }
    });
  }

  /**
   * Sync all effects to EffectRegistry
   * [PURPOSE]: Complete sync of all character effects
   * [WHEN]: Call when character loads or major state changes
   */
  syncAllToRegistry(playerStats, inventory, activeQuickBonuses) {
    if (!this.useEffectRegistry) return;

    this.syncFeatsToRegistry(playerStats.feats, playerStats.customFeats);
    this.syncItemsToRegistry(inventory);
    this.syncQuickBonusesToRegistry(activeQuickBonuses);
  }

  /**
   * Clear all effects from EffectRegistry
   * [PURPOSE]: Reset the registry (character reset, logout, etc.)
   */
  clearRegistry() {
    this.effectRegistry.clear();
    this.clearCache();
  }

  /**
   * Get effect breakdown from EffectRegistry (new unified system)
   * [PURPOSE]: Show all effects affecting a stat using the unified registry
   * [RETURNS]: Object with base, effects[], total
   */
  getRegistryEffectBreakdown(stat, baseValue, playerStats, inventory, activeQuickBonuses) {
    if (!this.useEffectRegistry) {
      return {
        base: baseValue,
        effects: [],
        total: baseValue,
        source: 'legacy'
      };
    }

    // Ensure effects are synced
    this.syncAllToRegistry(playerStats, inventory, activeQuickBonuses);

    // Get breakdown from registry
    return this.effectRegistry.getBreakdown(stat, baseValue);
  }
}

// [TDZ BUG FIX - NOV 17, 2025]: Changed from `const` to `var` to fix production minifier TDZ error
// [WHY]: Module-level `const` with instantiation can cause TDZ in minified bundles
// [CAUSE]: Webpack/minifier may reorder code, `var` is hoisted and has no TDZ
// Export singleton instance
export var effectCalculator = new EffectCalculator();

// Export for testing
export default EffectCalculator;
